/**
 * Browser-side album PDF generator.
 *
 * The Supabase Edge Function (and most serverless platforms) caps memory and
 * CPU per invocation, which makes it impossible to render a 30+ page album
 * with 1200 DPI photos on the server. Doing the work in the browser sidesteps
 * the limit entirely: the user's machine has GBs of memory and the photos
 * they uploaded are already loaded in the editor.
 *
 * Flow:
 *   1. Editor calls `generateAlbumPdfs(...)` on submit.
 *   2. We download each photo at full resolution from Supabase Storage,
 *      embed it into a pdf-lib document, and produce two Blobs:
 *        - cover.pdf  (A3+ spread = back + spine + front)
 *        - interior.pdf (A4 pages with photos)
 *   3. The caller uploads the Blobs to Supabase Storage via signed URLs and
 *      then calls /pdf/finalize to record the URLs on the order row.
 *
 * Quality: photos are embedded at their native resolution (no DPI cap). The
 * only resize is when an original is wildly bigger than the slot (>2000 px on
 * the long edge after fit) — we downsample that case to keep PDF size sane.
 */
import { renderCacheKey, type RenderBlobCache } from './renderCache';
import { renderFetchInit, renderFetch } from './renderFetch';
export type { RenderBlobCache } from './renderCache';
import {
  PDFDocument, StandardFonts, rgb, degrees,
  PDFPage, PDFFont, PDFOperator, PDFNumber,
  pushGraphicsState, popGraphicsState, clip, endPath,
  rotateRadians, translate, concatTransformationMatrix,
} from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import { jpegPrintIssue } from './printSafeJpeg';
import { assertPrintSafe } from './printPdfCheck';
import { assertPagesRender } from './pdfRenderCheck';
import heic2any from 'heic2any';
import { soloLandscapeFitWidth } from './soloFit';
import { isCropSafe, LETTERBOX_FILL } from './photoFit';
import { slotRects, usesBaseRules, baseFrameMm, scaleFrame, BASE_GAP_MM } from './slotGeometry';
import { isThemedBook, spineIndex, type BookPageLike } from './bookStructure';
import { cropSource } from './decor/cropMath';
import { gapPt as photoGapPt, normalizeGapColor } from './photoGap';
import { aiApi } from './api';
import type { LayoutTemplate, LayoutTemplateSlot } from '@/lib/api';
import { scalePath } from './scalePath';
import { svgPathToPdfOps } from './svgPathToPdf';
import { applyPhotosToTemplateSvg } from './templatePipeline';
import { fullBleedRect, extendSlotsToBleed, bleedPhotoPlacement } from './pdfBleed';
import { assertSameOrientation } from './renderParity';
import { unprintablePhoto, unprintableCover, assertPageComplete, expectedPhotosOnPage } from './printCompleteness';
import { rotationCoverGrowth } from './slotRotation';
import { repairModelPages } from './themedPageEdits';
import { MAX_IMAGE_LONG_EDGE, computeSlotTargetLongEdge, elementPhotoPixels } from './slotResolution';
import { textBlockBox, layoutTextLines, coverZoneBaselines, coverZoneFontSize, rasterWrapWidth, textRasterPx, TEXT_RASTER_DPR, textLineHeight } from './pdfTextLayout';
import { fontStackFor, hasArabic, paragraphDirection } from './textBidi';
import { toCssFontFamily as coverCssFontFamily } from './coverFonts';
import { vectorLine, fakeBoldRatio, setVectorTtfSource } from './vectorText';
import { textBand, bandRect } from './textBand';
import { roundUpToMultipleOf4 } from './interiorPagePadding';
import { planCouponLeaves, couponPagesKeyword } from './couponLeaves';
import type { CouponPrintData } from './couponPdf';
import { computeTemplateLayout, DEFAULT_PRINT_SETTINGS, withDerivedVinyl, type PrintSettings, type TemplateLayout } from './assemblyTemplate';
import { coverPanelGeometry, COVER_BLEED_MM, type CoverPanelGeometry } from './coverPanelGeometry';
import { COVER_FACES, coverFaceClipPt, coverFaceRectsPt, elementsOnFace, readCoverElements, type CoverElement } from './coverElements';
import { shadowPlan } from './decor/decorStyle';
import { drawOrderStamp, coverStampPlacement, placeStamp } from './orderStamp';
import { strokeWidthPt, RECT_FILL_DEFAULT, ELLIPSE_FILL_DEFAULT } from './decor/shapePaint';
import { printable } from './decor/holders';
import { alphaPlane, cachedImage, drawElementShadow, drawPageBackground, embedGrayMask, embedJpgWithSoftMask, pieceKey, tintedPieceJpg, tintedPiecePng, withElementTransform } from './decor/pdfDecor';
import { isTint, tintKey } from './decor/tint';
import { LIVING_BACK_TEXT } from './livingText';

// Re-exported so callers that already import from pdfGenerator can reach the
// print-padding helpers without a second import path.
export { paddedInteriorPageCount, interiorBlankCount } from './interiorPagePadding';

// ─── Page geometry ───────────────────────────────────────────────────────────
const MM_TO_PT = 2.8346;
// INTERIOR page only. The cover does NOT use these — its panels come from
// coverPanelGeometry(PrintSettings), i.e. the real 200 × 303mm board.
// `A4_W = 210` and `A3PLUS_H` were deleted on 2026-07-29: they were the cover
// artwork panel, 10mm too wide and 6mm too short against the board, which is
// what made an imported cover photo render partially and print with bare
// edges. Do not reintroduce them — a cover value that merely *looks* like the
// interior one is exactly how the two specs got swapped.
const A4_H = 297 * MM_TO_PT;    // interior finished TRIM height
const PAGE_W = 200 * MM_TO_PT;  // interior finished TRIM width
/** Cover artwork bleed, all four edges (pt). Every cover image path uses THIS —
 *  see bleedPanel(). Previously each path invented its own, and three of the
 *  four had none at all. */
const COVER_BLEED_PT = COVER_BLEED_MM * MM_TO_PT;

/** Expand a panel rect outward by the cover bleed, on all four sides. */
function bleedPanel(x: number, y: number, w: number, h: number) {
  const b = COVER_BLEED_PT;
  return { x: x - b, y: y - b, w: w + b * 2, h: h + b * 2 };
}
// Slots in grid layouts (2-up / 3-up / 4-up) sit flush by default — no
// gap between photos. Customers said the white seam between grid cells
// reads as unintentional, so we ship flush as the default. A future
// per-album toggle can opt back into a gap by overriding this value at
// render time.
// ─── B5: THE GAP IS PER ALBUM, NOT A CONSTANT ───────────────────────────────
//
// There used to be `const GUTTER = 0` here and `const GUTTER = 14` in the
// worker's pdfRenderer.ts — two renderers, two answers, and the editor (CSS
// `gap-0`) a third. Now every renderer hands the album's gap to the ONE
// `slotRects` in slotGeometry.ts, byte-identical between web and worker.
// See photoGap.ts.
//
// Set once per interior render from `AlbumPdfInput.photoGapMm`, read by
// getSlots. Module-level because getSlots is reached from helpers deep in
// the page loop that do not carry the input.
let currentGapPt = 0;
// BASE RULES (migration 234): set once per interior render from
// `AlbumPdfInput.layoutRules`. On → every grid page gets its 12/15 mm frame
// (side from the album page number) and the gap is the rules' 4 mm.
let currentBaseRules = false;
// THE BOOK'S STRUCTURE (design system v2 §7 S1): set once per interior render
// from the WHOLE album's pages (never a redo subset). On → the book opens on
// its themed S1 page, the Storiz intro page is not printed, and album page 1
// is a RIGHT page, so every base-rules frame's spine side flips
// (bookStructure.spineIndex). Off → the plain book, byte-identical to before.
let currentThemedBook = false;
// pdf-lib's `rgb()` returns its own RGB type (an r/g/b plus a `type` tag), so
// the hand-written shape here could not hold what hexToRgb actually produces.
// Taken from the function itself, which cannot drift from it.
let currentGapColor: ReturnType<typeof hexToRgb> | null = null;

// Print-ready hardcover output. The cover press sheet + board geometry now come
// from the SINGLE SOURCE OF TRUTH: the admin PrintSettings → computeTemplateLayout
// (assemblyTemplate.ts), the SAME function + inputs the fiche de montage uses, so
// the cover guides and the fiche can never drift. generateCoverPdf resolves the
// per-render geometry from input.printSettings; DEFAULT_PRINT_SETTINGS reproduces
// the historical 483 × 329 mm sheet, 216 × 303 mm boards and 16 mm spine column
// (back + spine + front spread centred, margin = bleed + turn-in, no crop marks).

// Soft cap on embedded image dimensions (imported from ./slotResolution so the
// per-slot resolution math and the global cap share one source of truth).
// Larger originals are downsampled to this on the long edge. Each draw site
// now passes a SMALLER per-slot cap when the slot doesn't need full res (see
// computeSlotTargetLongEdge) — a 4-up quadrant only needs ~2000px @300dpi, so
// embedding 3000px there just bloated the PDF. A full-page 1-up still resolves
// back up to this cap. Quality vs file-size knob, not a memory limit.

// ─── Types mirroring the editor + cover schema ───────────────────────────────
export interface PhotoMeta {
  id: string;
  storage_path: string;
  signed_url: string | null;
  /** Optional lower-tier fallback (e.g. the server-made JPEG preview of a
   *  HEIC). Tried when `signed_url` — the print original — can't be fetched or
   *  decoded, so a failed in-browser HEIC decode no longer leaves a blank slot
   *  in the PDF (the #1076 "missing in output, present in editor" bug). */
  fallback_url?: string | null;
  width: number | null;
  height: number | null;
  face_data: unknown;
  // Phase 1 fields used by the smart fit logic (Phase 2)
  face_count?: number | null;
  face_spread?: number | null;
  aspect_ratio?: 'landscape' | 'portrait' | 'square' | null;
}

/**
 * A free-floating element the customer added on top of a page via the
 * editor toolbar: text, shapes, icons, or extra photos. Coordinates +
 * sizes are in % of the page (0..100). Mirror of PageElement in
 * AlbumEditorPage.tsx — kept identical so the editor's state survives
 * the PDF pipeline.
 */
export interface PageElement {
  id: string;
  type: 'text' | 'photo' | 'rect' | 'ellipse' | 'icon';
  x: number;
  y: number;
  width: number;
  height: number;
  // text
  text?: string;
  fontFamily?: string;
  fontSize?: number;
  fontColor?: string;
  fontWeight?: string;
  fontStyle?: 'normal' | 'italic';
  textDecoration?: 'none' | 'underline';
  textAlign?: 'left' | 'center' | 'right';
  // shape
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  opacity?: number;
  borderRadius?: number;
  // photo
  photoId?: string;
  // icon
  svg?: string;
  /** Catalogue-décor icon (public covers-bucket PNG URL). When set, the icon
   *  renders as this image (contain-fit in the element box) instead of svg. */
  imageUrl?: string;
  /** STICKERS (2026-10-14) — the editor's lighter copy of `imageUrl`. NEVER
   *  read here: the print embeds `imageUrl` (guard: stickers/catalog.test.ts). */
  previewUrl?: string;
  iconColor?: string;
  flipH?: boolean;
  flipV?: boolean;
  /** Free-transform rotation in degrees (CSS clockwise). Applied to photo
   *  elements in the PDF (2026-07-06); other types TBD. */
  rotation?: number;
  /** CROP (2026-07-23) — photo elements only. Non-destructive window into the
   *  original image in normalized source coords (0..1). The crop region is
   *  cover-fitted into the element box; mirrors the editor's CSS crop exactly
   *  (parity). Absent = whole photo. */
  crop?: { x: number; y: number; w: number; h: number };
  /** CAPTIONS (2026-10-06, #WA-269649) — text only. A thin outline around each
   *  letter in this colour, `outlineWidth` × the font size wide, stroked UNDER
   *  the fill (editor: -webkit-text-stroke + paint-order: stroke fill). */
  outlineColor?: string;
  outlineWidth?: number;
  /** The book-colour band behind a caption (lib/textBand.ts). */
  bandFill?: string;
  bandOpacity?: number;
  /** COVER TEXT (2026-10-30) — the space between lines, × the font size
   *  (default 1.3, pdfTextLayout.textLineHeight), and the band's margin round
   *  the box in % of the page (face) WIDTH, all four sides (textBand.bandRect). */
  lineHeight?: number;
  bandPad?: number;
  /** The photo this caption belongs to (captionLayout.ts): re-running the
   *  caption tool replaces it, and it follows its photo to another page. */
  captionFor?: string;
  /** DESIGN SYSTEM STEP 2 — the one soft shadow (lib/decor/decorStyle.ts
   *  SHADOW). Only image icons and photos cast it (`shadowable`). */
  shadow?: boolean;
  /** Elements that form one piece (a polaroid = photo + frame). Their shadows
   *  are all drawn before the group's first element (`shadowPlan`). */
  group?: string;
  /** Photo elements: an alpha PNG (torn edge) stretched to the box. Editor =
   *  CSS mask-image; PDF = /SMask on the photo's JPEG. */
  maskUrl?: string;
  /** DESIGN SYSTEM STEP 3 — icons: `fill` stretches the piece to its box
   *  (torn paper scraps); default contain. */
  fit?: 'fill';
  /** A text HOLDER placed by a theme model (lib/decor/holders.ts): its role.
   *  Empty → not printed. */
  holder?: string;
  /** A label shape that belongs to the holder with this id: printed only when
   *  that holder has words. */
  labelFor?: string;
  /** A TINTED piece (lib/decor/tint.ts): its file is a colour map, recoloured with these
   *  four colours on a canvas before it is embedded — the editor's SVG matrix. */
  tint?: readonly string[];
}

export interface LayoutPage {
  page: number;
  layout: '1-up' | '2-up' | '3-up' | '4-up' | 'wide-top' | '3-up-flip' | '2-up-side' | '5-up' | '6-up' | '4-up-main' | '2-up-even';
  photos: string[];
  /** WIDE-TOP layout only — top band height as a fraction of page height
   *  (0..1). The wide group photo fills the full-width top band at its exact
   *  aspect ratio (zero crop); the companion fills the remaining bottom band.
   *  Mirror of `LayoutPage.top_h_frac` in AlbumEditorPage.tsx — the editor,
   *  preview, and this PDF generator all derive the two slot rectangles from
   *  this single value so the three pipelines stay visually identical. */
  top_h_frac?: number;
  /** 1-up landscape cropped to FILL the page around its faces (composer,
   *  2026-10-13) — cover-fit instead of fit-width (soloFit.ts). */
  fill?: boolean;
  /** STEP 3 — a page built from a theme model (`theme` slug, `model` code):
   *  a blank page whose photos are drawn as layers. */
  theme?: string;
  model?: string;
  /**
   * Per-slot crop/zoom decisions captured at edit time so the PDF renders
   * the EXACT framing the customer saw in the preview instead of
   * re-deriving it from face data. Keyed by `s{slotIndex}` (e.g. `s0`,
   * `s1`). Coordinates are CSS-style percent (0..100). Absent entries
   * fall back to the auto face-centroid crop, preserving behaviour for
   * legacy sessions saved before this field existed.
   */
  slot_positions?: Record<string, {
    x: number; y: number; scale?: number; rotation?: number; _v?: 2;
    /** REAL CROP (2026-07-24) — kept window of the ORIGINAL photo in
     *  normalized coords (0..1). Treated as THE photo: cover-fit + pan are
     *  computed on the cropped dims, then the full image is drawn oversized
     *  and offset so only this window lands in the slot (pdf-lib can't draw a
     *  sub-region; the slot clip does the cutting). Mirrors the editor's
     *  clipping crop-window wrapper. */
    crop?: { x: number; y: number; w: number; h: number };
  }>;
  /** Free-floating overlay elements (text, shapes, icons) the customer
   *  placed on top of the page in the editor. The PDF generator paints
   *  these AFTER the slot photos, in the order they appear here. */
  elements?: PageElement[];
  /** DESIGN SYSTEM STEP 2 — the page's paper: a colour and/or a full-page
   *  image cover-fitted to the BLEED box (decorStyle.backgroundBoxPct). Drawn
   *  under everything; the photo gaps show it. `previewUrl` is a lighter copy
   *  for the editor; the PDF always uses `imageUrl`. */
  background?: { imageUrl?: string | null; previewUrl?: string | null; color?: string | null; tint?: readonly string[] | null };
  /** ID of the layout template applied to this page. When set, the PDF
   *  uses template.slots for photo positioning and overlays
   *  template.overlay_print_png_url ABOVE the photos (Phase 1.5 layer
   *  order: bg colour → photos → decorative overlay). */
  template_id?: string | null;

  /** BLANK PAGE (2026-07-05) — true = pure white divider page: no photo
   *  slots, PDF fills white; the customer can add text/photos/icons as
   *  free elements. Reversible (toggle back to the AI layout). Kept in the
   *  page array so page count / divisible-by-4 are unaffected. */
  blank?: boolean;
  /** « PAGE DE FIN » (coupons plan 2026-10-28, lib/outroPage.ts): 'outro' =
   *  this page draws the brand outro image (outro_page_2_url) full page in the
   *  editor, preview and PDF. Always paired with `blank: true` and no photos. */
  kind?: 'outro';
  /** BLANK PAGE colour (2026-07-06) — fill for a blank page; default white. */
  blank_color?: string;
}

export interface SpineConfig {
  text: string;
  year: string;
  bgColor: string;
  textColor: string;
  yearColor: string;
  fontSize: number;
  yearFontSize: number;
  rotation: number;
  scale: number;
}

export interface TextZoneOverride {
  x?: number;
  y?: number;
  fontSize?: number;
  scale?: number;
  hidden?: boolean;
}

export interface ExtraTextZone {
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
  fontFamily: string;
  color: string;
  align?: 'left' | 'center' | 'right';
}

export interface CoverData {
  id: string;
  storage_path: string | null;
  thumbnail_url: string | null;
  /** Transparent design PNG (illustration only). When set, the PDF
   *  composites bg color + this image so per-face color picks recolor
   *  the front face. Null falls back to thumbnail_url. */
  design_url?: string | null;
  dominant_color: string | null;
  width_px: number | null;
  text_zones: Array<{
    type: 'destination' | 'year' | 'custom';
    x: number; y: number; width: number; height: number;
    font_size?: number;
    font_family?: string;
    color?: string;
    default_text?: string;
  }> | null;
}

export interface AlbumPdfInput {
  pages: LayoutPage[];
  /** B5 — gap between adjacent photos, mm. 0 / absent = flush. */
  photoGapMm?: number | null;
  /** B5 — what shows in the gap. `#RRGGBB`. Default white. */
  photoGapColor?: string | null;
  /** Base layout rules stamp (`album_sessions.layout_rules`). 'base-v1' =
   *  12/15 mm page margins + 4 mm gaps; absent = edge to edge. */
  layoutRules?: string | null;
  photos: PhotoMeta[];
  cover: CoverData | null;
  /** Signed URL (ideally just-fetched) for the front-cover storage_path. */
  coverImageUrl: string | null;
  destinationText: string;
  yearText: string;
  customTexts: Record<number | string, string>;
  zoneOverrides: Record<number | string, TextZoneOverride>;
  extraZones: ExtraTextZone[];
  spineConfig: SpineConfig | null;
  backCoverBgColor: string | null;
  /** Front-cover background color override. When set, the front face is
   *  painted with this color (under the artwork, or alone when
   *  hideFrontCoverImage is true). */
  frontCoverBgColor?: string | null;
  /** When true the cover artwork (thumbnail_url / storage_path image) is
   *  not embedded — the front face shows only the bg color, matching the
   *  editor's eye-toggle behaviour. */
  hideFrontCoverImage?: boolean;
  /** Drag + zoom transform applied to the transparent design overlay in
   *  the editor. x/y are container-relative percentages, scale is a
   *  multiplier on the contain-fit default. Applied only when the cover
   *  has a design_url (not on legacy thumbnail_url images). */
  coverDesignTransform?: { x: number; y: number; scale: number } | null;
  /** Per-order admin override for the front-cover image (signed URL). Takes
   *  priority over cover.design_url and renders via the same contain-fit +
   *  coverDesignTransform path. */
  frontCoverImageOverrideUrl?: string | null;
  /** Client-supplied front-cover image (signed URL) on a blank ("vierge")
   *  cover. Drawn cover-fit to fill the whole front panel, BEHIND the text
   *  zones. Distinct from frontCoverImageOverrideUrl (which is contain-fit for
   *  a transparent design); a blank cover has no design so they never collide. */
  blankCoverImageUrl?: string | null;
  /** Pan/zoom the client applied to their blank-cover photo in the editor —
   *  translate in % of the front panel + scale (≥1) on the cover-fit base.
   *  Mirrors CoverThumbnail's CSS `translate(x%,y%) scale(s)` exactly. */
  blankCoverTransform?: { x: number; y: number; scale: number } | null;
  /** CLIENT-PDF IMPORT — a full-wrap cover image (signed URL) already composited
   *  to OUR sheet layout (client front→front panel, spine→spine column,
   *  back→back panel). When set it is drawn to FILL the entire cover sheet as one
   *  continuous piece, REPLACING all per-panel artwork/bg + text zones + spine
   *  text. Our logo (back), marry-label and assembly guides still overlay it. */
  coverFullWrapImageUrl?: string | null;
  /** Image URLs (blob: or http:) for the back-cover photo grid. */
  backCoverImages: string[];
  /** COVER ELEMENTS (2026-10-20) — stickers / icons / the client's own photos
   *  placed freely on the front, back or spine (lib/coverElements.ts). % of the
   *  face's frame, drawn with the interior's drawPageElements on top of the
   *  face's artwork, clipped to the frame + bleed. Photo elements resolve
   *  through `photos` (the print original). Absent / empty → nothing drawn. */
  coverElements?: CoverElement[] | null;
  /** FULL-PAGE back photo (2026-07-15): drawn cover-fit filling the whole
   *  back panel, UNDER the grid images (which exclude it). */
  backCoverFullPageImage?: string | null;
  logoUrl: string | null;
  /** Single branding page prepended BEFORE the customer's content pages.
   *  May be a PDF (first page used) or a raster image (cover-fit on A4).
   *  Null is skipped — admin uploads this in Settings → Identité de marque.
   *  IGNORED for a themed book (`pages[0]` is the S1 opening): that book
   *  prints no intro page — see interiorIntroUrl / bookStructure.ts. */
  introPageUrl?: string | null;
  /** Single branding page appended AFTER the customer's content pages. */
  outroPageUrl?: string | null;
  /** « Photo Vivante » (2026-11-03, livingPrint.ts): the still printed as the
   *  book's very last page (after the ÷4 padding) and the link the back-cover
   *  QR opens. Both absent → the book exactly as before. */
  livingStillUrl?: string | null;
  livingLink?: string | null;
  /** Client-placed overlay elements (text / photo / shape / icon) drawn ON TOP
   *  of the intro branding background. Same %-based PageElement model as the
   *  content pages' `LayoutPage.elements` — rendered via the shared
   *  `drawPageElements`. The editor clamps these out of the bottom QR safe
   *  zone, so no extra safe-zone logic is needed here. Absent / empty → the
   *  intro renders exactly as before (just the branding image). */
  introElements?: PageElement[];
  /** Album session id — used to attribute safe-crop-rejection telemetry
   *  events to a specific session so the layout team can correlate
   *  rejections with the photos that triggered them. Optional: when
   *  omitted, telemetry is silently dropped. */
  sessionId?: string | null;
  /** Lookup of layout templates referenced by interior pages. The renderer
   *  reads template.slots for positioning and overlays
   *  template.overlay_print_png_url ON TOP of the photos (Phase 1.5: the
   *  decorative overlay is the top layer, not the background). Pass an
   *  empty map (or omit) if no pages use templates. */
  templatesById?: Map<string, Pick<LayoutTemplate, 'overlay_print_png_url' | 'slots' | 'slot_count' | 'original_svg_url'>>;
  /** Per-slot photoFit (scale + offset within the clip path) for templated
   *  pages. Keyed by `LayoutPage.page` (1-based page number) → slot index.
   *  Maps to the editor's `templatePhotoFits` state — same shape, same
   *  semantics. Absent entries default to `{ scale: 1, offsetX: 0, offsetY: 0 }`
   *  (cover-fit centred in the clip). Only consulted for pages that
   *  resolve a template via `template_id`. */
  templatePhotoFits?: Record<number, Record<number, { scale: number; offsetX: number; offsetY: number }>>;
  /** Print bleed ("fond perdu") in millimetres. Each interior page is grown
   *  by this margin on all four sides (trim 210×297 → media 216×303 at 3mm)
   *  so the bindery can trim through ink instead of leaving a white sliver.
   *  Page backgrounds, full-page rasters, and any photo slot touching a trim
   *  edge are extended into the bleed. Defaults to 3mm when omitted; pass 0
   *  to export at bare trim size with no bleed. */
  bleedMm?: number;
  /** Order identifier for production matching, e.g. "#1074 1/3". Printed as the
   *  ORDER STAMP (lib/orderStamp.ts, owner 2026-10-21): on the cover's back
   *  board, bottom spine-side corner, 12 mm inside the board (visible on the
   *  finished book), and on the interior's first page near the spine — pure
   *  black or white from the real pixels under it, boxed when busy, bold 8 pt.
   *  Undefined → nothing drawn. */
  orderLabel?: string;
  /** REDO / réimpression subset. When provided, only these content pages
   *  (0-based indices into `pages`) are rendered in the interior PDF. The
   *  intro/outro bookends are still emitted — they are part of a printable
   *  interior — and the ÷4 print padding is recomputed on the resulting page
   *  set (intro + selected content + outro). Undefined → render every page
   *  (the normal full-album path). Only consulted by generateInteriorPdf. */
  pageIndices?: number[];
  /** Assembly guides on the cover wrap: the 2 centered spine-centre ticks ONLY
   *  (the 4 board-corner cut marks were removed 2026-06-30). DEFAULT OFF —
   *  customer cover PDFs are ALWAYS clean. Set `true` (admin toggle only) to
   *  print the guides for the bindery. Ticks sit at the spine centre, top +
   *  bottom, never on artwork. */
  showAssemblyGuides?: boolean;
  /** Printed spine COLUMN width (mm) = spine board + 2× hinge. Drives the cover
   *  horizontal layout. Defaults to the module standard (16 mm) when omitted. */
  spineColumnMm?: number;
  /** Rigid spine BOARD width (mm) — caps spine-text height so it stays on the
   *  board, not the hinges. Default 10. */
  spineBoardMm?: number;
  /** SINGLE SOURCE OF TRUTH for cover + fiche print geometry (board / sheet /
   *  spine / hinge / turn-in). Set by buildAlbumPdfInput from the admin
   *  Paramètres d'impression. When present it supersedes the legacy
   *  spineColumnMm / spineBoardMm fields; when absent the cover falls back to
   *  reconstructing settings from those (then DEFAULT_PRINT_SETTINGS). */
  printSettings?: PrintSettings;
  /** CLIENT-PDF IMPORT (manual order only). A single-page PDF (raw bytes from
   *  splitImportedPdf) to EMBED — losslessly, via embedPdf/drawPage — into the
   *  front-cover panel, replacing the normal cover-artwork image path. The
   *  spine, back, text zones, guides, fiche, and marry-label all still render
   *  exactly as a normal cover. Absent → the normal front-cover path runs
   *  unchanged (normal orders are byte-for-byte identical). */
  importedCoverFrontPdf?: Uint8Array;
  /** CLIENT-PDF IMPORT. Single-page PDF embedded into the back-cover panel,
   *  same lossless technique. Absent → normal back-cover photo grid path. */
  importedCoverBackPdf?: Uint8Array;
  /** CLIENT-PDF IMPORT. Multi-page PDF whose EVERY page is embedded, one per
   *  interior page, onto the existing interior page geometry (A4 trim + bleed),
   *  cover-fit. Replaces the photo-layout loop (input.pages is ignored). The
   *  intro/outro bookends, marry-label, and ÷4 print padding still run. Absent →
   *  the normal photo-layout interior path runs unchanged. */
  importedInteriorPdf?: Uint8Array;
  /** REPORT ONLY — never changes a drawing. Called by the interior generator
   *  once it knows how many pages precede content page 1 (the intro, 0 or 1 —
   *  an intro that fails to embed is skipped, which shifts every page number).
   *  The 3D preview's per-page fingerprints record it so a quick redraw of
   *  page k lands on the right picture (lib/previewRenderKeys.ts). */
  onInteriorPlan?: (plan: { introPages: number }) => void;
  /** COUPONS (plan 2026-10-28) — PRINT RENDER ONLY. The order's coupons to
   *  print on this album's fully free leaf (couponLeaves.ts: both pages of the
   *  leaf are ×4 pad pages), instead of two outro copies. Set by the server
   *  print render for ONE album of the order (couponPrint.planOrderCoupons);
   *  never by a preview (generatePreviewPdfs strips it) nor a redo. Absent, or
   *  `enabled: false` → the interior is byte-for-byte what it was without the
   *  feature (couponPdf.offswitch.test.ts). */
  coupons?: CouponPrintData | null;
  /** REPORT ONLY — the print pages the coupon leaf was drawn on ([] = none:
   *  no free leaf, or a design failed and the outro copies stayed). Called only
   *  when `coupons` is set. */
  onCouponPlan?: (plan: { pages: number[] }) => void;
}

export interface PdfGenProgress {
  stage: 'cover' | 'interior' | 'done';
  current: number;
  total: number;
  message: string;
}

// ─── Color helpers ───────────────────────────────────────────────────────────
function hexToRgb(hex: string) {
  const h = String(hex || '').replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  // #RRGGBBAA: the colour is the first six digits (parseAlphaFromHex reads the
  // alpha). Before, an 8-digit fill printed BLACK — one live album's gold
  // ellipse #C9A24A15 showed gold in the editor and printed black (10-14).
  const six = full.length === 8 ? full.slice(0, 6) : full;
  const safe = /^[0-9a-fA-F]{6}$/.test(six) ? six : '000000';
  return rgb(
    parseInt(safe.slice(0, 2), 16) / 255,
    parseInt(safe.slice(2, 4), 16) / 255,
    parseInt(safe.slice(4, 6), 16) / 255,
  );
}

/** Perceptual luminance (0..1) of a hex colour — drives the spine-tick contrast
 *  choice. > 0.5 ⇒ a light background (use a dark tick); ≤ 0.5 ⇒ a dark or
 *  saturated background (use a light tick). */
function coverLuminance(hex: string): number {
  const h = String(hex || '').replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const safe = full.length === 6 ? full : '000000';
  const r = parseInt(safe.slice(0, 2), 16) / 255;
  const g = parseInt(safe.slice(2, 4), 16) / 255;
  const b = parseInt(safe.slice(4, 6), 16) / 255;
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

// pdf-lib's StandardFonts only encode WinAnsi (Latin-1). Custom embedded fonts
// support Unicode, but for safety we still normalise typography to ASCII
// equivalents — em-dash, smart quotes, ellipsis, NBSP.
function safeText(s: unknown): string {
  if (s == null) return '';
  return String(s)
    .replace(/[‐-―]/g, '-')
    .replace(/[‘’‚‛]/g, "'")
    .replace(/[“”„‟]/g, '"')
    .replace(/…/g, '...')
    .replace(/ /g, ' ');
}

function toWinAnsi(s: string): string {
  return s.replace(/[^\x00-\xFF]/g, '');
}

// ─── A WARM HOST'S CACHE (2026-10-21) — lib/renderCache.ts says why ──────────
let renderCache: RenderBlobCache | null = null;
export function setRenderBlobCache(cache: RenderBlobCache | null): void { renderCache = cache; }

/** A décor piece / paper, normalised at its default size — through the warm
 *  host's cache when one is installed (the same bytes either way). */
async function normaliseCached(url: string): Promise<{ bytes: Uint8Array; mime: 'image/jpeg' | 'image/png' }> {
  const nk = renderCache?.getNorm ? `${renderCacheKey(url)}|default` : null;
  if (nk) { const hit = renderCache!.getNorm!(nk); if (hit) return hit; }
  const norm = await normaliseImageBlob(await fetchAsBlob(url));
  if (nk) renderCache!.setNorm?.(nk, norm);
  return norm;
}

// ─── Image fetch + normalise ─────────────────────────────────────────────────
async function fetchAsBlob(url: string, attempts = 3): Promise<Blob> {
  const key = renderCache ? renderCacheKey(url) : null;
  if (key) { const hit = renderCache!.get(key); if (hit) return hit; }
  const blob = await fetchAsBlobNet(url, attempts);
  if (key) renderCache!.set(key, blob);
  return blob;
}
async function fetchAsBlobNet(url: string, attempts: number): Promise<Blob> {
  let lastErr: unknown;
  for (let i = 0; i < attempts; i++) {
    try {
      const res = await renderFetch(url, renderFetchInit(url));
      if (!res.ok) throw new Error(`fetch ${url}: ${res.status}`);
      return await res.blob();
    } catch (e) {
      lastErr = e;
      // Transient network blip / 5xx / storage rate-limit under a 95-photo
      // burst — back off briefly and retry before giving up on this source.
      if (i < attempts - 1) await new Promise((r) => setTimeout(r, 250 * (i + 1)));
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error(`fetch ${url} failed`);
}

// Pick the modal colour of the cover's OUTER FRAME (a band along the four
// edges, inset to skip bleed). Storiz covers are "background plate with
// centered illustration on top", so the frame holds the background plate
// pixels regardless of where the subject sits. The center-region sampler
// this replaced averaged the subject in and produced a wrong shade
// (e.g. Norway 2's viking ship pulled a light-blue plate toward dark blue).
// Returns null on failure so the caller falls back to cover.dominant_color.
async function sampleCoverBackgroundHex(imageUrl: string): Promise<string | null> {
  try {
    const res = await renderFetch(imageUrl, { mode: 'cors', ...renderFetchInit(imageUrl) });
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.type.startsWith('image/')) {
      // PDFs aren't decodable via createImageBitmap — caller falls back.
      return null;
    }
    const bitmap = await createImageBitmap(blob);
    const W = 200;
    const H = Math.max(1, Math.round(bitmap.height * (W / bitmap.width)));
    const canvas = typeof OffscreenCanvas !== 'undefined'
      ? new OffscreenCanvas(W, H)
      : (() => { const c = document.createElement('canvas'); c.width = W; c.height = H; return c; })();
    const ctx = canvas.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as
      CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, W, H);

    // INSET skips the outer 3% (press-sheet bleed / trim margin); BAND
    // harvests the next 10% inward from each edge. That gives ~four edge
    // strips that are virtually guaranteed to sit on the background plate
    // even for subject-heavy covers.
    const INSET = 0.03;
    const BAND  = 0.10;
    const i0 = Math.floor(W * INSET);
    const i1 = Math.floor(W * (INSET + BAND));
    const i2 = Math.ceil(W * (1 - INSET - BAND));
    const i3 = Math.ceil(W * (1 - INSET));
    const j0 = Math.floor(H * INSET);
    const j1 = Math.floor(H * (INSET + BAND));
    const j2 = Math.ceil(H * (1 - INSET - BAND));
    const j3 = Math.ceil(H * (1 - INSET));
    const data = ctx.getImageData(0, 0, W, H).data;

    const buckets = new Map<string, { r: number; g: number; b: number; n: number }>();
    // 16-bucket quantize per channel so near-identical pixels merge — keeps
    // anti-aliased glyph edges (white text on pink) from splintering the
    // histogram into 1-pixel singletons.
    const take = (x: number, y: number) => {
      const i = (y * W + x) * 4;
      if (data[i + 3] < 128) return; // transparent
      const key = `${data[i] >> 4}-${data[i + 1] >> 4}-${data[i + 2] >> 4}`;
      const b = buckets.get(key) ?? { r: 0, g: 0, b: 0, n: 0 };
      b.r += data[i]; b.g += data[i + 1]; b.b += data[i + 2]; b.n++;
      buckets.set(key, b);
    };
    // top + bottom bands (full width inside the inset)
    for (let y = j0; y < j1; y++) for (let x = i0; x < i3; x++) take(x, y);
    for (let y = j2; y < j3; y++) for (let x = i0; x < i3; x++) take(x, y);
    // left + right bands (mid-vertical only — corners already counted)
    for (let y = j1; y < j2; y++) {
      for (let x = i0; x < i1; x++) take(x, y);
      for (let x = i2; x < i3; x++) take(x, y);
    }

    if (buckets.size === 0) return null;
    let winner: { r: number; g: number; b: number; n: number } | null = null;
    for (const b of buckets.values()) {
      if (!winner || b.n > winner.n) winner = b;
    }
    if (!winner) return null;
    const r = Math.round(winner.r / winner.n);
    const g = Math.round(winner.g / winner.n);
    const b = Math.round(winner.b / winner.n);
    return '#' + [r, g, b].map(c => c.toString(16).padStart(2, '0')).join('');
  } catch (e) {
    console.warn('[pdfGenerator] sampleCoverBackgroundHex failed:', (e as Error)?.message);
    return null;
  }
}

/**
 * iPhone photos are HEIC. Chrome/Firefox can't decode HEIC with `<img>` or
 * `createImageBitmap`, and pdf-lib's `embedJpg` rejects them with "SOI not
 * found in JPEG". Detect HEIC by ISO BMFF "ftyp" box and convert via heic2any.
 */
function isHeic(bytes: Uint8Array): boolean {
  if (bytes.length < 12) return false;
  if (bytes[4] !== 0x66 || bytes[5] !== 0x74 || bytes[6] !== 0x79 || bytes[7] !== 0x70) return false;
  const brand = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]);
  return ['heic', 'heix', 'hevc', 'hevx', 'mif1', 'msf1', 'heim', 'heis'].includes(brand);
}

async function heicToJpegBlob(blob: Blob): Promise<Blob> {
  // Primary path: heic2any (libheif WASM) decodes the full-resolution image.
  try {
    const out = await heic2any({ blob, toType: 'image/jpeg', quality: 0.92 });
    return Array.isArray(out) ? out[0] : out;
  } catch (err) {
    // libheif rejects certain HEIC variants with 'ERR_LIBHEIF format not
    // supported' (newer iPhone HEIC10 grids, alpha-channel HEIC, etc.).
    // Fallback: extract the embedded JPEG preview that virtually every
    // camera bakes into HEIC files. Lower resolution but crucially
    // renderable — the printed page won't be blank.
    try {
      const exifr = await import('exifr');
      const thumb = await exifr.thumbnail(blob);
      if (thumb) {
        const arr = thumb instanceof Uint8Array ? thumb : new Uint8Array(thumb as ArrayBuffer);
        console.warn('[pdfgen] HEIC libheif failed, used embedded JPEG preview');
        return new Blob([arr as BlobPart], { type: 'image/jpeg' });
      }
    } catch { /* extractor unavailable or no preview embedded */ }
    throw err;
  }
}

/**
 * Normalise an arbitrary photo blob to a pdf-lib-embeddable JPEG.
 * - HEIC → heic2any → JPEG
 * - Anything else → re-encode through canvas (handles WebP/AVIF + downsamples
 *   anything wider than `maxLongEdge` on the long edge).
 *
 * We always go through canvas (rather than the old "skip if small" cheap path)
 * because the cheap path passed through HEIC and other formats that crashed
 * pdf-lib downstream.
 *
 * `maxLongEdge` is the per-call long-edge cap in pixels. Callers pass the
 * resolution their SLOT actually needs at 300 dpi (see computeSlotTargetLongEdge)
 * so a photo in a small grid cell embeds at ~2000px instead of the full 3000px
 * cap, shrinking the PDF without changing any draw geometry. Defaults to the
 * global MAX_IMAGE_LONG_EDGE for backward-compatibility (full-page / cover
 * sites that want full resolution).
 */
async function normaliseImageBlob(
  blob: Blob,
  maxLongEdge: number = MAX_IMAGE_LONG_EDGE,
  quality: number = 0.92,
): Promise<{ bytes: Uint8Array; mime: 'image/jpeg' | 'image/png' }> {
  // Detect HEIC up front — even small HEIC blobs need conversion.
  const headBuf = await blob.slice(0, 16).arrayBuffer();
  const head = new Uint8Array(headBuf);
  let workBlob = blob;
  if (isHeic(head)) {
    workBlob = await heicToJpegBlob(blob);
  }

  // PNG detection — first 8 bytes are 89 50 4E 47 0D 0A 1A 0A. We need to
  // preserve PNGs as PNGs because the transparent cover-design files lose
  // their alpha when re-encoded to JPEG (canvas.toBlob('image/jpeg')
  // paints transparent pixels BLACK), which painted black rectangles on
  // the front face in the rendered cover.pdf.
  const isPng =
    head[0] === 0x89 && head[1] === 0x50 && head[2] === 0x4e && head[3] === 0x47 &&
    head[4] === 0x0d && head[5] === 0x0a && head[6] === 0x1a && head[7] === 0x0a;
  // WebP ('RIFF'....'WEBP') can carry alpha too — the catalogue's trimmed
  // icons (covers.icon_url) are transparent WebP. Through the JPEG branch their
  // transparent pixels printed BLACK (a black square behind every « Icônes »
  // piece, found on the cover-stickers render 2026-10-20). Re-encode as PNG.
  const isWebp =
    head[0] === 0x52 && head[1] === 0x49 && head[2] === 0x46 && head[3] === 0x46 &&
    head[8] === 0x57 && head[9] === 0x45 && head[10] === 0x42 && head[11] === 0x50;

  // EXIF orientation source-of-truth. We read the tag from the bytes (DB
  // `orientation` is unreliable — was 0 for #1083's sideways photo) rather than
  // trusting `createImageBitmap({imageOrientation:'from-image'})`, which the
  // headless server-render context does NOT honour. Only JPEGs carry EXIF
  // orientation; PNGs never do. A read failure → null → as-is path (never crash).
  const exifOrientation = isPng ? null : await readExifOrientation(workBlob);
  const isRotated = exifOrientation != null && exifOrientation >= 2 && exifOrientation <= 8;

  // ── FAST AS-IS PATH (orientation 1 or absent) ──────────────────────────────
  // This is the 99% case (baked-at-intake / already-upright photos). We decode
  // raw with NO auto-orientation so behaviour is deterministic across browser
  // and headless. When no downscale is needed AND it's a plain JPEG, we embed
  // the ORIGINAL bytes byte-for-byte — zero re-encode, zero quality loss, zero
  // risk of double-rotation. PNGs keep their existing alpha-preserving handling.
  const isJpeg = head[0] === 0xff && head[1] === 0xd8;
  // Decode with imageOrientation:'none' so the canvas transform we apply for
  // rotated photos is the ONLY rotation — no hidden auto-orient to double up.
  const bitmap = await createImageBitmap(workBlob, { imageOrientation: 'none' });
  try {
    const longEdge = Math.max(bitmap.width, bitmap.height);
    const scale = longEdge > maxLongEdge ? maxLongEdge / longEdge : 1;

    // PNGs at or under the size cap pass straight through (alpha-safe).
    if (isPng && scale === 1) {
      const original = new Uint8Array(await workBlob.arrayBuffer());
      return { bytes: original, mime: 'image/png' };
    }
    // Upright JPEG that needs no downscale AND wasn't HEIC-converted → embed the
    // original bytes verbatim (zero re-encode, zero double-rotation risk on the
    // 99% case). Gated to the default quality tier (≥0.78): when the adaptive
    // size-guard steps `quality` DOWN to fit the 50 MB cap we must fall through
    // and actually re-encode to shrink, not pass the larger original through.
    //
    // ⚠️ ONLY A JPEG THE PRINTER CAN READ (2026-10-07). A3 duplex sheets
    // printed « [PDF to PS Conversion Error:] A drawing error occurred » on
    // every page with a PROGRESSIVE JPEG (how WhatsApp re-encodes photos):
    // PDF and screens decode it, PostScript does not (printSafeJpeg.ts). Such
    // a file — or a CMYK / 12-bit / cut-off one — falls through to the canvas
    // below and is re-encoded ONCE to baseline, same pixels, at ≥ 0.95.
    let printUnsafe: string | null = null;
    if (isJpeg && !isRotated && scale === 1 && workBlob === blob && quality >= 0.78) {
      const original = new Uint8Array(await workBlob.arrayBuffer());
      printUnsafe = jpegPrintIssue(original);
      if (!printUnsafe) return { bytes: original, mime: 'image/jpeg' };
    }

    // ── ROTATED PATH (orientation ∈ {2..8}) ─────────────────────────────────
    // Bake the upright pixels via an explicit canvas transform. The canvas
    // dimensions are the POST-rotation (upright) size — pdf-lib then reports
    // those as img.width/height, so the cover-fit frames exactly like the
    // editor. Re-encode at high quality (≥0.92) to preserve print quality,
    // since this branch only runs for the rare rotated photos.
    if (isRotated) {
      const canvas = drawOriented(bitmap, exifOrientation!, scale);
      const q = Math.max(quality, 0.92);
      const out: Blob = await new Promise((resolve, reject) => {
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('canvas.toBlob failed'))), 'image/jpeg', q);
      });
      return { bytes: new Uint8Array(await out.arrayBuffer()), mime: 'image/jpeg' };
    }

    // ── REMAINING CANVAS PATH (orientation 1/absent, but needs work) ─────────
    // Reached for: downscaling, HEIC→JPEG, WebP/AVIF, or any non-JPEG raster.
    // Same behaviour as before the orientation fix — just with deterministic
    // 'none' decode (these images carry no orientation to drop).
    const targetW = Math.max(1, Math.round(bitmap.width * scale));
    const targetH = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas 2d unavailable');
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);
    if (isPng || isWebp) {
      // Downscale path for PNG (and any WebP) — encode as PNG so we don't
      // flatten alpha to black like the JPEG branch does.
      const out: Blob = await new Promise((resolve, reject) => {
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('canvas.toBlob failed'))), 'image/png');
      });
      return { bytes: new Uint8Array(await out.arrayBuffer()), mime: 'image/png' };
    }
    // q0.92 (was 0.78 until 2026-07-29). Reaching this line means the photo IS
    // being resampled — a real downscale from the stored original (intake keeps
    // up to PRINT_MAX_DIM = 4800 px, a full-page slot asks for ~4034) or a HEIC/
    // WebP decode. Re-encoding a freshly resampled bitmap at 0.78 throws away
    // detail the downscale just produced, and that loss lands on the biggest,
    // most visible photos on the page.
    //
    // The old 0.78 was chosen to stay under a 50 MB Supabase upload cap. That
    // cap is gone (the `pdfs` bucket allows 1 GB), so the budget is now
    // SAFE_INTERIOR_BYTES and it is sized for 0.92. Photos that need NO
    // resampling never reach here at all — they take the verbatim-passthrough
    // branch above and keep their intake bytes untouched. `quality` is still
    // lowered adaptively by generateAlbumPdfs if an album blows the budget.
    // A print-safety re-encode (same size, nothing resampled) uses the SAME
    // quality as every other photo (2026-10-09). The ≥ 0.95 floor it had made
    // interiors 125–193 MB — WhatsApp photos are mostly progressive, so most
    // of an album took that path — and the browser upload of « Régénérer »
    // stalled on files that size. 0.92 is the print quality of every other
    // photo; the adaptive tiers can lower it again when an album is too big.
    const q = quality;
    const out: Blob = await new Promise((resolve, reject) => {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('canvas.toBlob failed'))), 'image/jpeg', q);
    });
    return { bytes: new Uint8Array(await out.arrayBuffer()), mime: 'image/jpeg' };
  } finally {
    bitmap.close?.();
  }
}

async function embedAuto(doc: PDFDocument, bytes: Uint8Array, mime: 'image/jpeg' | 'image/png') {
  return mime === 'image/png' ? doc.embedPng(bytes) : doc.embedJpg(bytes);
}

/**
 * Read the EXIF orientation tag (1..8) from an image blob, or null if absent /
 * unreadable. We do NOT trust DB `photos.orientation` (it was 0/unreliable for
 * #1083) — the truth is in the image bytes. We also do NOT trust
 * `createImageBitmap({imageOrientation:'from-image'})`: in a normal browser it
 * bakes EXIF for us, but in the Cloudflare Browser Rendering / headless context
 * that now produces the server-side PDF it is unreliable, so un-baked sideways
 * JPEGs decode raw and pdf-lib embeds them rotated (the #1083 cream-gap bug).
 * Reading the tag explicitly lets us apply the rotation deterministically.
 *
 * Returns null on ANY failure so the caller falls back to the as-is path —
 * never crash the PDF over an orientation read.
 */
async function readExifOrientation(blob: Blob): Promise<number | null> {
  try {
    const exifr = await import('exifr');
    // `orientation: true` parses just the Orientation tag — cheap, no full EXIF.
    const o = await exifr.parse(blob, { pick: ['Orientation'] });
    const v = o?.Orientation;
    return typeof v === 'number' && v >= 1 && v <= 8 ? v : null;
  } catch {
    return null;
  }
}

/**
 * Draw `bitmap` (decoded with NO auto-orientation) onto a canvas with the EXIF
 * orientation transform applied, producing UPRIGHT pixels. Mirrors the full
 * EXIF spec: 1=none, 2=flip-H, 3=180°, 4=flip-V, 5=transpose, 6=90°CW,
 * 7=transverse, 8=90°CCW. For the 90° cases the canvas W/H are swapped so the
 * output is the oriented (upright) size — those dims then feed the cover-fit,
 * matching what the editor shows (the Supabase display transform also applies
 * EXIF). Caller guarantees orientation ∈ {2..8}.
 */
function drawOriented(
  bitmap: ImageBitmap,
  orientation: number,
  scale: number,
): HTMLCanvasElement {
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  // For 90°/270° rotations the upright canvas has swapped dimensions.
  const swap = orientation >= 5;
  const canvas = document.createElement('canvas');
  canvas.width = swap ? h : w;
  canvas.height = swap ? w : h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d unavailable');
  switch (orientation) {
    case 2: ctx.transform(-1, 0, 0, 1, w, 0); break;            // flip-H
    case 3: ctx.transform(-1, 0, 0, -1, w, h); break;           // 180°
    case 4: ctx.transform(1, 0, 0, -1, 0, h); break;            // flip-V
    case 5: ctx.transform(0, 1, 1, 0, 0, 0); break;             // transpose
    case 6: ctx.transform(0, 1, -1, 0, h, 0); break;            // 90° CW
    case 7: ctx.transform(0, -1, -1, 0, h, w); break;           // transverse
    case 8: ctx.transform(0, -1, 1, 0, 0, w); break;            // 90° CCW
    default: break;
  }
  ctx.drawImage(bitmap, 0, 0, w, h);
  return canvas;
}

// Fetch + normalise a slot image, trying each candidate URL IN ORDER until one
// works. Candidate 1 is the full-resolution print original; later candidates
// are reliable fallbacks (e.g. the worker's JPEG preview of a HEIC), so a
// libheif decode failure on the original falls back to the preview instead of
// leaving a BLANK slot — the #1076 "images present in editor, missing in the
// PDF" bug. Only the slots whose original fails take the (lower-res) fallback;
// every other slot keeps full print resolution. Returns null only when EVERY
// candidate failed (genuinely unrenderable) — callers then skip as before.
async function fetchSlotNorm(
  candidates: Array<string | null | undefined>,
  maxLongEdge: number,
  quality: number,
  label: string,
): Promise<{ bytes: Uint8Array; mime: 'image/jpeg' | 'image/png' } | null> {
  const urls = candidates.filter((u): u is string => !!u);
  for (let i = 0; i < urls.length; i++) {
    try {
      const nk = renderCache?.getNorm ? `${renderCacheKey(urls[i])}|${maxLongEdge}|${quality}` : null;
      if (nk) { const hit = renderCache!.getNorm!(nk); if (hit) return hit; }
      const blob = await fetchAsBlob(urls[i]);
      const norm = await normaliseImageBlob(blob, maxLongEdge, quality);
      if (nk) renderCache!.setNorm?.(nk, norm);
      return norm;
    } catch (e) {
      console.warn(`[pdfgen] ${label}: source ${i + 1}/${urls.length} failed — ${(e as Error)?.message}`);
    }
  }
  if (urls.length > 0) {
    console.error(`[pdfgen] ${label}: ALL ${urls.length} image source(s) failed — slot left blank`);
  }
  return null;
}

// ─── Layout slots (matches the editor + Edge Function) ──────────────────────
// Restored to the original horizontal-split layout per user feedback —
// the portrait-aware side-by-side split (commit c54ea46) was rejected
// because the customer prefers the previous spread style. Subject
// emphasis now lives in computeDrawParams (gentle zoom + downward
// face-centroid bias) instead of the slot geometry.
// The wide-top clamp used to be duplicated here (70..200mm of a 297mm page)
// and was never read: `slotRects` in lib/slotGeometry.ts owns that pair and
// applies it, so an out-of-range stored frac is already clamped before this
// file ever sees a slot. A second copy of a constant that nothing reads is
// the two-paths-one-question shape, waiting for someone to edit one of them.

function getSlots(lp: Pick<LayoutPage, 'layout' | 'top_h_frac' | 'page'>) {
  // ⚠️ NO LAYOUT NAMES HERE. The maths is shared with the worker, byte for
  // byte, and takes the album's gap. A layout added anywhere but
  // slotGeometry.ts is the `3-up-flip` hole again.
  const frame = currentBaseRules
    ? scaleFrame(baseFrameMm(spineIndex((Number(lp.page) || 1) - 1, currentThemedBook)), MM_TO_PT)
    : null;
  return slotRects(lp.layout, lp.top_h_frac, PAGE_W, A4_H, currentGapPt, frame);
}

function computeFaceCrop(faceData: unknown, imgW: number, imgH: number): { x: number; y: number } {
  const faces = Array.isArray(faceData) ? (faceData as Array<{ x: number; y: number; width: number; height: number }>) : null;
  if (!faces?.length || !imgW || !imgH) return { x: 50, y: 50 };
  const avgX = faces.reduce((s, f) => s + f.x + f.width / 2, 0) / faces.length;
  const avgY = faces.reduce((s, f) => s + f.y + f.height / 2, 0) / faces.length;
  const cx = Math.max(0, Math.min(100, (avgX / imgW) * 100));
  const cy = Math.max(0, Math.min(100, (avgY / imgH) * 100));
  // Centre the crop on the face centroid itself. The old HOLD_BIAS_PCT=12
  // downward shift was wedding-specific (push the focal point onto the
  // ring/cake below the face). For travel/portrait photos it cropped the
  // top of the head — exactly the "subject framing wrong" regression the
  // customer reported.
  return { x: cx, y: cy };
}

/**
 * Translate-based draw params — mirrors the editor's CSS rendering
 * exactly. The image is cover-fitted to the slot (one dimension matches,
 * the other overflows), centred, then translated by the customer's
 * (translateX, translateY) percentage of slot dimensions and scaled by
 * the customer's `userScale` multiplier around the slot centre.
 *
 * Y-axis is flipped between CSS (down-positive) and pdf-lib
 * (up-positive), so a downward translate in the editor moves the image
 * DOWN in the PDF by negating the Y delta.
 *
 * `rotationDeg` is the customer's Canva-style manual slot rotation
 * (clockwise degrees). The cover scale is grown by `rotationCoverGrowth`
 * so the slot stays fully covered after the caller rotates the draw about
 * the image's centre — identical to pdfMirrorBox in the editor/preview.
 * The rotation itself is returned so the caller can apply it; at 0° the
 * growth is 1 and the returned params are byte-identical to before.
 */
export function computeTranslateDrawParams(
  slotX: number,
  slotY: number,
  slotW: number,
  slotH: number,
  imgW: number,
  imgH: number,
  translateXPct: number,
  translateYPct: number,
  userScale: number,
  rotationDeg: number = 0,
  fitWidth: boolean = false,
) {
  // Grow the cover-fit just enough that the rotated photo still covers the
  // slot (no corner gaps). Shared with the editor + preview via
  // slotRotation.ts so all three pipelines agree. At 0° this returns 1.
  const growth = rotationCoverGrowth(slotW, slotH, imgW, imgH, rotationDeg);
  // Cover (max) fills + crops; fit-width (min) shows the full landscape with a
  // cream letterbox top/bottom. The min() branch is the parity twin of
  // pdfMirrorBox's fitWidth branch; centring (baseX/baseY below) makes the box.
  const coverScale = (fitWidth
    ? Math.min(slotW / imgW, slotH / imgH)
    : Math.max(slotW / imgW, slotH / imgH)) * growth;
  const scale = coverScale * userScale;
  const sw = imgW * scale, sh = imgH * scale;
  // Centred draw position before translation.
  const baseX = slotX + (slotW - sw) / 2;
  const baseY = slotY + (slotH - sh) / 2;
  // Apply customer's pan offset (translate is in % of slot dimensions).
  // RULE 1 (2026-06-09): in fit-width the image spans the full slot width
  // (sw ≈ slotW) at scale 1 — no horizontal slack, so a translateX would only
  // shove the letterboxed image into the cream margin (Problem A). Apply it ONLY
  // when zoomed in enough to overflow (sw > slotW). Parity twin of
  // pdfMirrorBox's `ratioW > 1` guard.
  const applyTX = !fitWidth || Math.abs(sw - slotW) > 0.01;  // zoomed in OR out (2026-10-15, editor parity)
  const x = baseX + (applyTX ? (translateXPct / 100) * slotW : 0);
  const y = baseY - (translateYPct / 100) * slotH; // flip Y for pdf-lib
  return { x, y, width: sw, height: sh, rotationDeg };
}

// pdf-lib doesn't expose a high-level clip helper — push the raw graphics-
// state ops. q = save, re = rectangle, W = clip path, n = end path without
// painting, Q = restore. The TS types want `PDFOperatorNames` for the name
// argument but at runtime any string works; we cast through `unknown`.
function pushOp(page: PDFPage, name: string, operands?: PDFNumber[]) {
  page.pushOperators(PDFOperator.of(name as unknown as never, operands as unknown as never));
}
function clipRect(page: PDFPage, x: number, y: number, w: number, h: number) {
  pushOp(page, 'q');
  pushOp(page, 're', [PDFNumber.of(x), PDFNumber.of(y), PDFNumber.of(w), PDFNumber.of(h)]);
  pushOp(page, 'W');
  pushOp(page, 'n');
}
/**
 * Clip to a rectangle with rounded corners. Matches the editor's
 * `rounded-md` styling on back-cover photo cells. Pop with popClip().
 * Uses cubic Bezier approximation of quarter circles (k = 0.5523).
 */
function clipRoundedRect(page: PDFPage, x: number, y: number, w: number, h: number, r: number) {
  const radius = Math.max(0, Math.min(r, w / 2, h / 2));
  const cp = radius * (1 - 0.5523); // Bezier control-point offset from corner
  const x0 = x, x1 = x + w, y0 = y, y1 = y + h;
  pushOp(page, 'q');
  pushOp(page, 'm', [PDFNumber.of(x0 + radius), PDFNumber.of(y0)]);
  pushOp(page, 'l', [PDFNumber.of(x1 - radius), PDFNumber.of(y0)]);
  pushOp(page, 'c', [PDFNumber.of(x1 - cp), PDFNumber.of(y0), PDFNumber.of(x1), PDFNumber.of(y0 + cp), PDFNumber.of(x1), PDFNumber.of(y0 + radius)]);
  pushOp(page, 'l', [PDFNumber.of(x1), PDFNumber.of(y1 - radius)]);
  pushOp(page, 'c', [PDFNumber.of(x1), PDFNumber.of(y1 - cp), PDFNumber.of(x1 - cp), PDFNumber.of(y1), PDFNumber.of(x1 - radius), PDFNumber.of(y1)]);
  pushOp(page, 'l', [PDFNumber.of(x0 + radius), PDFNumber.of(y1)]);
  pushOp(page, 'c', [PDFNumber.of(x0 + cp), PDFNumber.of(y1), PDFNumber.of(x0), PDFNumber.of(y1 - cp), PDFNumber.of(x0), PDFNumber.of(y1 - radius)]);
  pushOp(page, 'l', [PDFNumber.of(x0), PDFNumber.of(y0 + radius)]);
  pushOp(page, 'c', [PDFNumber.of(x0), PDFNumber.of(y0 + cp), PDFNumber.of(x0 + cp), PDFNumber.of(y0), PDFNumber.of(x0 + radius), PDFNumber.of(y0)]);
  pushOp(page, 'h');
  pushOp(page, 'W');
  pushOp(page, 'n');
}
function popClip(page: PDFPage) { pushOp(page, 'Q'); }

/**
 * Push a clip mask defined by an SVG `d=` path attribute in slot-LOCAL unit
 * coords. The path is scaled to bbox pixel-space via `scalePath`, converted
 * to pdf-lib operator strings via `svgPathToPdfOps`, then translated into
 * absolute page coords by adding (bboxX, bboxY) to every coordinate pair.
 *
 * Caller is responsible for the surrounding graphics-state save/restore —
 * `pushGraphicsState` BEFORE this call, `popGraphicsState` AFTER the
 * subsequent draw, so the clip is scoped to the draw and doesn't leak into
 * later slots or the decorative overlay.
 */
function pushClipPath(
  page: PDFPage,
  d: string,
  bboxX: number,
  bboxY: number,
  bboxW: number,
  bboxH: number,
) {
  const scaled = scalePath(d, bboxW, bboxH);
  const ops = svgPathToPdfOps(scaled, bboxW, bboxH);
  for (const opStr of ops) {
    const parts = opStr.split(/\s+/);
    const cmd = parts[parts.length - 1];
    // `h` (close path) carries no numeric operands.
    if (cmd === 'h') {
      pushOp(page, 'h');
      continue;
    }
    const nums = parts.slice(0, -1).map(Number);
    // svgPathToPdfOps emits ops in (0..bboxW, 0..bboxH) local coords; offset
    // every coord pair by the slot's bottom-left in absolute page space.
    for (let k = 0; k < nums.length; k += 2) {
      nums[k] += bboxX;
      nums[k + 1] += bboxY;
    }
    pushOp(page, cmd, nums.map((n) => PDFNumber.of(n)));
  }
  page.pushOperators(clip(), endPath());
}

// ─── Custom font loading ─────────────────────────────────────────────────────
// jsdelivr's google/fonts mirror serves the `master` branch — `/main` 404s
// for everything, even though GitHub itself shows `main` as the default
// branch. Verified manually with curl. The `@master` form is the working
// pattern.
const CDN = 'https://cdn.jsdelivr.net/gh/google/fonts@master';
// Keyed by the BARE primary family name (see primaryFontName). Every font the
// cover editor offers (EDITOR_TEXT_FONTS in lib/editorFonts.ts) must have an
// entry here or its PDF text silently falls back to Helvetica. URLs verified
// reachable (HTTP 200) on the jsdelivr google/fonts @master mirror.
const FONT_URLS: Record<string, string> = {
  'Anton':              `${CDN}/ofl/anton/Anton-Regular.ttf`,
  'Londrina Solid':     `${CDN}/ofl/londrinasolid/LondrinaSolid-Regular.ttf`,
  'Bungee':             `${CDN}/ofl/bungee/Bungee-Regular.ttf`,
  'Cormorant Garamond': `${CDN}/ofl/cormorantgaramond/CormorantGaramond%5Bwght%5D.ttf`,
  'Cormorant SC':       `${CDN}/ofl/cormorantsc/CormorantSC-Medium.ttf`,
  'Playfair Display':   `${CDN}/ofl/playfairdisplay/PlayfairDisplay%5Bwght%5D.ttf`,
  'DM Sans':            `${CDN}/ofl/dmsans/DMSans%5Bopsz%2Cwght%5D.ttf`,
  'Roboto':             `${CDN}/ofl/roboto/Roboto%5Bwdth%2Cwght%5D.ttf`,
  'Oswald':             `${CDN}/ofl/oswald/Oswald%5Bwght%5D.ttf`,
  'Bebas Neue':         `${CDN}/ofl/bebasneue/BebasNeue-Regular.ttf`,
  'Montserrat':         `${CDN}/ofl/montserrat/Montserrat%5Bwght%5D.ttf`,
  'Poppins':            `${CDN}/ofl/poppins/Poppins-Bold.ttf`,
  'Lobster':            `${CDN}/ofl/lobster/Lobster-Regular.ttf`,
  'Pacifico':           `${CDN}/ofl/pacifico/Pacifico-Regular.ttf`,
  // ── Added so the rest of the cover font picker renders in the PDF ──────────
  'Fraunces':           `${CDN}/ofl/fraunces/Fraunces%5BSOFT%2CWONK%2Copsz%2Cwght%5D.ttf`,
  'Lora':               `${CDN}/ofl/lora/Lora%5Bwght%5D.ttf`,
  'Roboto Slab':        `${CDN}/apache/robotoslab/RobotoSlab%5Bwght%5D.ttf`,
  'Abril Fatface':      `${CDN}/ofl/abrilfatface/AbrilFatface-Regular.ttf`,
  'Gasoek One':         `${CDN}/ofl/gasoekone/GasoekOne-Regular.ttf`,
  'Inter':              `${CDN}/ofl/inter/Inter%5Bopsz%2Cwght%5D.ttf`,
  'Quicksand':          `${CDN}/ofl/quicksand/Quicksand%5Bwght%5D.ttf`,
  'Fredoka':            `${CDN}/ofl/fredoka/Fredoka%5Bwdth%2Cwght%5D.ttf`,
  'Ruda':               `${CDN}/ofl/ruda/Ruda%5Bwght%5D.ttf`,
  'Great Vibes':        `${CDN}/ofl/greatvibes/GreatVibes-Regular.ttf`,
  'Sacramento':         `${CDN}/ofl/sacramento/Sacramento-Regular.ttf`,
  'Dancing Script':     `${CDN}/ofl/dancingscript/DancingScript%5Bwght%5D.ttf`,
  'Caveat':             `${CDN}/ofl/caveat/Caveat%5Bwght%5D.ttf`,
  'Indie Flower':       `${CDN}/ofl/indieflower/IndieFlower-Regular.ttf`,
  'Permanent Marker':   `${CDN}/apache/permanentmarker/PermanentMarker-Regular.ttf`,
  'Amatic SC':          `${CDN}/ofl/amaticsc/AmaticSC-Regular.ttf`,
  'Special Elite':      `${CDN}/apache/specialelite/SpecialElite-Regular.ttf`,
  // 'Shadows Into Light' intentionally omitted — no working TTF on the @master
  // mirror (404/403); it falls back to Helvetica until a source is found.
};

const fontBytesCache = new Map<string, ArrayBuffer | null>();
// Variable weights for the vector text (lib/vectorText.ts): the same upright TTFs.
setVectorTtfSource((family, style) => (style === 'normal' ? FONT_URLS[family] : undefined));

// The editor's font picker stores a CSS family LIST (e.g.
// `"'Fraunces', 'Cormorant Garamond', Georgia, serif"`) on zone overrides
// + spineConfig. FONT_URLS is keyed by the bare primary font name, so we
// peel the first family name off before lookup. A bare name like
// `"Bebas Neue"` already matches verbatim. Returns null when the input is
// null/empty so getFont falls back cleanly.
function primaryFontName(family: string | null | undefined): string | null {
  if (!family) return null;
  const first = (family.split(',')[0] ?? '').trim();
  return first.replace(/^['"]|['"]$/g, '').trim() || null;
}

async function loadFontBytes(name: string): Promise<ArrayBuffer | null> {
  if (fontBytesCache.has(name)) return fontBytesCache.get(name) ?? null;
  const url = FONT_URLS[name];
  if (!url) { fontBytesCache.set(name, null); return null; }
  try {
    const r = await fetch(url);
    if (!r.ok) { fontBytesCache.set(name, null); return null; }
    const buf = await r.arrayBuffer();
    fontBytesCache.set(name, buf);
    return buf;
  } catch {
    fontBytesCache.set(name, null);
    return null;
  }
}

async function getFont(doc: PDFDocument, name: string | null | undefined, cache: Map<string, PDFFont>, fallback: PDFFont): Promise<PDFFont> {
  // Normalize FIRST: callers pass either a bare family ("Londrina Solid") or a
  // full CSS stack ("'Londrina Solid', Impact, sans-serif" — how cover
  // text_zones store their default font_family). FONT_URLS is keyed by the bare
  // primary name, so a raw stack used to miss the lookup and fall back to
  // Helvetica silently — the editor-vs-PDF cover-title mismatch. Peeling here
  // (the single chokepoint) fixes every call site at once.
  const resolved = primaryFontName(name);
  if (!resolved) return fallback;
  const cached = cache.get(resolved);
  if (cached) return cached;
  const bytes = await loadFontBytes(resolved);
  if (!bytes) return fallback;
  try {
    const font = await doc.embedFont(bytes, { subset: true });
    cache.set(resolved, font);
    return font;
  } catch {
    return fallback;
  }
}

// ─── ARABIC ON THE COVER (2026-10-26, #WA-576804) ───────────────────────────
//
// « هنا.. نبض الأمكنة في حكايات ترويها الصور » showed on the editor's front
// cover and spine and printed as « .. » alone. pdf-lib's drawText neither
// shapes Arabic (joined letters, right-to-left) nor has the glyphs in the
// cover's Latin faces, and the Helvetica fallback ran through toWinAnsi, which
// deletes every non-Latin-1 letter. So an Arabic cover line is drawn by the
// BROWSER — the same canvas text engine and the same named Arabic face
// (textBidi.fontStackFor) the editor's cover uses — at print resolution, and
// placed with the same size, alignment and baselines as a Latin title.
const COVER_RASTER_PX_PER_PT = 4.5; // ≈ 324 dpi
interface CoverRasterLine { png: Uint8Array; widthPt: number; ascentPt: number; descentPt: number; imgWPt: number; imgHPt: number; padPt: number }
function coverCanvasSpec(stack: string, sizePx: number, weight: number): string {
  return `${weight} ${sizePx}px ${stack}`;
}
/** Width / ascent / descent of `text` at 100 pt (scale linearly). Null off a browser. */
async function measureCoverArabic(text: string, cssStack: string, weight = 400): Promise<{ w100: number; a100: number; d100: number } | null> {
  if (typeof document === 'undefined') return null;
  const stack = fontStackFor(cssStack, text);
  const px = 100 * COVER_RASTER_PX_PER_PT;
  const spec = coverCanvasSpec(stack, px, weight);
  await ensureFontLoaded(spec, text);
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) return null;
  ctx.font = spec;
  (ctx as CanvasRenderingContext2D & { direction: CanvasDirection }).direction = paragraphDirection(text);
  const m = ctx.measureText(text);
  const asc = m.fontBoundingBoxAscent || m.actualBoundingBoxAscent || px * 0.8;
  const desc = m.fontBoundingBoxDescent || m.actualBoundingBoxDescent || px * 0.2;
  return { w100: m.width / COVER_RASTER_PX_PER_PT, a100: asc / COVER_RASTER_PX_PER_PT, d100: desc / COVER_RASTER_PX_PER_PT };
}
/** One Arabic cover line drawn by the browser at `sizePt`, in `colorHex`. */
async function rasterCoverArabicLine(text: string, cssStack: string, sizePt: number, colorHex: string, weight = 400): Promise<CoverRasterLine | null> {
  if (typeof document === 'undefined') return null;
  const stack = fontStackFor(cssStack, text);
  const px = sizePt * COVER_RASTER_PX_PER_PT;
  const spec = coverCanvasSpec(stack, px, weight);
  await ensureFontLoaded(spec, text);
  const canvas = document.createElement('canvas');
  let ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const dir = paragraphDirection(text);
  ctx.font = spec;
  (ctx as CanvasRenderingContext2D & { direction: CanvasDirection }).direction = dir;
  const m = ctx.measureText(text);
  const asc = m.fontBoundingBoxAscent || m.actualBoundingBoxAscent || px * 0.8;
  const desc = m.fontBoundingBoxDescent || m.actualBoundingBoxDescent || px * 0.2;
  const pad = Math.ceil(px * 0.3); // ink past the line box (diacritics, swashes) is kept
  canvas.width = Math.max(1, Math.ceil(m.width + 2 * pad));
  canvas.height = Math.max(1, Math.ceil(asc + desc + 2 * pad));
  ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.font = spec;
  (ctx as CanvasRenderingContext2D & { direction: CanvasDirection }).direction = dir;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = colorHex || '#000000';
  ctx.fillText(text, pad, pad + asc);
  const blob: Blob | null = await new Promise((res) => canvas.toBlob((b) => res(b), 'image/png'));
  if (!blob) return null;
  return {
    png: new Uint8Array(await blob.arrayBuffer()),
    widthPt: m.width / COVER_RASTER_PX_PER_PT, ascentPt: asc / COVER_RASTER_PX_PER_PT, descentPt: desc / COVER_RASTER_PX_PER_PT,
    imgWPt: canvas.width / COVER_RASTER_PX_PER_PT, imgHPt: canvas.height / COVER_RASTER_PX_PER_PT, padPt: pad / COVER_RASTER_PX_PER_PT,
  };
}

// ─── Cover rendering ─────────────────────────────────────────────────────────
function resolveZoneText(
  zone: { type: string; default_text?: string },
  i: number,
  input: AlbumPdfInput,
): string {
  // Respect an intentionally-empty edit. Falling back to default_text on
  // empty string snapped the placeholder back the moment the customer
  // deleted the last letter — exactly the editor's empty-zone bug, mirrored
  // here so the PDF doesn't end up with text the editor wasn't showing.
  if (zone.type === 'destination') {
    return safeText(input.destinationText !== undefined ? input.destinationText : (zone.default_text || ''));
  }
  if (zone.type === 'year') {
    return safeText(input.yearText !== undefined ? input.yearText : (zone.default_text || ''));
  }
  if (zone.type === 'custom') {
    const ct = input.customTexts[i] ?? input.customTexts[String(i)];
    if (ct !== undefined) return safeText(ct);
    return safeText(zone.default_text ?? '');
  }
  return safeText(zone.default_text ?? '');
}

/** An Arabic title zone, drawn by the browser (see « ARABIC ON THE COVER »). False = not drawn. */
async function drawArabicCoverZone(
  doc: PDFDocument, page: PDFPage,
  z: { font_family?: string | null; font_size?: number | null; color?: string | null; x: number; y: number; width: number; height: number; align?: string },
  o: Record<string, unknown>, text: string, overrideFamily: string | undefined,
  offsetX: number, offsetY: number, panelW: number, panelH: number, zoneW: number, zoneH: number, fontScale: number,
): Promise<boolean> {
  try {
    const cssStack = coverCssFontFamily(overrideFamily ?? z.font_family ?? null);
    const lines: string[] = text.split('\n');
    const metrics = await Promise.all(lines.map((ln) => measureCoverArabic(ln, cssStack)));
    if (metrics.some((m) => !m)) return false;
    const ms = metrics as Array<{ w100: number; a100: number; d100: number }>;
    const widestAt = (size: number) => ms.reduce((max, m) => Math.max(max, (m.w100 * size) / 100), 0);
    const fontSize = coverZoneFontSize({
      baseSize: (o.fontSize as number | undefined) ?? z.font_size ?? 24,
      fontScale, manualScale: (o.scale as number | undefined) ?? 1, zoneW, zoneH, lines: lines.length, widestAt,
    });
    const a100 = Math.max(...ms.map((m) => m.a100));
    const d100 = Math.max(...ms.map((m) => m.d100));
    const ascent = (a100 * fontSize) / 100;
    const descent = (d100 * fontSize) / 100;
    // WRAP BY WORDS inside the zone, as the editor's « white-space: pre-wrap »
    // box does: « هنا.. نبض الأمكنة في حكايات ترويها الصور » at scale 0.51 is two
    // lines in the editor and was one line running off the cover in the PDF.
    const widthAt = async (t: string) => { const m = await measureCoverArabic(t, cssStack); return m ? (m.w100 * fontSize) / 100 : Infinity; };
    const wrapped: string[] = [];
    for (const src of lines) {
      const words = src.split(' ');
      let cur = '';
      for (const w of words) {
        const cand = cur ? `${cur} ${w}` : w;
        if (!cur || (await widthAt(cand)) <= zoneW + 0.5) cur = cand;
        else { wrapped.push(cur); cur = w; }
      }
      wrapped.push(cur);
    }
    lines.splice(0, lines.length, ...wrapped);
    const align = (z.align as 'left' | 'center' | 'right' | undefined) ?? 'center';
    const zoneLeft = ((o.x as number | undefined) ?? z.x) * panelW;
    const zoneBottom = panelH - ((o.y as number | undefined) ?? z.y) * panelH - zoneH;
    const baselines = coverZoneBaselines({ zoneBottom, zoneH, lines: lines.length, fontSize, ascent, descent });
    const colorHex = String((o.color as string | undefined) ?? z.color ?? '#000000');
    for (let li = 0; li < lines.length; li++) {
      if (!lines[li].trim()) continue;
      const r = await rasterCoverArabicLine(lines[li], cssStack, fontSize, colorHex);
      if (!r) return false;
      let drawX = zoneLeft;
      if (align === 'center') drawX = zoneLeft + (zoneW - r.widthPt) / 2;
      else if (align === 'right') drawX = zoneLeft + (zoneW - r.widthPt);
      const img = await doc.embedPng(r.png);
      page.drawImage(img, {
        x: offsetX + drawX - r.padPt,
        y: offsetY + baselines[li] - r.descentPt - r.padPt,
        width: r.imgWPt, height: r.imgHPt,
      });
    }
    return true;
  } catch (e) {
    console.warn('[pdfgen] arabic zone draw failed:', (e as Error)?.message);
    return false;
  }
}

async function drawFrontCover(
  doc: PDFDocument,
  page: PDFPage,
  input: AlbumPdfInput,
  offsetX: number,
  offsetY: number,
  /** Artwork panel = the physical board (pt). Was panelW/panelH (210×297) while
   *  the board is 200×303 — the cause of the partial cover render. */
  panelW: number,
  panelH: number,
  fallbackFont: PDFFont,
  fontCache: Map<string, PDFFont>,
  fallbackBgHex: string,
) {
  let drewImage = false;
  // Cover artwork source priority:
  //   1. `design_url` (transparent PNG — illustration only) when present, so
  //      the front bg color shows through and per-face color picks recolor
  //      the cover. We paint the bg first then overlay the design.
  //   2. `thumbnail_url` (legacy `bg.jpg` with baked-in background) as the
  //      fallback for rows that don't yet have a transparent design.
  //   3. `coverImageUrl` (signed storage_path) as a last resort. CAUTION:
  //      legacy storage_path PNGs may have artwork that doesn't match the
  //      bg.jpg the admin uploaded — that's why thumbnail_url wins.
  // The eye toggle in the editor flips `hideFrontCoverImage` so the PDF
  // skips ALL artwork and shows only the flat color.
  // A per-order admin override (signed PNG) wins over the catalogue design_url
  // and renders via the same transparent-design (contain-fit + transform) path.
  const overrideUrl = input.frontCoverImageOverrideUrl ?? null;
  // Client's own front-cover image on a blank ("vierge") cover. Wins over
  // everything and renders cover-fit (NOT contain-fit) so the photo fills the
  // whole front panel, with the text zones drawn on top.
  const blankCoverUrl = input.blankCoverImageUrl ?? null;
  const designUrl = overrideUrl ?? input.cover?.design_url ?? null;
  // A blank-cover client image is a full-bleed photo, not a transparent
  // illustration — so it must NOT take the contain-fit `usingDesign` path.
  const usingDesign = !blankCoverUrl && !!designUrl;
  const imgUrl = input.hideFrontCoverImage
    ? null
    : (blankCoverUrl ?? designUrl ?? input.cover?.thumbnail_url ?? input.coverImageUrl ?? null);
  // Paint the front bg before drawing the design overlay so the customer's
  // color pick shows through the transparent illustration. For legacy
  // baked-in PNGs this fill is invisible (image covers it) but harmless.
  if (usingDesign) {
    let bg = rgb(0.96, 0.94, 0.91);
    try { bg = hexToRgb(fallbackBgHex); } catch {}
    page.drawRectangle({ x: offsetX, y: offsetY, width: panelW, height: panelH, color: bg });
  }
  // CLIENT-PDF IMPORT: embed the imported front page into the SAME front-panel
  // rectangle the normal cover image occupies (cover-fit to fill), losslessly
  // via embedPdf/drawPage. Skips the normal artwork path entirely. The text
  // zones below still render on top. Absent → unchanged.
  if (input.importedCoverFrontPdf) {
    try {
      const [embedded] = await doc.embedPdf(input.importedCoverFrontPdf, [0]);
      const { width: pw, height: ph } = embedded.scale(1);
      // Bleed all four sides — an imported cover page must also run past the
      // fold line, or the wrap shows bare board at the trimmed edges.
      const R = bleedPanel(offsetX, offsetY, panelW, panelH);
      const scale = Math.max(R.w / pw, R.h / ph); // cover-fit (fill) incl. bleed
      const dw = pw * scale, dh = ph * scale;
      const dx = R.x + (R.w - dw) / 2;
      const dy = R.y + (R.h - dh) / 2;
      clipRect(page, R.x, R.y, R.w, R.h);
      page.drawPage(embedded, { x: dx, y: dy, width: dw, height: dh });
      popClip(page);
      drewImage = true;
    } catch (e) {
      console.warn('[pdfgen] imported front cover failed:', (e as Error)?.message);
    }
  } else if (imgUrl) {
    try {
      const blob = await fetchAsBlob(imgUrl);
      const rawBytes = new Uint8Array(await blob.arrayBuffer());
      // PDF cover templates: embed first page directly. Avoids needing
      // server-side rasterisation just to get pixels for the front.
      if (rawBytes[0] === 0x25 && rawBytes[1] === 0x50 && rawBytes[2] === 0x44 && rawBytes[3] === 0x46) {
        const [embedded] = await doc.embedPdf(rawBytes, [0]);
        clipRect(page, offsetX, offsetY, panelW, panelH);
        page.drawPage(embedded, { x: offsetX, y: offsetY, width: panelW, height: panelH });
        popClip(page);
        drewImage = true;
      } else {
        const { bytes, mime } = await normaliseImageBlob(blob);
        const img = await embedAuto(doc, bytes, mime);
        if (usingDesign) {
          // Transparent design: contain-fit centred at trim size, optionally
          // shifted + scaled by the customer's drag/zoom in the editor. NO
          // bleed — the transparent border would otherwise let the press
          // paper show through where the bg fill expected. The bg color
          // fill already painted under this image handles the bleed area.
          const t = input.coverDesignTransform ?? { x: 0, y: 0, scale: 1 };
          const fitScale = Math.min(panelW / img.width, panelH / img.height);
          const effectiveScale = fitScale * t.scale;
          const dw = img.width * effectiveScale, dh = img.height * effectiveScale;
          // Editor uses CSS `transform: translate(x%, y%)` on the img, where
          // the percentage is relative to the IMG'S own bounding box (which
          // is the trim area thanks to object-contain). Mirror that exactly
          // so the PDF lines up pixel-for-pixel with the preview.
          const offsetXAdjust = (t.x / 100) * panelW;
          const offsetYAdjust = (t.y / 100) * panelH;
          const dx = offsetX + (panelW - dw) / 2 + offsetXAdjust;
          // PDF Y axis goes up; editor Y goes down. Flip the sign so a
          // downward drag in the editor moves the design down in the PDF.
          const dy = offsetY + (panelH - dh) / 2 - offsetYAdjust;
          page.drawImage(img, { x: dx, y: dy, width: dw, height: dh });
        } else {
          // Legacy baked-in artwork OR the client's blank-cover photo:
          // cover-fit with vertical bleed so the top/bottom seam to the
          // surrounding bleed fill is invisible. The blank photo additionally
          // honours the client's pan/zoom (blankCoverTransform), mirroring
          // the editor's CSS translate(x%,y%) scale(s) exactly.
          const isBlankPhoto = !!blankCoverUrl && imgUrl === blankCoverUrl;
          const bt = isBlankPhoto
            ? (input.blankCoverTransform ?? { x: 0, y: 0, scale: 1 })
            : { x: 0, y: 0, scale: 1 };
          // Bleed on ALL FOUR sides. This was `BLEED_PT = 6` — six POINTS
          // (~2.1mm), applied top/bottom ONLY, so the spine and fore-edge had
          // no bleed at all and head/tail fell ~1mm short of the board.
          // Pan/zoom stays a % of the PANEL (not the bled rect) so the editor
          // preview and the PDF keep matching.
          const R = bleedPanel(offsetX, offsetY, panelW, panelH);
          const drawY = R.y, drawH = R.h;
          const scale = Math.max(R.w / img.width, R.h / img.height) * (bt.scale || 1);
          const dw = img.width * scale, dh = img.height * scale;
          const dx = R.x + (R.w - dw) / 2 + (bt.x / 100) * panelW;
          // PDF Y-up vs editor Y-down: flip the translate sign (same rule as
          // the design-overlay mirror above).
          const dy = drawY + (drawH - dh) / 2 - (bt.y / 100) * panelH;
          clipRect(page, R.x, R.y, R.w, R.h);
          page.drawImage(img, { x: dx, y: dy, width: dw, height: dh });
          popClip(page);
        }
        drewImage = true;
      }
    } catch (e) {
      console.warn('[pdfgen] cover image failed:', (e as Error)?.message);
      // ⚠️ A cover with a hole is a hole (#1215, 2026-09-26).
      throw unprintableCover({ part: 'front cover', reason: (e as Error)?.message ?? 'draw failed' });
    }
  }
  if (!drewImage) {
    let bg = rgb(0.96, 0.94, 0.91);
    try { bg = hexToRgb(fallbackBgHex); } catch {}
    page.drawRectangle({ x: offsetX, y: offsetY, width: panelW, height: panelH, color: bg });
  }

  const zones = input.cover?.text_zones ?? [];

  // Reference width for font scaling — MUST match `PDF_WIDTH` in
  // `web/src/components/CoverThumbnail.tsx`. Both editor and PDF compute
  // `fontSize = zone.font_size × (renderWidth / refW)`, so any divergence
  // here makes PDF text scale wrong relative to the preview. Previously this
  // used `cover.width_px` (the source image's pixel width), but the editor
  // is hardcoded to 1860 — so PDFs of covers whose source image was wider
  // than 1860 came out with text 30-50% smaller than the editor showed.
  const refW = 1860;
  const fontScale = panelW / refW;

  for (let i = 0; i < zones.length; i++) {
    const z = zones[i];
    const o = input.zoneOverrides[i] ?? input.zoneOverrides[String(i)] ?? {};
    if (o.hidden) continue;
    const text = resolveZoneText(z, i, input);
    if (!text) continue;

    // Override fontFamily wins over the template's default so the PDF
    // honors the picker the customer used in the editor sidebar.
    const overrideFamily = (o as { fontFamily?: string }).fontFamily;
    const familyForPdf = primaryFontName(overrideFamily) ?? z.font_family;
    const font = await getFont(doc, familyForPdf, fontCache, fallbackFont);
    const zoneW = z.width * panelW;
    const zoneH = z.height * panelH;

    if (hasArabic(text)) {
      const ok = await drawArabicCoverZone(doc, page, z, o as Record<string, unknown>, text, overrideFamily, offsetX, offsetY, panelW, panelH, zoneW, zoneH, fontScale);
      if (ok) continue;
    }

    // Hard-line breaks — the cover EditableText now allows Enter to insert
    // a newline. Each line wraps independently within the zone width.
    const sourceLines = (font === fallbackFont ? toWinAnsi(text) : text).split('\n');
    // The editor's letter-spacing (0.01em) counts in the width it fits.
    const widestAt = (size: number) => sourceLines.reduce((max, ln) => {
      let w: number;
      try { w = font.widthOfTextAtSize(ln, size); } catch { w = ln.length * size * 0.5; }
      return Math.max(max, w + ln.length * size * 0.01);
    }, 0);
    // SIZE = the editor's (CoverThumbnail `finalFontSize`): its font-size override,
    // fit by width AND height unless resized by hand — lib/pdfTextLayout.ts (2026-10-17).
    const fontSize = coverZoneFontSize({
      baseSize: (o as { fontSize?: number }).fontSize ?? z.font_size ?? 24,
      fontScale, manualScale: o.scale ?? 1, zoneW, zoneH, lines: sourceLines.length, widestAt,
    });

    const align = (z as { align?: 'left' | 'center' | 'right' }).align ?? 'center';
    const zoneLeft = (o.x ?? z.x) * panelW;
    // BASELINES = the browser's line box with the font's own ascent/descent — the
    // old hand formula printed the title 0.63·fs higher than the editor (#1248).
    const ascent = font.heightAtSize(fontSize, { descender: false });
    const descent = Math.max(0, font.heightAtSize(fontSize) - ascent);
    const zoneBottom = panelH - (o.y ?? z.y) * panelH - zoneH;
    const baselines = coverZoneBaselines({ zoneBottom, zoneH, lines: sourceLines.length, fontSize, ascent, descent });

    // Color override from the floating toolbar wins over the template's
    // default for the same reason fontFamily does above.
    const overrideColor = (o as { color?: string }).color;
    let color = rgb(0, 0, 0);
    try { color = hexToRgb(overrideColor ?? z.color ?? '#000000'); } catch {}

    for (let li = 0; li < sourceLines.length; li++) {
      const line = sourceLines[li];
      let lineW = 0;
      try { lineW = font.widthOfTextAtSize(line, fontSize); }
      catch { lineW = line.length * fontSize * 0.5; }
      let drawX = zoneLeft;
      if (align === 'center') drawX = zoneLeft + (zoneW - lineW) / 2;
      else if (align === 'right') drawX = zoneLeft + (zoneW - lineW);
      const baselineY = baselines[li];
      try {
        page.drawText(line, {
          x: offsetX + drawX,
          y: offsetY + baselineY,
          size: fontSize, font, color,
        });
      } catch (e) {
        console.warn('[pdfgen] zone draw failed:', (e as Error)?.message);
      }
    }
  }

  for (const ez of input.extraZones) {
    if (!ez?.text) continue;
    const text = safeText(ez.text);
    if (!text) continue;
    const font = await getFont(doc, primaryFontName(ez.fontFamily), fontCache, fallbackFont);
    const fontSize = Math.max(6, (ez.fontSize ?? 18) * fontScale);
    if (hasArabic(text)) {
      // Same browser-drawn lines as the title zones; anchor / align / top as below.
      try {
        const cssStack = coverCssFontFamily(ez.fontFamily ?? null);
        const alignA = ez.align ?? 'center';
        const anchorXA = (ez.x ?? 0.5) * panelW;
        let baseline = panelH - (ez.y ?? 0.5) * panelH - fontSize * 0.82;
        let drawn = true;
        for (const ln of text.split('\n')) {
          if (ln.trim()) {
            const r = await rasterCoverArabicLine(ln, cssStack, fontSize, String(ez.color ?? '#000000'));
            if (!r) { drawn = false; break; }
            const x0 = alignA === 'center' ? anchorXA - r.widthPt / 2 : alignA === 'right' ? anchorXA - r.widthPt : anchorXA;
            const img = await doc.embedPng(r.png);
            page.drawImage(img, { x: offsetX + x0 - r.padPt, y: offsetY + Math.max(0, baseline) - r.descentPt - r.padPt, width: r.imgWPt, height: r.imgHPt });
          }
          baseline -= fontSize * 1.1;
        }
        if (drawn) continue;
      } catch (e) { console.warn('[pdfgen] arabic extra zone failed:', (e as Error)?.message); }
    }
    // Anchor + alignment mirror the editor's hugging box (ExtraTextOverlay):
    // `x` is the anchor; `align` says which edge it pins. We offset x left by the
    // measured line width for centre/right so the printed text lands where the
    // customer placed it. Vertical: `y` is the box TOP; drawText wants the
    // baseline, ~0.82·fontSize below the top.
    const align = ez.align ?? 'center';
    const anchorX = (ez.x ?? 0.5) * panelW;
    const maxW = Math.min(0.96, ez.width ?? 0.8) * panelW;
    let color = rgb(0, 0, 0);
    try { color = hexToRgb(ez.color ?? '#000000'); } catch {}
    try {
      const renderText = font === fallbackFont ? toWinAnsi(text) : text;
      let lineW = maxW;
      try { lineW = Math.min(maxW, font.widthOfTextAtSize(renderText.replace(/\n/g, ' '), fontSize)); } catch { /* fall back to maxW */ }
      const drawX = align === 'center' ? anchorX - lineW / 2 : align === 'right' ? anchorX - lineW : anchorX;
      const baselineY = panelH - (ez.y ?? 0.5) * panelH - fontSize * 0.82;
      page.drawText(renderText, {
        x: offsetX + drawX, y: offsetY + Math.max(0, baselineY),
        size: fontSize, font, color,
        maxWidth: maxW, lineHeight: fontSize * 1.1,
      });
    } catch (e) {
      console.warn('[pdfgen] extra zone draw failed:', (e as Error)?.message);
    }
  }
}

/**
 * Lays out 1-4 user-uploaded back-cover photos centred on the back panel.
 * Mirrors the editor's grid (1, 2 in a row, 3 with one wide bottom, 4 in 2x2).
 * Photo URLs may be blob: URLs from the same browser session, or remote
 * signed URLs — both fetchable here.
 */
async function drawBackCoverPhotos(
  doc: PDFDocument,
  page: PDFPage,
  imageUrls: string[],
  panelX: number,
  panelY: number,
  /** Artwork panel = the physical board (pt). Was A4_W/A4_H (210×297) while the
   *  board is 200×303 — which put the back panel 10mm off the board. */
  panelW: number,
  panelH: number,
) {
  const imgs = imageUrls.filter(Boolean).slice(0, 4);
  if (imgs.length === 0) return;

  // Centred ~70% wide square block, like the editor's back-cover grid.
  const blockSize = panelW * 0.7;
  const blockX = panelX + (panelW - blockSize) / 2;
  const blockY = panelY + (panelH - blockSize) / 2;
  const gap = 6;

  type Cell = { x: number; y: number; w: number; h: number };
  let cells: Cell[];
  if (imgs.length === 1) {
    cells = [{ x: blockX, y: blockY, w: blockSize, h: blockSize }];
  } else if (imgs.length === 2) {
    const w = (blockSize - gap) / 2;
    cells = [
      { x: blockX,            y: blockY, w, h: blockSize },
      { x: blockX + w + gap,  y: blockY, w, h: blockSize },
    ];
  } else if (imgs.length === 3) {
    const halfW = (blockSize - gap) / 2;
    const halfH = (blockSize - gap) / 2;
    cells = [
      { x: blockX,                    y: blockY + halfH + gap, w: halfW, h: halfH },
      { x: blockX + halfW + gap,      y: blockY + halfH + gap, w: halfW, h: halfH },
      { x: blockX,                    y: blockY,               w: blockSize, h: halfH },
    ];
  } else {
    const halfW = (blockSize - gap) / 2;
    const halfH = (blockSize - gap) / 2;
    cells = [
      { x: blockX,               y: blockY + halfH + gap, w: halfW, h: halfH },
      { x: blockX + halfW + gap, y: blockY + halfH + gap, w: halfW, h: halfH },
      { x: blockX,               y: blockY,               w: halfW, h: halfH },
      { x: blockX + halfW + gap, y: blockY,               w: halfW, h: halfH },
    ];
  }

  for (let i = 0; i < imgs.length; i++) {
    const url = imgs[i];
    const cell = cells[i];
    try {
      const blob = await fetchAsBlob(url);
      // Back-cover photos sit in small cells (≤ a quarter of A4) and have no
      // customer zoom, so resolve to the cell size at 300 dpi instead of the
      // full cap. Resolution-only — the cover-fit math below is unchanged.
      const { bytes, mime } = await normaliseImageBlob(
        blob,
        computeSlotTargetLongEdge(cell.w, cell.h, 1),
      );
      const img = await embedAuto(doc, bytes, mime);
      const scale = Math.max(cell.w / img.width, cell.h / img.height);
      const dw = img.width * scale, dh = img.height * scale;
      // Rounded clip matches the editor's `rounded-md` (~6pt) on each cell.
      const radius = Math.min(8, cell.w * 0.04, cell.h * 0.04);
      clipRoundedRect(page, cell.x, cell.y, cell.w, cell.h, radius);
      page.drawImage(img, {
        x: cell.x - (dw - cell.w) / 2,
        y: cell.y - (dh - cell.h) / 2,
        width: dw, height: dh,
      });
      popClip(page);
    } catch (e) {
      console.warn('[pdfgen] back-cover photo failed:', (e as Error)?.message);
      throw unprintableCover({ part: 'back cover', reason: (e as Error)?.message ?? 'draw failed' });
    }
  }
}

/**
 * COVER ELEMENTS (2026-10-20). Each face is drawn like a page: the graphics
 * state is translated to the face's frame (bottom-left), clipped to the frame +
 * bleed, and the face's elements go through the interior's drawPageElements —
 * the same contain-fit icons, cover-fit photos, rotation, flips, opacity and
 * soft shadows the pages print — with the face's frame as the "page". The
 * editor lays the same % in a box of the same mm aspect (parity:
 * coverElements.test.ts).
 *
 * ⚠️ A placed photo is a photo the editor shows (#1215): one that exists in the
 * album but cannot be drawn STOPS the render. A photo deleted since (no longer
 * in `photos`) is an empty frame in the editor and prints nothing.
 */
async function drawCoverElements(
  doc: PDFDocument,
  page: PDFPage,
  input: AlbumPdfInput,
  G: CoverPanelGeometry,
  fallbackFont: PDFFont,
  fontCache: Map<string, PDFFont>,
): Promise<void> {
  const all = readCoverElements(input.coverElements);
  if (!all.length) return;
  const photoMap = new Map((input.photos ?? []).map((p) => [p.id, p]));
  const rects = coverFaceRectsPt(G);
  for (const face of COVER_FACES) {
    const els = elementsOnFace(all, face).filter((e) => e.type !== 'photo' || (e.photoId && photoMap.has(e.photoId)));
    if (!els.length) continue;
    const r = rects[face];
    const c = coverFaceClipPt(face, G);
    clipRect(page, c.x, c.y, c.w, c.h);
    page.pushOperators(pushGraphicsState(), concatTransformationMatrix(1, 0, 0, 1, r.x, r.y));
    let drawn: Set<string>;
    try {
      drawn = await drawPageElements(doc, page, els, r.w, r.h, fontCache, fallbackFont, (photoId) => {
        const ph = photoMap.get(photoId);
        return ph?.signed_url ?? (ph as { fallback_url?: string | null } | undefined)?.fallback_url ?? null;
      });
    } finally {
      page.pushOperators(popGraphicsState());
      popClip(page);
    }
    for (const e of els) {
      if (e.type === 'photo' && e.photoId && !drawn.has(e.photoId)) {
        throw unprintableCover({ part: `cover ${face} (photo ${e.photoId})`, reason: 'photo element not drawn' });
      }
    }
  }
}

// Shared hardcover assembly geometry — derived from the EXISTING cover spread
// so the cover's guide lines and the universal fiche de montage land at the
// SAME positions (overlay the printed cover on the fiche and the marks
// coincide). Boards are trimmed to fit two-up on the A3+ sheet; the spine board
// is the fixed 0,8 cm standard. The cover artwork is NOT moved by any of this.
/** Single source of truth for cover geometry — resolve the effective
 *  PrintSettings for a render. Prefers input.printSettings (the admin Paramètres
 *  d'impression, the SAME object the fiche de montage consumes). Falls back to
 *  reconstructing from the legacy spineColumnMm / spineBoardMm fields, then to
 *  DEFAULT_PRINT_SETTINGS — so a cover rendered with no settings is byte-identical
 *  to the pre-refactor output (483 × 329 sheet, 216 × 303 boards, 16 mm column). */
function effectiveCoverSettings(input: AlbumPdfInput): PrintSettings {
  // withDerivedVinyl recomputes sheetWmm/sheetHmm from the case spec so the
  // vinyl size always follows the formula — a stale stored sheet (483×329) or a
  // changed spine can never desync it.
  if (input.printSettings) return withDerivedVinyl({ ...DEFAULT_PRINT_SETTINGS, ...input.printSettings });
  const spineWmm = input.spineBoardMm ?? DEFAULT_PRINT_SETTINGS.spineWmm;
  const jointGapMm = input.spineColumnMm != null
    ? Math.max(0, (input.spineColumnMm - spineWmm) / 2)
    : DEFAULT_PRINT_SETTINGS.jointGapMm;
  return withDerivedVinyl({ ...DEFAULT_PRINT_SETTINGS, spineWmm, jointGapMm });
}

// Assembly-guide layer — drawn ONLY when input.showAssemblyGuides === true (an
// ADMIN toggle; customer PDFs are ALWAYS clean). Now exactly ONE element, at low
// opacity, geometry derived from the board variables (no hardcoded positions):
//
//   SPINE-CENTRE TICKS — a short tick at the TOP and BOTTOM of the spine centre
//   (x = turnIn + boardW + spine/2). Colour auto-contrasts with the spine
//   background (dark on light, light on dark/saturated) so it is always visible
//   and never matches the bg.
//
// The 4 board-CORNER cut marks were REMOVED at the user's request (2026-06-30) —
// only the centered spine-centre ticks remain. (Earlier removals: fold/turn-in
// lines, board-edge dashes, coloured overlays, board-placement ticks.)
function drawCoverGuides(
  page: PDFPage,
  spineBgHex: string,
  L: TemplateLayout,
  _sheetW: number, // unused since the corner cut marks were removed
  sheetH: number,
  boardThicknessMm: number,
): void {
  const GUIDE_OPACITY = 0.6;

  // Spine centre x — from the SHARED computeTemplateLayout (assemblyTemplate) so
  // the cover guide and the fiche de montage stay ONE calculation.
  const spineCx = L.sheetCx;

  // CORNER CUT MARKS REMOVED (user request, 2026-06-30) — the 4 board-corner
  // L-brackets are gone; ONLY the CENTERED spine-centre ticks remain.

  // ── Spine-centre ticks — contrasting colour, top + bottom ──────────────────
  const tickColor = coverLuminance(spineBgHex) > 0.5 ? hexToRgb('#1a1a1a') : hexToRgb('#f5f5f5');
  const tickLen = 2.5 * boardThicknessMm * MM_TO_PT; // ~5 mm short tick
  page.drawLine({ start: { x: spineCx, y: 0 }, end: { x: spineCx, y: tickLen }, thickness: 0.8, color: tickColor, opacity: GUIDE_OPACITY });
  page.drawLine({ start: { x: spineCx, y: sheetH }, end: { x: spineCx, y: sheetH - tickLen }, thickness: 0.8, color: tickColor, opacity: GUIDE_OPACITY });
}

export async function generateCoverPdf(input: AlbumPdfInput): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  doc.registerFontkit(fontkit);

  // ── SINGLE SOURCE OF TRUTH for all print geometry ─────────────────────────
  // Board / sheet / spine / hinge / turn-in all come from the admin PrintSettings
  // via computeTemplateLayout — the SAME function + inputs the fiche de montage
  // uses (assemblyTemplate.ts) — so the cover guides and the fiche derive from
  // ONE calculation and can never drift. DEFAULT_PRINT_SETTINGS reproduces the
  // historical 483 × 329 sheet / 216 × 303 boards / 16 mm spine column exactly.
  const s = effectiveCoverSettings(input);
  const L = computeTemplateLayout(s);
  const COVER_PAPER_W = s.sheetWmm * MM_TO_PT;
  const COVER_PAPER_H = s.sheetHmm * MM_TO_PT;
  const spineBoardMm = s.spineWmm;
  // ARTWORK PANELS = THE PHYSICAL BOARDS (fixed 2026-07-29).
  // These used to be A4_W (210mm) × A3PLUS_H (297mm), centred independently of
  // the boards — so the guides said 200 × 303 while the picture was drawn
  // 210 × 297. Result: 10mm of photo past the fore-edge, the back panel 10mm
  // off, and a bare strip at head and tail. coverPanelGeometry derives the
  // panels from the SAME computeTemplateLayout the guides use, so the two can
  // no longer disagree. A4_W / A3PLUS_H must not come back into the cover path;
  // A4_H stays the INTERIOR height (297mm) and is 6mm away from the board.
  const G = coverPanelGeometry(s);
  const CVR_W = G.panelW;        // 200mm — front/back artwork panel width
  const CVR_H = G.panelH;        // 303mm — artwork panel height
  const SPINE_W_ = G.spineW;     // printed spine COLUMN (board + both hinges)
  const COVER_TY = G.panelY;
  const HC_CX = COVER_PAPER_W / 2;                              // spine centre x

  // A3+ press sheet — the cover spread (back + spine + front) is centred; the
  // surrounding margin doubles as bleed + turn-in for case-bound hardcover.
  const page = doc.addPage([COVER_PAPER_W, COVER_PAPER_H]);

  // Only HelveticaBold is used now (the QUATRIEME footer was removed).
  const helvBold = await doc.embedFont(StandardFonts.HelveticaBold);
  const fontCache = new Map<string, PDFFont>();

  const BACK_X  = G.backX;
  const SPINE_X = G.spineX;
  const FRONT_X = G.frontX;

  // Pick the spine + back colour. The A3+ spread is [back | spine | front]
  // so the back/spine fill must match the front cover's background plate to
  // avoid a visible seam. Priority chain:
  //   1. backCoverBgColor   — user explicit pick (overrides everything)
  //   2. dominant_color     — admin's curated value, editable via the cover
  //                           admin's bg color picker. Trust the admin's
  //                           pick over any pixel inference; otherwise a
  //                           sampled colour from a stale or transparent
  //                           thumbnail_url would override the new bg the
  //                           admin just set.
  //   3. outer-frame sample — modal pixel of the cover's edge band, used
  //                           only as a fallback when dominant_color is
  //                           missing (rare — auto-extract on upload sets
  //                           it). Sampled from thumbnail_url so the
  //                           result matches what the front panel renders.
  //   4. cream              — last-resort fallback for blank covers
  const sampleUrl = input.cover?.thumbnail_url ?? input.coverImageUrl;
  const needSample = !input.backCoverBgColor && !input.cover?.dominant_color;
  const coverImageHex = needSample && sampleUrl
    ? await sampleCoverBackgroundHex(sampleUrl).catch(() => null)
    : null;
  const sharedBg =
    input.backCoverBgColor
    || input.cover?.dominant_color
    || coverImageHex
    || '#F5EFE0';
  // Each face can be recolored independently in the editor (front dot,
  // spine swatch, back dot). Fall back to the shared color when a face
  // override isn't set so legacy customizations keep the old look.
  const backHex = input.backCoverBgColor || sharedBg;
  const spineHex = input.spineConfig?.bgColor || sharedBg;
  const frontHex = input.frontCoverBgColor || sharedBg;

  // ── Full-paper bleed fill ──────────────────────────────────────────────
  // Extends the back/spine/front bg colors all the way to the press-sheet
  // edges so the press operator's trim line cuts through coloured stock, not
  // bare white. Split horizontally at the spine column.
  // Seamless: a full-sheet base in the back colour, the front HALF on top, then
  // the spine on top. Regions OVERLAP (front runs under the spine) instead of
  // abutting, so there's no hairline seam at the spine-column edges when the
  // three colours match (the common case). Distinct colours still read as a
  // clean boundary.
  page.drawRectangle({ x: 0, y: 0, width: COVER_PAPER_W, height: COVER_PAPER_H, color: hexToRgb(backHex) });
  page.drawRectangle({ x: HC_CX, y: 0, width: COVER_PAPER_W - HC_CX, height: COVER_PAPER_H, color: hexToRgb(frontHex) });
  page.drawRectangle({ x: SPINE_X, y: 0, width: SPINE_W_, height: COVER_PAPER_H, color: hexToRgb(spineHex) });

  // Back cover bg (already painted above via the full-paper fill, but
  // re-drawn at trim-area precision so any future overlays land cleanly).
  page.drawRectangle({ x: BACK_X, y: COVER_TY, width: CVR_W, height: CVR_H, color: hexToRgb(backHex) });

  // CLIENT-PDF IMPORT (full wrap): the client's finished cover, re-fit to OUR
  // specs. The composite is already laid out to our sheet (front→front panel,
  // spine→spine column, back→back panel), so it draws to FILL the whole sheet as
  // one continuous piece — no white, spine included. It REPLACES all per-panel
  // artwork/text; our logo (back), marry-label + guides still overlay it below.
  const usingFullWrap = !!input.coverFullWrapImageUrl;
  if (usingFullWrap) {
    try {
      const blob = await fetchAsBlob(input.coverFullWrapImageUrl!);
      const { bytes, mime } = await normaliseImageBlob(blob);
      const img = await embedAuto(doc, bytes, mime);
      const scale = Math.max(COVER_PAPER_W / img.width, COVER_PAPER_H / img.height);
      const dw = img.width * scale, dh = img.height * scale;
      page.drawImage(img, {
        x: (COVER_PAPER_W - dw) / 2, y: (COVER_PAPER_H - dh) / 2,
        width: dw, height: dh,
      });
    } catch (e) { console.warn('[pdfgen] full-wrap cover failed:', (e as Error)?.message); }
  }

  // CLIENT-PDF IMPORT: embed the imported back page into the back panel
  // (cover-fit to fill), losslessly, INSTEAD of the photo grid. Absent → the
  // normal back-cover photo grid runs unchanged.
  if (!usingFullWrap && input.importedCoverBackPdf) {
    try {
      const [embedded] = await doc.embedPdf(input.importedCoverBackPdf, [0]);
      const { width: pw, height: ph } = embedded.scale(1);
      const RB = bleedPanel(BACK_X, COVER_TY, CVR_W, CVR_H);
      const scale = Math.max(RB.w / pw, RB.h / ph); // cover-fit (fill) incl. bleed
      const dw = pw * scale, dh = ph * scale;
      const dx = RB.x + (RB.w - dw) / 2;
      const dy = RB.y + (RB.h - dh) / 2;
      clipRect(page, RB.x, RB.y, RB.w, RB.h);
      page.drawPage(embedded, { x: dx, y: dy, width: dw, height: dh });
      popClip(page);
    } catch (e) {
      console.warn('[pdfgen] imported back cover failed:', (e as Error)?.message);
    }
  } else if (!usingFullWrap) {
    // FULL-PAGE back photo (2026-07-15): cover-fit filling the whole back
    // panel (same clip + fit as the imported-back-cover path above). Drawn
    // FIRST so the remaining grid photos + logo overlay it.
    if (input.backCoverFullPageImage) {
      try {
        const blob = await fetchAsBlob(input.backCoverFullPageImage);
        const { bytes, mime } = await normaliseImageBlob(blob);
        const img = await embedAuto(doc, bytes, mime);
        const RF = bleedPanel(BACK_X, COVER_TY, CVR_W, CVR_H);
        const scale = Math.max(RF.w / img.width, RF.h / img.height);
        const dw = img.width * scale, dh = img.height * scale;
        clipRect(page, RF.x, RF.y, RF.w, RF.h);
        page.drawImage(img, {
          x: RF.x + (RF.w - dw) / 2,
          y: RF.y + (RF.h - dh) / 2,
          width: dw, height: dh,
        });
        popClip(page);
      } catch (e) {
        console.warn('[pdfgen] full-page back-cover photo failed:', (e as Error)?.message);
        throw unprintableCover({ part: 'back cover (full-page photo)', reason: (e as Error)?.message ?? 'draw failed' });
      }
    }
    // User-added back-cover photos (drawn before the logo so the logo overlays them)
    await drawBackCoverPhotos(doc, page, input.backCoverImages, BACK_X, COVER_TY, CVR_W, CVR_H);
  }

  if (input.logoUrl) {
    try {
      const logoBlob = await fetchAsBlob(input.logoUrl);
      const logoBytes = new Uint8Array(await logoBlob.arrayBuffer());
      const logoMime: 'image/jpeg' | 'image/png' =
        logoBlob.type === 'image/png' || (logoBytes[0] === 0x89 && logoBytes[1] === 0x50)
          ? 'image/png'
          : 'image/jpeg';
      const logo = await embedAuto(doc, logoBytes, logoMime);
      // Smaller, subtler bottom-centred logo — was ~25% of the back panel,
      // now ~12% wide which matches the editor preview.
      const ls = Math.min(CVR_W * 0.12 / logo.width, CVR_H * 0.05 / logo.height);
      page.drawImage(logo, {
        x: BACK_X + (CVR_W - logo.width * ls) / 2,
        y: COVER_TY + 30,
        width: logo.width * ls, height: logo.height * ls,
        opacity: 0.7,
      });
    } catch (e) { console.warn('[pdfgen] logo failed:', (e as Error)?.message); }
  }

  // PHOTO VIVANTE — the QR on the back cover (livingPrint.ts): a white rounded
  // plate, 26 mm QR, one line under it, centred above the logo. Drawn on top
  // of any back-cover photo so it always scans. A failure never costs the
  // cover its print (the link is also sent on WhatsApp).
  if (input.livingLink) {
    try {
      const QR = (await import('qrcode')).default;
      const dataUrl: string = await QR.toDataURL(input.livingLink, { margin: 0, width: 600, errorCorrectionLevel: 'M' });
      const pngBytes = Uint8Array.from(atob(dataUrl.split(',')[1]), (ch) => ch.charCodeAt(0));
      const qr = await doc.embedPng(pngBytes);
      const MMPT = 72 / 25.4;
      const q = 26 * MMPT, pad = 4 * MMPT, textH = 7 * MMPT;
      const plateW = Math.max(q + pad * 2, 64 * MMPT), plateH = q + pad * 2 + textH;
      const px = BACK_X + (CVR_W - plateW) / 2;
      const py = COVER_TY + 30 + CVR_H * 0.05 + 6 * MMPT;   // just above the logo
      // Rounded white plate (3 mm corners), solid: no transparency in print.
      const r = 3 * MMPT;
      const plate = `M ${r} 0 H ${plateW - r} Q ${plateW} 0 ${plateW} ${r} V ${plateH - r} Q ${plateW} ${plateH} ${plateW - r} ${plateH} H ${r} Q 0 ${plateH} 0 ${plateH - r} V ${r} Q 0 0 ${r} 0 Z`;
      page.drawSvgPath(plate, { x: px, y: py + plateH, color: rgb(1, 1, 1), borderWidth: 0 });
      page.drawImage(qr, { x: px + (plateW - q) / 2, y: py + textH + pad, width: q, height: q });
      const label = toWinAnsi(LIVING_BACK_TEXT);
      const fs = 7.5;
      const tw = helvBold.widthOfTextAtSize(label, fs);
      page.drawText(label, { x: px + (plateW - tw) / 2, y: py + pad + 1.5 * MMPT, size: fs, font: helvBold, color: rgb(0.16, 0.13, 0.11) });
    } catch (e) {
      console.warn('[pdfgen] Photo Vivante QR failed:', (e as Error)?.message);
    }
  }

  // (Spine column bg is already painted full-height + seamless by the bleed
  // fill above — no separate trim-area rect, which used to leave a hairline.)
  const spineText = safeText(input.spineConfig?.text ?? input.destinationText ?? '').trim();
  if (spineText && !usingFullWrap) {
    const sScale = input.spineConfig?.scale ?? 1;
    let sSize = Math.max(18, (input.spineConfig?.fontSize ?? 28) * sScale);
    // SPINE COLOUR (fixed 2026-07-24): the customer's spineConfig.textColor
    // WINS. This used to prefer the front title zone's colour, which meant a
    // deliberately-chosen blue spine still printed in the cover design's own
    // title colour (#FFFFFF on the Europe design) — the editor showed blue,
    // the PDF printed white. The editor keeps the two in sync when the TITLE
    // colour changes (see AlbumEditorPage's spine sync), so mirroring here as
    // well only served to override an explicit choice. Title-zone colour is
    // kept as the fallback for legacy sessions that never stored a spine
    // colour; white is the last resort.
    let titleColor: string | undefined;
    const titleZones = input.cover?.text_zones ?? [];
    const di = titleZones.findIndex((z) => z.type === 'destination');
    if (di >= 0) {
      const ov = ((input.zoneOverrides ?? {}) as Record<string, { color?: string }>)[di]
        ?? ((input.zoneOverrides ?? {}) as Record<string, { color?: string }>)[String(di)];
      titleColor = ov?.color ?? titleZones[di].color ?? undefined;
    }
    let color = rgb(1, 1, 1);
    try { color = hexToRgb(input.spineConfig?.textColor ?? titleColor ?? '#FFFFFF'); } catch {}
    const spineFamily = primaryFontName((input.spineConfig as { fontFamily?: string } | null)?.fontFamily) ?? 'Londrina Solid';
    const font = await getFont(doc, spineFamily, fontCache, helvBold);
    const text = font === helvBold ? toWinAnsi(spineText.toUpperCase()) : spineText.toUpperCase();
    // Arabic spine: drawn by the browser (« ARABIC ON THE COVER »), bold as the
    // editor's spine, turned -90° exactly like the Latin spine text below.
    let spineDone = false;
    if (hasArabic(spineText)) {
      try {
        const cssStack = coverCssFontFamily((input.spineConfig as { fontFamily?: string } | null)?.fontFamily ?? "'Londrina Solid', sans-serif");
        const m = await measureCoverArabic(spineText, cssStack, 700);
        if (m) {
          const capPt = spineBoardMm * MM_TO_PT * 0.8;
          const asc = (m.a100 * sSize) / 100;
          if (asc > capPt && asc > 0) sSize = sSize * (capPt / asc);
          const colHex = input.spineConfig?.textColor ?? titleColor ?? '#FFFFFF';
          const r = await rasterCoverArabicLine(spineText, cssStack, sSize, colHex, 700);
          if (r) {
            const img = await doc.embedPng(r.png);
            page.drawImage(img, {
              x: SPINE_X + (SPINE_W_ - r.imgHPt) / 2,
              y: COVER_TY + (CVR_H + r.imgWPt) / 2,
              width: r.imgWPt, height: r.imgHPt,
              rotate: degrees(-90),
            });
            spineDone = true;
          }
        }
      } catch (e) { console.warn('[pdfgen] arabic spine failed:', (e as Error)?.message); }
    }
    if (!spineDone) try {
      // Spine text now reads TOP→BOTTOM when the book sits flat on a
      // table cover-up, matching Western publishing convention. Was
      // degrees(90) which reads bottom-to-top (forces the customer to
      // tilt their head left). With rotate(-90):
      //   pdf-lib pivots about (x, y); text grows DOWNWARD from y, with
      //   ascender extending RIGHTWARD from x. Bounding box becomes:
      //     x ∈ [x, x + ascent],  y ∈ [y - textWidth, y]
      //   To centre on spine column:  x + ascent/2 = SPINE_X + SPINE_W/2
      //   To centre vertically:       y - textWidth/2 = COVER_TY + CVR_H/2
      //
      // Cap the glyph height to the RIGID hardcover spine (~8 mm / 0.8 cm),
      // which is narrower than the 14 mm printed spine COLUMN (SPINE_W). Without
      // this, a 28pt+ setting renders ~1 cm tall and spills off the rigid board
      // onto the hinge. `heightAtSize(…,{descender:false})` is the same
      // across-spine extent the centring above uses, so scaling the size down
      // to fit it keeps the text on the board (≤80% of 8 mm leaves a margin for
      // binding tolerance). Only ever shrinks — small spine text is untouched.
      const RIGID_SPINE_MAX_PT = spineBoardMm * MM_TO_PT * 0.8; // cap on the rigid spine board (default 10 mm → ≤ 8 mm)
      const ascentAtRequested = font.heightAtSize(sSize, { descender: false });
      if (ascentAtRequested > RIGID_SPINE_MAX_PT && ascentAtRequested > 0) {
        sSize = sSize * (RIGID_SPINE_MAX_PT / ascentAtRequested);
      }
      const textWidth = font.widthOfTextAtSize(text, sSize);
      const ascent = font.heightAtSize(sSize, { descender: false });
      page.drawText(text, {
        x: SPINE_X + (SPINE_W_ - ascent) / 2,
        y: COVER_TY + (CVR_H + textWidth) / 2,
        size: sSize, font, color,
        rotate: degrees(-90),
      });
    } catch (e) { console.warn('[pdfgen] spine draw failed:', (e as Error)?.message); }
  }

  // Front cover — pass the front-specific bg so the fallback fill (when
  // the artwork is hidden or fails to load) matches what the editor shows.
  // Skipped for a full-wrap import (the composite already fills the front panel).
  if (!usingFullWrap) {
    await drawFrontCover(doc, page, input, FRONT_X, COVER_TY, CVR_W, CVR_H, helvBold, fontCache, frontHex);
  }

  // ── Cover elements (stickers, icons, the client's photos) ────────────────
  // ABOVE every face's artwork, title and back photos; UNDER the guides and the
  // marry-label (bindery marks stay readable). Drawn on a full-wrap import too.
  await drawCoverElements(doc, page, input, G, helvBold, fontCache);

  // ── Assembly guide layer ─────────────────────────────────────────────────
  // Default OFF — customer covers are ALWAYS clean. Drawn ONLY when an admin
  // explicitly opts in (showAssemblyGuides === true). Marks live in the
  // turn-in / bleed margins (never on artwork). The spine bg drives the
  // tick's auto-contrast colour.
  if (input.showAssemblyGuides === true) drawCoverGuides(page, spineHex, L, COVER_PAPER_W, COVER_PAPER_H, s.boardThicknessMm);
  // ORDER STAMP (« #1242 1/1 ») — lib/orderStamp.ts. PLACE (owner, 10-23,
  // after #WA-41692 printed): back in the middle of the bottom TURN-IN, centred
  // under the spine, as before 10-21 (10-21 → 10-23 it sat on the back board,
  // spine side). COLOUR (owner, 10-21): drawn LAST so
  // the pixels measured under it are the real ones (plate + photos + stickers
  // + imported wrap): light → pure black, dark → pure white, busy → a rounded
  // box in the opposite colour behind it. Bold, 8 pt.
  if (input.orderLabel) {
    try {
      const { style, placement } = await drawOrderStamp({
        doc, page, pageIndex: 0,
        text: toWinAnsi(input.orderLabel),
        font: helvBold,
        place: (w, a, d) => coverStampPlacement(L, w, a, d),
        plateHex: backHex,
      });
      console.info(`[stamp] cover ${input.orderLabel}: ${style.text} text${style.box ? ` on ${style.box} box` : ''} — ${style.reason} @ (${(placement.box.x / MM_TO_PT).toFixed(1)}, ${(placement.box.y / MM_TO_PT).toFixed(1)}) mm`);
    } catch (e) { console.warn('[pdfgen] order stamp failed:', (e as Error)?.message); }
  }

  // ⚠️ THE PRINT GATE (2026-10-07): every page must survive PDF → PostScript,
  // or nothing is saved, uploaded or downloaded (printPdfCheck.ts).
  await assertPrintSafe(doc, null, 'couverture');
  return doc.save();
}

// ─── Interior rendering ──────────────────────────────────────────────────────

/**
 * The intro page this album's interior prints, or null. A THEMED book (its
 * first page is the S1 opening, bookStructure.ts) prints none, whatever the
 * brand setting holds; a plain book prints the brand's, exactly as before.
 * `pages` is the WHOLE album, never a redo subset.
 */
export function interiorIntroUrl(
  pages: ReadonlyArray<BookPageLike | null | undefined> | null | undefined,
  introPageUrl: string | null | undefined,
): string | null {
  return isThemedBook(pages) ? null : (introPageUrl ?? null);
}

async function generateInteriorPdf(
  rawInput: AlbumPdfInput,
  onProgress?: (p: PdfGenProgress) => void,
  imageQuality: number = 0.92,
): Promise<Uint8Array> {
  // ⚠️ #WA-510802 — a model page whose `photos` and windows disagree (an old
  // « Régénérer » kept the previous model's windows) prints as the plain grid
  // of its photos, as the editor now opens it: every photo printed once,
  // instead of a refused print or a window over another page's photo.
  // Same pure function as the editor (lib/themedPageEdits.repairModelPages).
  const healed = repairModelPages(rawInput.pages);
  if (healed.repaired.length) console.warn('[pdfgen] model pages out of step with their photos, printed as grids:', healed.repaired);
  const input: AlbumPdfInput = healed.repaired.length ? { ...rawInput, pages: healed.pages } : rawInput;
  // B5 — the album's gap, for this render only. Reset first so a render that
  // threw last time cannot leak its gap into the next album.
  currentBaseRules = usesBaseRules(input.layoutRules);
  // The WHOLE album decides, not `contentPages`: a redo of pages 5–8 of a
  // themed book is still a themed book (no intro, album page 5 on the right).
  currentThemedBook = isThemedBook(input.pages);
  currentGapPt = photoGapPt(currentBaseRules ? BASE_GAP_MM : Number(input.photoGapMm ?? 0));
  currentGapColor = currentGapPt > 0 ? hexToRgb(normalizeGapColor(input.photoGapColor)) : null;
  const doc = await PDFDocument.create();
  // fontkit lets us embed Google Fonts (TTFs) for the customer's chosen
  // typefaces on overlay text elements. Without it, embedFont accepts
  // only the StandardFonts (Helvetica family) and any non-WinAnsi
  // glyphs fall back silently.
  doc.registerFontkit(fontkit);
  const helvetica = await doc.embedFont(StandardFonts.Helvetica);
  const fontCache = new Map<string, PDFFont>();
  const photoMap = new Map<string, PhotoMeta>();
  for (const p of input.photos) photoMap.set(p.id, p);

  // ── Print bleed ("fond perdu") ───────────────────────────────────────────
  // Each interior page is grown by `bleedPt` on every side so the bindery can
  // trim through ink instead of paper. At the 3 mm default the A4 trim
  // (210×297) prints on a 216×303 media box; page backgrounds, full-page
  // rasters and any photo slot that touches a trim edge are extended into the
  // margin. `media` is the negative-origin media box that keeps the trim rect
  // centred (see fullBleedRect). bleedMm = 0 falls back to bare A4 trim.
  const bleedMm = Math.max(0, input.bleedMm ?? 3);
  const bleedPt = bleedMm * MM_TO_PT;
  const media = fullBleedRect(PAGE_W, A4_H, bleedPt);

  // ── Intro page ─────────────────────────────────────────────────────────
  // Single branding page the admin uploaded in Settings → Identité de
  // marque sits BEFORE the customer's content. URL can be a PDF (we use
  // the first page) or a raster image (cover-fit to A4). A failure is
  // logged and skipped so a missing intro doesn't take down submission.
  // ⚠️ A THEMED BOOK HAS NO INTRO PAGE: its S1 opening is print page 1 and
  // carries the Storiz mark itself (bookStructure.ts). No intro → no intro
  // elements, and the marry-label falls back to the first content page below.
  const introPage = await embedBookendPage(doc, interiorIntroUrl(input.pages, input.introPageUrl), bleedPt, imageQuality);
  try { rawInput.onInteriorPlan?.({ introPages: introPage ? 1 : 0 }); } catch { /* report only */ }

  // Marry-label (order stamp): drawn in ONE place at the end of this function,
  // on the interior's first page (the intro when there is one), once that page
  // is complete — see INTERIOR ORDER STAMP below.

  // ── Client intro elements ──────────────────────────────────────────────────
  // The customer can add text / photos / shapes on top of the intro branding
  // background using the same free-floating element system as the content
  // pages. They share the %-based PageElement model and render through the
  // SAME `drawPageElements`, so the editor + PDF stay in render parity. The
  // editor clamps these elements OUT of the bottom QR safe zone, so the QR
  // code in the branding image is never covered — no extra logic needed here.
  // Elements are positioned in A4 trim space (origin 0,0), matching the
  // content-page call below; with bleed the intro page's TrimBox is at
  // (0,0,A4_W,A4_H) so this lands on the visible page area exactly like the
  // content pages. No intro elements → this is a no-op (legacy orders safe).
  if (introPage && input.introElements && input.introElements.length > 0) {
    try {
      await drawPageElements(doc, introPage, input.introElements, PAGE_W, A4_H, fontCache, helvetica);
    } catch (e) { console.warn('[pdfgen] intro elements failed:', (e as Error)?.message); }
  }

  // ── CLIENT-PDF IMPORT: imported interior ─────────────────────────────────
  // When an imported interior PDF is supplied (manual order), build the body by
  // embedding EACH of its pages — losslessly via embedPdf/drawPage — onto the
  // SAME interior page geometry (A4 trim + bleed media box) the normal path
  // uses, cover-fit to fill. One imported page per interior page. This REPLACES
  // the photo-layout loop (input.pages is ignored). The intro/outro bookends,
  // the marry-label, and the ÷4 print padding below all still run, so an
  // imported album prints with the same bindery furniture as a normal one.
  // Absent → the normal photo-layout content loop runs unchanged.
  const contentPages =
    input.pageIndices && input.pageIndices.length > 0
      ? input.pageIndices
          .filter((i) => i >= 0 && i < input.pages.length)
          .map((i) => input.pages[i])
      : input.pages;

  if (input.importedInteriorPdf) {
    const srcDoc = await PDFDocument.load(input.importedInteriorPdf, { ignoreEncryption: true });
    const pageCount = srcDoc.getPageCount();
    const embedded = await doc.embedPdf(input.importedInteriorPdf, Array.from({ length: pageCount }, (_, i) => i));
    for (let pi = 0; pi < embedded.length; pi++) {
      const ep = embedded[pi];
      const page = doc.addPage([PAGE_W, A4_H]);
      if (bleedPt > 0) {
        page.setMediaBox(media.x, media.y, media.w, media.h);
        page.setBleedBox(media.x, media.y, media.w, media.h);
        page.setTrimBox(0, 0, PAGE_W, A4_H);
      }
      // Cream bleed backing so a non-bleed imported page never shows white at
      // the trimmed edge.
      page.drawRectangle({
        x: media.x, y: media.y, width: media.w, height: media.h,
        color: rgb(LETTERBOX_FILL.r, LETTERBOX_FILL.g, LETTERBOX_FILL.b),
      });
      const { width: pw, height: ph } = ep.scale(1);
      // Cover-fit to fill the A4 trim (centred). Imported pages are already at
      // album aspect, so this is essentially 1:1 with a hairline overscan.
      const scale = Math.max(PAGE_W / pw, A4_H / ph);
      const dw = pw * scale, dh = ph * scale;
      const dx = (PAGE_W - dw) / 2;
      const dy = (A4_H - dh) / 2;
      clipRect(page, 0, 0, PAGE_W, A4_H);
      page.drawPage(ep, { x: dx, y: dy, width: dw, height: dh });
      popClip(page);
      onProgress?.({
        stage: 'interior',
        current: pi + 1,
        total: embedded.length,
        message: `Page ${pi + 1} / ${embedded.length}`,
      });
    }
  } else
  // ── REDO subset filter ───────────────────────────────────────────────────
  // For a `custom-pages` redo only the admin-selected content pages are
  // rendered (the bookends above/below still ship). For every other path
  // `pageIndices` is undefined and we render the full content set. The ÷4 print
  // padding below uses doc.getPageCount(), so it stays correct for whatever
  // subset we emit here.
  for (let pi = 0; pi < contentPages.length; pi++) {
    const lp = contentPages[pi];
    // The page number the editor shows — what a person needs to find the hole.
    const albumPage = Number(lp.page) || pi + 1;
    // « PAGE DE FIN » (lib/outroPage.ts) — the brand outro image, full page,
    // exactly like the automatic outro bookend below. It is one content page
    // (billed, counted in the ×4 padding). If the image cannot load it falls
    // through to the blank-page branch: a white page, never a hole.
    if (lp.kind === 'outro' && input.outroPageUrl) {
      const drawn = await embedBookendPage(doc, input.outroPageUrl, bleedPt, imageQuality);
      if (drawn) {
        onProgress?.({ stage: 'interior', current: pi + 1, total: contentPages.length, message: `Page ${pi + 1} / ${contentPages.length}` });
        continue;
      }
    }
    const page = doc.addPage([PAGE_W, A4_H]);
    // Grow the page to the bleed media box (negative origin) while keeping the
    // A4 trim rect as the TrimBox so the printer knows where to cut. With
    // bleed 0 the media box stays at the bare A4 trim and these are no-ops.
    if (bleedPt > 0) {
      page.setMediaBox(media.x, media.y, media.w, media.h);
      page.setBleedBox(media.x, media.y, media.w, media.h);
      page.setTrimBox(0, 0, PAGE_W, A4_H);
    }
    // BLANK PAGE (2026-07-05) — a pure-white divider. Fill the full bleed box
    // white (skip cream + photo slots + template), then draw only the free
    // elements the customer added on top. The page stays in the array so page
    // count / divisible-by-4 padding is unaffected.
    if (lp.blank) {
      // ITEM 8 (2026-07-06) — honour the blank page's chosen colour (defaults
      // to white). Fills the full bleed box so the trimmed edge is the colour.
      let blankFill = rgb(1, 1, 1);
      if (lp.blank_color) { try { blankFill = hexToRgb(lp.blank_color); } catch { /* keep white */ } }
      page.drawRectangle({
        x: media.x, y: media.y, width: media.w, height: media.h,
        color: blankFill,
      });
      // STEP 2 — a blank page can carry paper too (section / closing pages).
      await drawLayoutBackground(doc, page, lp, media);
      const drawnPhotos = await drawPageElements(doc, page, lp.elements ?? [], PAGE_W, A4_H, fontCache, helvetica,
        (photoId) => {
          const ph = photoMap.get(photoId);
          return ph?.signed_url ?? (ph as { fallback_url?: string | null } | undefined)?.fallback_url ?? null;
        });
      // ⚠️ STEP 3 — a themed page lists its photos in `photos` and draws them as
      // layers. Every one must have been drawn: a missing original stops the
      // render here instead of printing a page with a hole (#1215 rule).
      // Only THEMED pages: a page a person turned blank may still list photos
      // it no longer shows (the blank toggle keeps them) — that is not a hole.
      if (lp.theme) for (const pid of lp.photos ?? []) {
        if (!drawnPhotos.has(pid)) throw unprintablePhoto({ albumPage, photoId: pid, reason: 'photo layer not drawn on a themed page' });
      }
      continue; // next page — skip slot/template rendering entirely
    }

    const template = lp.template_id ? input.templatesById?.get(lp.template_id) : null;

    // SVG-href-swap path: if the template has an original SVG, render the
    // whole page as ONE rasterized PNG of the modified SVG. Pixel-perfect
    // Canva reproduction. Bypasses the slot loop AND the post-loop overlay
    // PNG draw — the rasterized SVG IS the complete page.
    if (
      template?.original_svg_url &&
      template.slots.every((s) => typeof s.dom_index === 'number')
    ) {
      try {
        // 1. Page background still drawn (template's SVG may have transparent
        //    areas where the brand cream should show through). Filled to the
        //    full bleed media box so transparent edges bleed cream, not white.
        page.drawRectangle({
          x: media.x, y: media.y, width: media.w, height: media.h,
          color: rgb(LETTERBOX_FILL.r, LETTERBOX_FILL.g, LETTERBOX_FILL.b),
        });

        // 2. Fetch the original SVG.
        const svgRes = await fetch(template.original_svg_url);
        if (!svgRes.ok) throw new Error(`fetch SVG ${svgRes.status}`);
        const svgString = await svgRes.text();

        // 3. Build photo-url + fit maps using DATA URLs (so the canvas-image
        //    load isn't tainted by cross-origin photos).
        const photoUrlsByDomIndex: Record<number, string> = {};
        const photoFitsByDomIndex: Record<number, { scale: number; offsetX: number; offsetY: number }> = {};
        for (let si = 0; si < template.slots.length; si++) {
          const slotDef = template.slots[si];
          if (typeof slotDef.dom_index !== 'number') continue;
          const pid = lp.photos[si];
          const photo = pid ? photoMap.get(pid) : null;
          if (!pid) continue;
          // ⚠️ A placed photo with no original never rasterises as an empty
          // slot (#1215): the throw drops to the slot loop below, which stops.
          if (!photo?.signed_url) throw unprintablePhoto({ albumPage, photoId: pid, reason: 'no original file to print' });
          const photoBlob = await fetchAsBlob(photo.signed_url);
          const photoDataUrl = await blobToDataUrl(photoBlob);
          photoUrlsByDomIndex[slotDef.dom_index] = photoDataUrl;
          const fit = input.templatePhotoFits?.[lp.page]?.[si];
          if (fit) photoFitsByDomIndex[slotDef.dom_index] = fit;
        }

        const slotsArg = template.slots
          .filter((s) => typeof s.dom_index === 'number')
          .map((s) => ({ dom_index: s.dom_index! }));
        const modifiedSvg = applyPhotosToTemplateSvg(svgString, photoUrlsByDomIndex, photoFitsByDomIndex, slotsArg);

        // 4. Rasterize at 300dpi print resolution (A4 width 595pt → ~2480px).
        //
        // Adaptive quality: try q92 first (visually lossless for print). If
        // the rendered page is exceptionally large (dense Canva template
        // with lots of decoration), step the quality down ONLY for that
        // page until it fits the per-page budget. Most pages stay at full
        // q92 — only pathological cases get extra compression. This keeps
        // print quality high for the typical album while preventing one
        // bloat page from busting the bucket cap.
        const targetWidth = Math.round(PAGE_W * 300 / 72);
        const jpegBytes = await rasterizeWithBudget(
          modifiedSvg,
          targetWidth,
          PER_PAGE_BUDGET_BYTES,
          ADAPTIVE_QUALITIES,
        );

        // 5. Embed + draw the full page across the whole media box. The
        //    rasterised SVG fills the A4 trim 1:1; stretching it to the bleed
        //    box (~1% larger) lets the full-page design bleed to the trimmed
        //    edge instead of leaving a cream margin. With bleed 0 this is the
        //    bare A4 rect.
        const embedded = await doc.embedJpg(jpegBytes);
        page.drawImage(embedded, { x: media.x, y: media.y, width: media.w, height: media.h });

        // FREE ELEMENTS ON TEMPLATE PAGES (2026-07-06) — draw the page's free
        // elements (text, detached/free photos, icons) ON TOP of the rasterised
        // template. Without this, the SVG-href-swap path skipped them entirely,
        // so a photo "detached" from a template slot never printed.
        await drawPageElements(doc, page, lp.elements ?? [], PAGE_W, A4_H, fontCache, helvetica,
          (photoId) => {
            const ph = photoMap.get(photoId);
            return ph?.signed_url ?? (ph as { fallback_url?: string | null } | undefined)?.fallback_url ?? null;
          });

        continue; // slot loop + overlay-PNG already handled by the SVG raster
      } catch (e) {
        console.warn(`[pdfgen] SVG-href-swap failed for page ${pi}, falling back to legacy:`, (e as Error)?.message);
        // Fall through to the legacy slot-loop path.
      }
    }

    // Templated pages use the template's slot rectangles (0-1 floats, keys
    // width/height) converted to absolute PDF points ({x, y, w, h}).
    // Non-templated pages keep the existing grid-derived layout.
    const slots: { x: number; y: number; w: number; h: number; rotation?: number; clip_path?: string | null }[] = template
      ? template.slots.map((s: LayoutTemplateSlot) => ({
          x: s.x * PAGE_W,
          y: (1 - s.y - s.height) * A4_H, // flip Y: CSS top→PDF bottom-left origin
          w: s.width * PAGE_W,
          h: s.height * A4_H,
          rotation: s.rotation ?? 0,
          // Per-slot SVG clip path in slot-LOCAL unit coords (0..1 of bbox).
          // Null/absent = rectangular slot; the renderer falls back to
          // clipRect for non-templated pages and for templated slots
          // without a custom clip mask.
          clip_path: s.clip_path ?? null,
        }))
      // Non-templated grid pages: push any cell that sits on a trim edge out
      // into the bleed so the customer's full-bleed photos cover-fit to the
      // trimmed edge (no white sliver). Interior cells are untouched. Templated
      // (Canva) slots are left at their exact positions so photos stay aligned
      // with their decorative frames. No-op when bleed is 0.
      : extendSlotsToBleed(getSlots(lp), bleedPt, PAGE_W, A4_H);
    // The editor's own boxes (no bleed): every photo is PLACED on these, the
    // bleed slot only fills the cut (pdfBleed.bleedPhotoPlacement, #1236).
    const trimSlots: { x: number; y: number; w: number; h: number }[] = template ? slots : getSlots(lp);

    // Page background — brand cream so the gutters between slots + any
    // missing-photo holes all read as one continuous canvas. The PDF
    // default (white) made the cream letterbox look like a different
    // colour patch on the page. Painted across the full bleed media box so
    // the margin trims to cream, never a white edge.
    // B5: with a gap on, the page fill IS the gap colour — it is what shows
    // between the photos. Each slot is then painted letterbox-cream below its
    // photo, so the gap colour appears in the gaps and nowhere else.
    // STEP 2 — with paper, the gaps and margins show the paper instead.
    const hasPaper = await drawLayoutBackground(doc, page, lp, media);
    if (!hasPaper) page.drawRectangle({
      x: media.x, y: media.y, width: media.w, height: media.h,
      // ⚠️ PASSED THROUGH, NOT REBUILT. `hexToRgb` already returns pdf-lib's
      // own Color, whose fields are `red`/`green`/`blue` — reading `.r`/`.g`/
      // `.b` off it yielded undefined, and `rgb(undefined, …)` throws inside
      // pdf-lib's own range assertion. A type error had been flagging exactly
      // this line for as long as the web build has been failing.
      color: currentGapColor
        ?? rgb(LETTERBOX_FILL.r, LETTERBOX_FILL.g, LETTERBOX_FILL.b),
    });

    // Fix #4a (2026-05-29): pre-fetch + normalise every photo on this
    // page in parallel. The slow steps are the signed-URL fetch and the
    // HEIC→JPEG conversion (when applicable). Running them concurrently
    // turns a 4-photo spread from 4× sequential into 1× max — typical
    // 4-up pages drop from ~600ms to ~180ms of IO. pdf-lib's `embedJpg`
    // / `drawImage` still run serially in the loop below because
    // pdf-lib mutates shared document state and isn't reentrant.
    const fetchedSlots: Array<{ bytes: Uint8Array; mime: 'image/jpeg' | 'image/png' } | null> = await Promise.all(
      slots.map(async (slot, si) => {
        const pid = lp.photos[si];
        const photo = pid ? photoMap.get(pid) : null;
        if (!photo?.signed_url) return null;
        // Downscale to what THIS slot needs at 300 dpi instead of the full
        // 3000px cap. The user's saved zoom (template fit.scale or the
        // interior slot's scale) feeds in so a zoomed-in crop keeps enough
        // source pixels. Resolution-only — no geometry change here.
        const userScale = template
          ? (input.templatePhotoFits?.[lp.page]?.[si]?.scale ?? 1)
          : (lp.slot_positions?.[`s${si}`]?.scale ?? 1);
        const targetLongEdge = computeSlotTargetLongEdge(slot.w, slot.h, userScale);
        // ⚠️ THE ORIGINAL ONLY (owner 2026-09-26: « we need to keep the high
        // quality of the image »). The preview fallback used to step in
        // silently when an original failed; a book never prints from a
        // low-resolution copy unless a person chose it (« Forcer le PDF »
        // substitutes the file itself, logged). A failure stops the render.
        return await fetchSlotNorm(
          [photo.signed_url],
          targetLongEdge, imageQuality, `prefetch pi=${pi} si=${si}`,
        );
      }),
    );

    // What this page actually drew — counted against what the editor shows.
    const drawnPids = new Set<string>();
    for (let si = 0; si < slots.length; si++) {
      const slot = slots[si];
      // Base rules: a slot's empty space is the PAGE (the gap colour), like
      // the 12/15 mm margins around it — a cream box inside a white page read
      // as a broken print on the #TEST-VALENCIA check (2026-10-12).
      if (currentGapColor && !currentBaseRules) {
        page.drawRectangle({
          x: slot.x, y: slot.y, width: slot.w, height: slot.h,
          color: rgb(LETTERBOX_FILL.r, LETTERBOX_FILL.g, LETTERBOX_FILL.b),
        });
      }
      const pid = lp.photos[si];
      const photo = pid ? photoMap.get(pid) : null;
      const prefetched = fetchedSlots[si];

      if (!photo?.signed_url) {
        // ⚠️ A PLACED PHOTO WITH NO ORIGINAL TO PRINT STOPS THE RENDER (#1215,
        // 2026-09-26). Only a slot the editor shows EMPTY stays empty below.
        if (pid) {
          throw unprintablePhoto({ albumPage, photoId: pid, reason: photo ? 'no original file to print' : 'the photo record is missing' });
        }
        // For templated pages we silently skip empty slots — the
        // decorative overlay (drawn after this loop) provides whatever
        // visual cue is appropriate (polaroid frame, etc.), and the cream
        // page background already fills the area. Painting a fill
        // rectangle here would punch through the overlay's transparent
        // regions and break the composition.
        if (template) continue;
        // Non-templated grid — keep the legacy cream fill so the slot
        // disappears into the page background instead of showing as a
        // distinct grey patch.
        page.drawRectangle({
          x: slot.x, y: slot.y, width: slot.w, height: slot.h,
          color: rgb(LETTERBOX_FILL.r, LETTERBOX_FILL.g, LETTERBOX_FILL.b),
        });
        continue;
      }

      // Templated pages with a per-slot SVG clip + photoFit render via a
      // separate path that mirrors the editor's TemplatedPhotoSlot. This
      // sits before the legacy crop/translate branch because the two
      // models are not compatible: photoFit is "cover-fit + uniform
      // zoom/pan inside the clip", whereas the legacy path is
      // "translate-based face centering + slot_positions overrides". We
      // pick by presence of a template — every templated slot uses
      // photoFit, even when clip_path is null (then the clip degrades to
      // the bbox rectangle).
      if (template) {
        try {
          // Reuse the per-page parallel pre-fetch (see comment above). Falls
          // back to a fresh fetch if the pre-fetch failed for this slot —
          // the per-slot try/catch below still catches and fills the cream
          // background, so a failure here is non-fatal.
          const fit = input.templatePhotoFits?.[lp.page]?.[si] ?? { scale: 1, offsetX: 0, offsetY: 0 };
          const norm = prefetched ?? await fetchSlotNorm(
            [photo.signed_url],
            computeSlotTargetLongEdge(slot.w, slot.h, fit.scale), imageQuality,
            `templated pi=${pi} si=${si}`,
          );
          if (!norm) throw new Error('all image candidates failed');
          const { bytes, mime } = norm;
          const img = await embedAuto(doc, bytes, mime);

          const bboxX = slot.x;
          const bboxY = slot.y;
          const bboxW = slot.w;
          const bboxH = slot.h;

          page.pushOperators(pushGraphicsState());

          // Rotate around bbox centre. CSS rotation is clockwise; pdf-lib
          // rotateRadians is counter-clockwise; negate to match.
          const rotDegCss = slot.rotation ?? 0;
          if (rotDegCss !== 0) {
            const cx = bboxX + bboxW / 2;
            const cy = bboxY + bboxH / 2;
            const radPdf = -rotDegCss * Math.PI / 180;
            page.pushOperators(
              translate(cx, cy),
              rotateRadians(radPdf),
              translate(-cx, -cy),
            );
          }

          // Push the clip mask. With a `clip_path` we use the SVG-derived
          // mask; otherwise we fall back to the slot rectangle so photoFit
          // zoom/pan still gets clipped at the slot edges.
          if (slot.clip_path) {
            pushClipPath(page, slot.clip_path, bboxX, bboxY, bboxW, bboxH);
          } else {
            pushOp(page, 're', [
              PDFNumber.of(bboxX), PDFNumber.of(bboxY),
              PDFNumber.of(bboxW), PDFNumber.of(bboxH),
            ]);
            pushOp(page, 'W');
            pushOp(page, 'n');
          }

          // object-fit: cover inside the bbox, then multiply by fit.scale
          // and shift by fit.offset (% of bbox). CSS offsetY is positive-
          // down; in PDF Y-up coords we subtract. The editor's
          // TemplatedPhotoSlot uses the same formula in CSS, so editor /
          // preview / PDF stay in lockstep.
          const imgAR = img.width / img.height;
          const bboxAR = bboxW / bboxH;
          let dw: number, dh: number;
          if (imgAR > bboxAR) { dh = bboxH; dw = bboxH * imgAR; }
          else { dw = bboxW; dh = bboxW / imgAR; }
          dw *= fit.scale;
          dh *= fit.scale;
          const dx = bboxX + (bboxW - dw) / 2 + (fit.offsetX / 100) * bboxW;
          const dy = bboxY + (bboxH - dh) / 2 - (fit.offsetY / 100) * bboxH;
          page.drawImage(img, { x: dx, y: dy, width: dw, height: dh });

          page.pushOperators(popGraphicsState());
          drawnPids.add(String(pid));
        } catch (e) {
          console.warn(`[pdfgen] templated interior pi=${pi} si=${si} failed:`, (e as Error)?.message);
          // ⚠️ NEVER A SILENT HOLE (#1215): stop, name the page and the photo.
          throw unprintablePhoto({ albumPage, photoId: pid, reason: (e as Error)?.message ?? 'draw failed' });
        }
        continue;
      }

      try {
        // Reuse the per-page parallel pre-fetch — see comment above the
        // Promise.all block. Same fallback semantics as the templated path.
        const slotScale = lp.slot_positions?.[`s${si}`]?.scale ?? 1;
        const norm = prefetched ?? await fetchSlotNorm(
          [photo.signed_url],
          computeSlotTargetLongEdge(slot.w, slot.h, slotScale), imageQuality,
          `interior pi=${pi} si=${si}`,
        );
        if (!norm) throw new Error('all image candidates failed');
        const { bytes, mime } = norm;
        const img = await embedAuto(doc, bytes, mime);
        // SOURCE OF TRUTH for aspect: the embedded image's natural pixel
        // dimensions, not photo.width/height in the DB. DB values can go
        // stale (post-rotation update lag, HEIC→JPEG dim drift) and
        // mismatched values cause pdf-lib's drawImage to stretch the
        // image to a non-natural aspect — same root cause as the editor's
        // recent "squashed/elongated photos" report.
        const iw = img.width;
        const ih = img.height;
        // ⚠️ …BUT STALE DIMS AND A DIFFERENT FILE ARE NOT THE SAME THING.
        // The paragraph above is right that the file's own pixels decide the
        // aspect. What it cannot see is that the file may not be the photo the
        // customer approved at all. #1198 (2026-09-20): the album agent's
        // rotate wrote a new print file and left the editor's display tier on
        // the old one, so the editor letterboxed a landscape and the printer
        // cover-filled a portrait — the same page, 280 pt apart, and nothing
        // logged. A resize can never turn a landscape into a portrait, so an
        // orientation flip is proof of two files and the render stops here. A
        // failed render is a queue entry; a wrong-shaped page is a printed book.
        assertSameOrientation(String(photo.id ?? pid ?? '?'), photo.width, photo.height, iw, ih);

        // Cover-fit forced in the PDF, mirroring the editor. The earlier
        // auto-decide could flip to contain-fit (cream letterbox) and
        // produce "ugly empty cells" the customer reported. Face
        // protection now relies on the translate-based face-centering
        // computed at AI placement time, plus the user's manual pan.
        const fitMode: 'cover' = 'cover';

        // Unit B: face protection is a HARD constraint. Even when
        // decideFitMode picked 'cover', if the chosen crop centre would
        // push any detected face outside the inner 85% of the slot we
        // demote to contain-fit so the face stays whole. Customer-
        // panned positions are honoured first — they explicitly told us
        // where they wanted the crop — but still validated. A rejected
        // cover-fit emits a telemetry event so we can learn which
        // photo/slot combinations need a smarter default crop.
        const saved = lp.slot_positions?.[`s${si}`];
        // Normalise saved into the translate model. v2 records are already
        // translate offsets in %; legacy records (object-position 0..100,
        // default 50,50) become translate offsets via the same formula
        // the editor uses on load — at scale ≤ 1 the legacy crop had no
        // visible effect so the translate falls to 0. computeFaceCrop's
        // output stays in legacy coords for back-compat with isCropSafe.
        const faceCrop = computeFaceCrop(photo.face_data, iw, ih);
        const isV2 = saved?._v === 2;
        const userScale = saved?.scale ?? 1;
        const overflow = Math.max(0, userScale - 1);
        const translateX = isV2
          ? (saved!.x ?? 0)
          : saved
            ? (50 - saved.x) * overflow
            : (50 - faceCrop.x) * overflow;
        const translateY = isV2
          ? (saved!.y ?? 0)
          : saved
            ? (50 - saved.y) * overflow
            : (50 - faceCrop.y) * overflow;
        // candX/candY in legacy 0..100 coords are still used below for the
        // face-safety check + the contain-fit telemetry payload.
        const candX = saved && !isV2 ? saved.x : faceCrop.x;
        const candY = saved && !isV2 ? saved.y : faceCrop.y;
        // When the customer has explicitly panned/zoomed (saved exists),
        // ALWAYS use cover-fit + their translate/scale combo. The earlier
        // path ran decideFitMode + isCropSafe and could flip a customised
        // page back to contain — so a photo the customer had panned to a
        // specific framing ended up letterboxed instead, ignoring their
        // scale entirely. Auto-fit only applies to slots the customer
        // hasn't touched.
        // fitMode is now always 'cover' — see force-cover comment above.
        // We keep the isCropSafe check for telemetry only; failing it
        // no longer demotes to contain (the editor doesn't either, so
        // cover is the source of truth in both).
        const safeForCover = !saved
          ? isCropSafe(photo.face_data, iw, ih, slot.w, slot.h, candX, candY)
          : true;
        const effectiveFit: 'cover' = 'cover';
        void fitMode; // referenced for telemetry compatibility

        if (!saved && !safeForCover && input.sessionId) {
          // Fire-and-forget — feedback API is best-effort and never
          // blocks the PDF render. Includes enough context to debug a
          // bad crop from the dashboard without exposing the image.
          aiApi.feedback(input.sessionId, 'crop_rejected_face_outside_safezone', {
            photo_id: pid,
            page: lp.page,
            slot_index: si,
            layout: lp.layout,
            crop_x: candX,
            crop_y: candY,
            face_count: photo.face_count ?? null,
            face_spread: photo.face_spread ?? null,
            user_panned: !!saved,
          });
        }

        // effectiveFit is always 'cover' now (see above) — the contain
        // branch is gone with the cream-letterbox UX. Faces are kept in
        // frame by translate, not by sacrificing the fill.
        void effectiveFit;
        // Canva-style MANUAL slot rotation (clockwise degrees) read from the
        // customer's saved slot_positions. Distinct from `slot.rotation`
        // (the template's polaroid tilt below). It rotates the photo about
        // the IMAGE's own centre — the same pivot the editor/preview use
        // (transform-origin: center on the <img>, whose centre is the slot
        // centre offset by the pan). Pass it into computeTranslateDrawParams
        // so the cover box grows (rotationCoverGrowth) exactly as pdfMirrorBox.
        const photoRotCss = saved?.rotation ?? 0;
        // REAL CROP (2026-07-24): the kept window IS the photo. Feed its dims
        // into the cover-fit/pan math (identical to the editor's croppedDims),
        // then expand the draw rect back to the FULL image and offset it so
        // only the window sits where dp says — the slot clip removes the rest.
        // PDF space is Y-UP while crop.y is measured from the image TOP, hence
        // the (1 - y - h) term. No crop → dims/rect unchanged (byte-identical).
        const savedCrop = saved?.crop && saved.crop.w > 0 && saved.crop.h > 0 ? saved.crop : null;
        const srcW = savedCrop ? img.width * savedCrop.w : img.width;
        const srcH = savedCrop ? img.height * savedCrop.h : img.height;
        const trimSlot = trimSlots[si] ?? slot;
        const dpWin = computeTranslateDrawParams(
          trimSlot.x, trimSlot.y, trimSlot.w, trimSlot.h,
          srcW, srcH,
          translateX, translateY, userScale,
          photoRotCss,
          // Solo 1-up landscape → fit width (full image, letterbox), not cover.
          soloLandscapeFitWidth(lp.layout, srcW, srcH, lp.fill),
        );
        const dpFull = savedCrop
          ? {
              ...dpWin,
              width:  dpWin.width  / savedCrop.w,
              height: dpWin.height / savedCrop.h,
              x: dpWin.x - savedCrop.x * (dpWin.width / savedCrop.w),
              y: dpWin.y - (1 - savedCrop.y - savedCrop.h) * (dpWin.height / savedCrop.h),
            }
          : dpWin;
        // Rotated photos (manual or template tilt) keep the old path: their
        // cover box is already grown for the turn and never letterboxed.
        const placed = (photoRotCss !== 0 || ((slot as { rotation?: number }).rotation ?? 0) !== 0)
          ? { draw: dpFull, clip: slot }
          : bleedPhotoPlacement(dpWin, dpFull, trimSlot, slot);
        const dp = { ...dpFull, ...placed.draw };
        clipRect(page, placed.clip.x, placed.clip.y, placed.clip.w, placed.clip.h);
        // Phase 1 — rotate the drawn image around the slot's CENTRE so
        // tilted polaroid templates render the customer photo at the same
        // tilt as the placeholder. pdf-lib rotates around (x, y) — the
        // bottom-left of the draw rectangle — so we shift (x, y) by the
        // delta that puts the rotation pivot on the slot centre. CSS
        // rotation is clockwise, pdf-lib is counter-clockwise → negate.
        const slotTiltCss = (slot as { rotation?: number }).rotation ?? 0;
        if (slotTiltCss !== 0) {
          const pdfRot = -slotTiltCss;
          const theta = (pdfRot * Math.PI) / 180;
          const cosT = Math.cos(theta);
          const sinT = Math.sin(theta);
          const cx = slot.x + slot.w / 2;
          const cy = slot.y + slot.h / 2;
          // Vector from slot centre to image bottom-left in unrotated frame.
          const vx = dp.x - cx;
          const vy = dp.y - cy;
          // Rotate that vector by theta around the origin.
          const rx = vx * cosT - vy * sinT;
          const ry = vx * sinT + vy * cosT;
          page.drawImage(img, {
            x: cx + rx,
            y: cy + ry,
            width: dp.width,
            height: dp.height,
            rotate: degrees(pdfRot),
          });
        } else if (photoRotCss !== 0) {
          // Manual photo rotation about the IMAGE draw-rect centre. The img
          // rect centre = (dp.x + dp.width/2, dp.y + dp.height/2), which is
          // the slot centre shifted by the customer's pan — exactly the CSS
          // transform-origin:center pivot in the editor/preview. pdf-lib
          // rotates about the draw rectangle's bottom-left (dp.x, dp.y), so
          // we rotate the bottom-left point about that centre and use the
          // result as the new origin. CSS clockwise → pdf-lib CCW: negate.
          const pdfRot = -photoRotCss;
          const theta = (pdfRot * Math.PI) / 180;
          const cosT = Math.cos(theta);
          const sinT = Math.sin(theta);
          // CROP: pivot on the CROP WINDOW's centre (dpWin) — the editor
          // rotates the clipping wrapper, whose centre is the window's, not
          // the oversized full-image rect's. Identical when there's no crop.
          const cx = dpWin.x + dpWin.width / 2;
          const cy = dpWin.y + dpWin.height / 2;
          const vx = dp.x - cx;
          const vy = dp.y - cy;
          const rx = vx * cosT - vy * sinT;
          const ry = vx * sinT + vy * cosT;
          page.drawImage(img, {
            x: cx + rx,
            y: cy + ry,
            width: dp.width,
            height: dp.height,
            rotate: degrees(pdfRot),
          });
        } else {
          page.drawImage(img, dp);
        }
        popClip(page);
        drawnPids.add(String(pid));
      } catch (e) {
        console.warn(`[pdfgen] interior pi=${pi} si=${si} failed:`, (e as Error)?.message);
        // ⚠️ NEVER A SILENT HOLE (#1215, 2026-09-26). This used to paint the
        // slot cream so a failure « doesn't show as a grey hole » — exactly how
        // pages 2, 5 and 34 of a 56-page book came out empty while the editor
        // showed them full, with nothing anywhere knowing. Stop, and say which
        // page and which photo; the server turns it into an alert.
        throw unprintablePhoto({ albumPage, photoId: pid, reason: (e as Error)?.message ?? 'draw failed' });
      }
    }

    // ⚠️ PHOTOS PER PAGE (#1215): every photo the editor shows in a slot was
    // drawn — p.4 with two and p.5 with none can never both print.
    assertPageComplete(albumPage, expectedPhotosOnPage(lp.photos, slots.length), drawnPids);

    // Phase 1.5 — template decorative overlay drawn LAST so it sits ABOVE
    // the customer photos in the PDF z-stack. The print-resolution PNG of
    // the SVG overlay is used here because pdf-lib can't rasterise SVG.
    // Contains only decorative parts (polaroid borders, frames, captions);
    // photo placeholders were stripped during processing. Non-fatal: a
    // fetch failure leaves the photos visible without the decoration.
    if (template?.overlay_print_png_url) {
      try {
        const blob = await fetchAsBlob(template.overlay_print_png_url);
        const bytes = new Uint8Array(await blob.arrayBuffer());
        const png = await doc.embedPng(bytes);
        page.drawImage(png, {
          x: 0,
          y: 0,
          width: PAGE_W,
          height: A4_H,
        });
      } catch (e) {
        console.warn('[pdfGenerator] template overlay fetch failed:', (e as Error).message);
      }
    }

    // Free-floating overlay elements (text, shapes, icons, photos) drawn
    // ON TOP of the slot photos so they're visible regardless of layout.
    // Painted in array order, so later elements stack above earlier ones —
    // matching the editor's z-order convention.
    await drawPageElements(doc, page, lp.elements ?? [], PAGE_W, A4_H, fontCache, helvetica,
      (photoId) => {
        const ph = photoMap.get(photoId);
        return ph?.signed_url ?? (ph as { fallback_url?: string | null } | undefined)?.fallback_url ?? null;
      });


    onProgress?.({
      stage: 'interior',
      current: pi + 1,
      total: contentPages.length,
      message: `Page ${pi + 1} / ${contentPages.length}`,
    });
  }

  // ── Outro page ─────────────────────────────────────────────────────────
  const outroPage = await embedBookendPage(doc, input.outroPageUrl ?? null, bleedPt, imageQuality);

  // ── Print padding to a multiple of 4 (A3 double-sided binding) ───────────
  // The bindery images each album's interior on A3 sheets, double-sided — one
  // A3 sheet = 4 album (A4) pages. So this album's TOTAL interior page count
  // (intro + content + outro, whichever were actually embedded) must be
  // divisible by 4. We append the minimum number of pages (1–3) to reach the
  // next multiple — DUPLICATING THE OUTRO so the album ends on outro pages, not
  // cream blanks (the #1074 "2 blank pages after the outro" report). Falls back
  // to a blank only when the order has no outro to duplicate. doc.getPageCount()
  // is the source of truth (accounts for skipped bookends). PRINT-ONLY: these
  // filler pages never affect pricing or order.page_count.
  const currentCount = doc.getPageCount();
  // PHOTO VIVANTE: one page is kept for the still, which closes the book.
  const livingLast = !!input.livingStillUrl && !(input.pageIndices && input.pageIndices.length > 0);
  const targetCount = livingLast ? roundUpToMultipleOf4(currentCount + 1) - 1 : roundUpToMultipleOf4(currentCount);
  // ── COUPON LEAF (plan 2026-10-28) ────────────────────────────────────────
  // When the print render hands this album the order's coupons, a leaf whose
  // BOTH pages are pad pages (couponLeaves.ts — always the book's last leaf)
  // carries them: front on the recto, back on the verso. Counted from the real
  // page count (an intro that failed to embed shifts everything). Nothing
  // below runs without `input.coupons` → the padding is exactly as before.
  const couponLeaf = input.coupons?.enabled === true && !livingLast && !(input.pageIndices && input.pageIndices.length > 0)
    ? (planCouponLeaves(currentCount).couponLeaves[0] ?? null)
    : null;
  let couponPages: number[] = [];
  for (let b = currentCount; b < targetCount; b++) {
    if (couponLeaf && b + 1 === couponLeaf.front) {
      try {
        // Drawn in a scratch document first and copied in only when BOTH sides
        // are complete: a failure leaves this book untouched.
        const { buildCouponSheetPair } = await import('./couponPdf');
        const pair = await buildCouponSheetPair(input.coupons as CouponPrintData, bleedMm,
          async (url) => new Uint8Array(await (await fetchAsBlob(url)).arrayBuffer()));
        const [front, back] = await doc.copyPages(pair, [0, 1]);
        doc.addPage(front);
        doc.addPage(back);
        couponPages = [couponLeaf.front, couponLeaf.back];
        b = couponLeaf.back - 1;           // both pad pages are done
        continue;
      } catch (e) {
        // A coupon must never cost the album its print: pad with outro copies
        // as before; the order's coupons then go to the separate coupon PDF
        // (couponPrint.finishOrderCoupons).
        console.warn('[pdfgen] coupon leaf failed — outro copies instead:', (e as Error)?.message);
      }
    }
    // Prefer an outro duplicate — re-render the same outro URL → an identical
    // page. Each iteration adds exactly one page (dup OR fallback blank).
    if (outroPage && input.outroPageUrl) {
      const dup = await embedBookendPage(doc, input.outroPageUrl, bleedPt, imageQuality);
      if (dup) continue;
      // dup unexpectedly failed → fall through to a blank so we still reach ×4.
    }
    // Fallback blank — match interior pages exactly: A4 trim, same bleed media
    // box, brand-cream (LETTERBOX_FILL) fill across the full bleed box.
    const blank = doc.addPage([PAGE_W, A4_H]);
    if (bleedPt > 0) {
      blank.setMediaBox(media.x, media.y, media.w, media.h);
      blank.setBleedBox(media.x, media.y, media.w, media.h);
      blank.setTrimBox(0, 0, PAGE_W, A4_H);
    }
    blank.drawRectangle({
      x: media.x, y: media.y, width: media.w, height: media.h,
      color: rgb(LETTERBOX_FILL.r, LETTERBOX_FILL.g, LETTERBOX_FILL.b),
    });
  }

  // PHOTO VIVANTE — the still, full bleed, as the very last page: the reader
  // turns the last leaf and the QR on the back cover is right there. A still
  // that fails to embed must not cost the print: a cream page closes the book
  // instead (the count stays ÷4) and the failure is logged.
  if (livingLast) {
    const still = await embedBookendPage(doc, input.livingStillUrl ?? null, bleedPt, imageQuality);
    if (!still) {
      console.warn('[pdfgen] Photo Vivante still failed to embed — cream page instead');
      const blank = doc.addPage([PAGE_W, A4_H]);
      if (bleedPt > 0) {
        blank.setMediaBox(media.x, media.y, media.w, media.h);
        blank.setBleedBox(media.x, media.y, media.w, media.h);
        blank.setTrimBox(0, 0, PAGE_W, A4_H);
      }
      blank.drawRectangle({ x: media.x, y: media.y, width: media.w, height: media.h, color: rgb(LETTERBOX_FILL.r, LETTERBOX_FILL.g, LETTERBOX_FILL.b) });
    }
  }

  if (input.coupons) {
    // The PDF says which of its pages are coupons, so every raster of a print
    // interior (the flipbook, the server preview) can keep them out of sight.
    if (couponPages.length) doc.setKeywords([couponPagesKeyword(couponPages)]);
    try { rawInput.onCouponPlan?.({ pages: couponPages }); } catch { /* report only */ }
  }

  // ── INTERIOR ORDER STAMP ─────────────────────────────────────────────────
  // The bindery tag on the interior's FIRST page — the intro when there is
  // one, else the first content page (themed opening, imported page or a redo
  // subset's first page: always doc page 0). Same place as before (5 mm from
  // the spine edge, 8 mm up, inside the trim), same rule as the cover
  // (lib/orderStamp.ts): read the real pixels, pure black / white, boxed when
  // busy, bold 8 pt. Was 6 pt Helvetica, grey 55 % at 70 % opacity. Drawn
  // after every page is complete so it measures what really prints there.
  if (input.orderLabel && doc.getPageCount() > 0) {
    try {
      const helvBold = await doc.embedFont(StandardFonts.HelveticaBold);
      const { style } = await drawOrderStamp({
        doc, page: doc.getPage(0), pageIndex: 0,
        text: toWinAnsi(input.orderLabel),
        font: helvBold,
        place: (w, a, d) => placeStamp(5 * MM_TO_PT, 7 * MM_TO_PT, w, a, d, 'left'),
      });
      console.info(`[stamp] interior ${input.orderLabel}: ${style.text} text${style.box ? ` on ${style.box} box` : ''} — ${style.reason}`);
    } catch (e) { console.warn('[pdfgen] interior order stamp failed:', (e as Error)?.message); }
  }

  // ⚠️ THE PRINT GATE (2026-10-07): « A drawing error occurred » on A3 duplex —
  // every page must survive PDF → PostScript and be the book's size, or the
  // export stops here with the page numbers (printPdfCheck.ts).
  await assertPrintSafe(doc, { width: PAGE_W, height: A4_H }, 'intérieur');
  return doc.save();
}

/**
 * Append a single A4 page whose content is loaded from `url`. PDF URLs
 * embed the source's first page; image URLs (jpg/png) are drawn
 * cover-fit. Null/missing/failed loads are no-ops so the rest of the
 * interior PDF still ships.
 *
 * `bleedPt` grows the raster fallback to the same bleed media box as the
 * content pages and cover-fits the image into it so a full-bleed branding
 * image trims cleanly. Copied PDF pages keep their own source MediaBox — we
 * can't know a third-party PDF's trim, so they are embedded as-authored.
 */
async function embedBookendPage(doc: PDFDocument, url: string | null, bleedPt = 0, imageQuality = 0.92): Promise<PDFPage | null> {
  if (!url) return null;
  try {
    const blob = await fetchAsBlob(url);
    const head = new Uint8Array(await blob.slice(0, 8).arrayBuffer());
    const isPdf = head[0] === 0x25 && head[1] === 0x50 && head[2] === 0x44 && head[3] === 0x46;
    if (isPdf) {
      const bytes = new Uint8Array(await blob.arrayBuffer());
      const src = await PDFDocument.load(bytes, { ignoreEncryption: true });
      const [first] = await doc.copyPages(src, [0]);
      doc.addPage(first);
      return first;
    }
    // Raster fallback — fit the image as cover on a fresh page so intros at
    // any aspect ratio still print full-bleed. The page grows to the bleed
    // media box and the image cover-fits that larger box so it trims clean.
    const media = fullBleedRect(PAGE_W, A4_H, bleedPt);
    const page = doc.addPage([PAGE_W, A4_H]);
    if (bleedPt > 0) {
      page.setMediaBox(media.x, media.y, media.w, media.h);
      page.setBleedBox(media.x, media.y, media.w, media.h);
      page.setTrimBox(0, 0, PAGE_W, A4_H);
    }
    const { bytes, mime } = await normaliseImageBlob(blob, MAX_IMAGE_LONG_EDGE, imageQuality);
    const img = await embedAuto(doc, bytes, mime);
    const scale = Math.max(media.w / img.width, media.h / img.height);
    const dw = img.width * scale, dh = img.height * scale;
    page.drawImage(img, {
      x: media.x + (media.w - dw) / 2,
      y: media.y + (media.h - dh) / 2,
      width: dw,
      height: dh,
    });
    return page;
  } catch (e) {
    console.warn('[pdfgen] bookend page failed:', (e as Error)?.message);
    return null;
  }
}

// ─── Page overlay elements (text, shapes, icons, photos) ────────────────────
// The customer places these via the editor's "Éléments" panel. Each
// element's x/y/width/height are stored as a percent of the page. Text
// content rasterises via canvas (see rasterizeTextElement) so fontSize,
// weight, italic, custom families etc. all render exactly as the editor
// displayed them.

// Ensure custom @font-face families are actually loaded before the canvas
// measures glyph widths. If we measure with a fallback font, the wrap point
// (and thus the line breaks) drifts from what the editor showed — text can
// then wrap/clip differently in the PDF. `document.fonts.ready` resolves once
// all pending font loads settle; we also explicitly `load()` the family we're
// about to use to kick lazy @font-face fetches that nothing has triggered yet.
// Awaiting the cached promise is essentially free after the first call, so
// this is once-per-generation in practice even though it lives per-element.
let _fontsReadyPromise: Promise<unknown> | null = null;
const _loadedFontSpecs = new Set<string>();
async function ensureFontLoaded(spec: string, text = ''): Promise<void> {
  if (typeof document === 'undefined' || !(document as Document).fonts) return;
  const fonts = (document as Document).fonts;
  try {
    // ⚠️ WITH THE TEXT (#WA-269649). `fonts.load(spec)` loads only the faces
    // whose unicode-range covers a SPACE — the Latin subset. The Arabic subset
    // of Cairo/Amiri was never fetched, so the canvas drew Arabic in whatever
    // system font the rendering machine had. Passing the text loads the
    // subsets that text needs; keyed by script so it runs once per pair.
    const key = `${spec}|${hasArabic(text) ? 'ar' : 'lat'}`;
    if (!_loadedFontSpecs.has(key)) {
      _loadedFontSpecs.add(key);
      // `load` triggers the @font-face fetch for this exact size/family and
      // resolves when the face is usable (or rejects/no-ops if unknown).
      await fonts.load(spec, text || ' ').catch(() => {});
    }
    if (!_fontsReadyPromise) _fontsReadyPromise = fonts.ready;
    await _fontsReadyPromise;
  } catch {
    /* defensive — never block PDF generation on font readiness */
  }
}

function parseAlphaFromHex(hex: string | undefined): number | undefined {
  if (!hex) return undefined;
  const h = hex.replace('#', '');
  if (h.length !== 8) return undefined;
  const a = parseInt(h.slice(6, 8), 16);
  if (Number.isNaN(a)) return undefined;
  return a / 255;
}

/**
 * Rasterise a text element via <canvas> at high DPI so the PDF embeds
 * the EXACT typography the customer saw in the editor — same font face,
 * weight, italic, alignment, line breaks, colour, underline. pdf-lib's
 * vector drawText needs perfectly-matched embedded font files for every
 * weight/italic variant, which means shipping ~40 TTFs and getting the
 * baseline math right; rasterising sidesteps both with one canvas call.
 *
 * `pageWidthPt` is the PDF page width in points. We scale fontSize from
 * editor-pixels (CSS px, ~400-px-wide page reference) into the same
 * proportion of the PDF page so the text reads the same size on print.
 *
 * Density is 3× the trim width — enough that a 300-dpi printer never
 * sees pixels.
 */
// PRINT-RIP MATTE FIX (2026-07-17, the "faint shadow frame around every
// printed text" bug): a fresh canvas is transparent BLACK, so the rasterised
// text PNG stores RGB=(0,0,0) under every alpha=0 pixel. pdf-lib splits that
// into an RGB image + SMask — on screen the mask hides the black rectangle,
// but print RIPs flatten transparency imperfectly during CMYK conversion and
// bleed a few percent of the hidden BLACK BOX into the page: a ~10%-opacity
// shadow frame around every text (and tinted icon). Fix: after drawing, set
// the RGB of fully-transparent pixels to WHITE. HTML canvas stores PREMULTIPLIED
// alpha, so we CANNOT set transparent RGB via put/getImageData (it's forced back
// to black on write). Instead we encode the PNG bytes ourselves so the hidden
// pixels genuinely carry white, and a bad RIP bleeds white (invisible) not black.

// CRC-32 (PNG chunk checksums). Tiny table-driven implementation.
const _crcTable = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();
function _crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = _crcTable[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}
async function _zlibDeflate(data: Uint8Array): Promise<Uint8Array> {
  // CompressionStream('deflate') emits a zlib stream (header+adler) — exactly
  // what a PNG IDAT chunk needs. Piping via Blob keeps the typing clean.
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new CompressionStream('deflate'));
  const buf = await new Response(stream).arrayBuffer();
  return new Uint8Array(buf);
}
/** Encode an RGBA buffer (transparent pixels already matted to white) as an
 *  8-bit RGBA PNG — bypassing canvas so the matte colour survives. */
async function encodeRgbaPngWhiteMatte(rgba: Uint8ClampedArray, w: number, h: number): Promise<Uint8Array> {
  // 1) matte: every fully-transparent pixel → white RGB (keeps alpha=0).
  for (let i = 0; i < rgba.length; i += 4) {
    if (rgba[i + 3] === 0) { rgba[i] = 255; rgba[i + 1] = 255; rgba[i + 2] = 255; }
  }
  // 2) filtered scanlines (filter 0 = None): a 0 byte prefixes each row.
  const stride = w * 4;
  const raw = new Uint8Array((stride + 1) * h);
  for (let y = 0; y < h; y++) {
    raw[y * (stride + 1)] = 0;
    raw.set(rgba.subarray(y * stride, y * stride + stride), y * (stride + 1) + 1);
  }
  const idatData = await _zlibDeflate(raw);
  // 3) assemble chunks.
  const be32 = (n: number) => new Uint8Array([(n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255]);
  const chunk = (type: string, body: Uint8Array): Uint8Array => {
    const typeBytes = new Uint8Array([...type].map((c) => c.charCodeAt(0)));
    const tb = new Uint8Array(typeBytes.length + body.length);
    tb.set(typeBytes, 0); tb.set(body, typeBytes.length);
    const out = new Uint8Array(4 + tb.length + 4);
    out.set(be32(body.length), 0); out.set(tb, 4); out.set(be32(_crc32(tb)), 4 + tb.length);
    return out;
  };
  const ihdr = new Uint8Array(13);
  ihdr.set(be32(w), 0); ihdr.set(be32(h), 4);
  ihdr[8] = 8;   // bit depth
  ihdr[9] = 6;   // colour type 6 = RGBA
  // 10,11,12 = compression/filter/interlace = 0
  const sig = new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]);
  const parts = [sig, chunk('IHDR', ihdr), chunk('IDAT', idatData), chunk('IEND', new Uint8Array(0))];
  let total = 0; for (const p of parts) total += p.length;
  const png = new Uint8Array(total); let off = 0; for (const p of parts) { png.set(p, off); off += p.length; }
  return png;
}

/**
 * Where every line of a text element goes, measured with the browser's own
 * font on a canvas — ONE function for the raster and the vector print paths
 * (#1236, 2026-10-17), so they can never place a line differently. Canvas px;
 * divide by `DPR` for points.
 */
async function textCanvasLayout(
  el: PageElement,
  pageW: number,
  pageH: number,
) {
  if (typeof document === 'undefined') return null;
  const text = el.text ?? '';
  if (!text.trim()) return null;

  // Element box in PDF points.
  const ewPt = (el.width / 100) * pageW;
  const ehPt = (el.height / 100) * pageH;
  // 3× density = ~900 px wide for a half-page text element on A4. Enough
  // for sharp 300-dpi print; capped to keep memory + PDF size reasonable.
  const DPR = TEXT_RASTER_DPR;
  const canvasW = Math.max(2, Math.min(4096, Math.ceil(ewPt * DPR)));
  const canvas = document.createElement('canvas');
  canvas.width = canvasW;
  canvas.height = 2; // provisional — real height depends on the wrapped lines
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // editor renders the page at ~400 CSS px. fontSize is in those pixels.
  // The text/element-width ratio must match in the PDF.
  // Editor: fontSize_px / page_width_px = el.fontSize / 400
  // PDF canvas: fontSize_canvas / canvasW = el.fontSize / (el.width/100 × 400)
  //                                       = el.fontSize × 100 / (el.width × 400)
  // Or equivalently: fontSize_canvas = el.fontSize × (pageW × DPR / 400)
  // (textRasterPx — the editor pins the SAME value as its optical size.)
  const fontCanvasPx = textRasterPx(el.fontSize, pageW);

  const weight = el.fontWeight ?? '600';
  const style = el.fontStyle ?? 'normal';
  // fontFamily comes from the editor's CSS stack — let the canvas use the
  // same loaded @font-face. If the family isn't loaded yet (rare race),
  // the browser falls back to the next family in the stack, still better
  // than the previous Helvetica drop-out.
  // Arabic letters get a NAMED Arabic face (textBidi.fontStackFor) — the same
  // stack the editor's DOM uses — instead of whatever the machine falls back to.
  const family = fontStackFor(el.fontFamily ?? "'Fraunces', 'Cormorant Garamond', Georgia, serif", text);
  const fontSpec = `${style} ${weight} ${fontCanvasPx}px ${family}`;
  const applyCtxStyle = () => {
    ctx.font = fontSpec;
    ctx.fillStyle = el.fontColor ?? '#1A1A1A';
    ctx.textAlign = el.textAlign === 'left'
      ? 'left'
      : el.textAlign === 'right' ? 'right' : 'center';
    // CSS places a line by the font's own ascent/descent (half-leading),
    // not by the em « middle »: alphabetic baseline at cy + (A − D)/2 below.
    ctx.textBaseline = 'alphabetic';
    ctx.globalAlpha = el.opacity ?? 1;
  };
  // Load the real font BEFORE measuring so glyph widths (and therefore the
  // wrap/break points) match the editor instead of a fallback metric. Cached,
  // so this is effectively once-per-generation.
  await ensureFontLoaded(fontSpec, text);
  applyCtxStyle();

  const lineHeight = fontCanvasPx * textLineHeight(el);
  // Word-wrap to the element box width — the editor wraps text via CSS
  // (white-space: pre-wrap + maxWidth:100%), so the PDF MUST wrap to the same
  // box or a long caption renders as one over-wide line that gets clipped on
  // both sides (the "...t know it yet, but this game was over befor..." bug).
  // canvasW and fontCanvasPx scale in the SAME proportion as the editor's box
  // and font size, so wrapping at canvasW reproduces the editor's line breaks.
  const wrapWidth = rasterWrapWidth(ewPt, DPR);
  // Each line carries its paragraph's direction (the editor's
  // `unicode-bidi: plaintext`): an Arabic line ends at its LEFT edge, full
  // stop included (#WA-269649).
  const lines = layoutTextLines(text, wrapWidth, (s) => ctx.measureText(s).width);
  const totalH = lines.length * lineHeight;

  // OVERFLOW PARITY (2026-06-12, the "Belgique/Amti Nezha" intro bug): the
  // editor (TextOverlayChrome) lets text overflow the stored el.height box —
  // flex alignItems:center + overflow:visible — so a long dedication shows in
  // full, centred on the box. The raster used to clip its canvas at the
  // stored height, printing only the middle slice of the block. Grow the
  // canvas to the wrapped text height instead and report the effective
  // height so drawPageElements can keep the block centred on the stored box.
  const { heightPt: effHPtWanted } = textBlockBox(0, ehPt, totalH / DPR);
  // INK PAST THE LINE BOX (theme check B, 2026-10-14): a script face's
  // descenders (Pacifico's « y », « j » in Anniversaire's title) reach well
  // below its 1.3 line box, and the DOM shows them (overflow visible). A canvas
  // cut at the box printed them flat. Half an em of room above and below; the
  // block stays centred on the canvas, and the canvas — now taller than the
  // box — is centred on the stored box (textBlockBox), so nothing moves.
  const inkPad = Math.ceil(fontCanvasPx * 0.5);
  const canvasH = Math.max(2, Math.min(4096, Math.ceil(effHPtWanted * DPR) + 2 * inkPad));
  canvas.height = canvasH;     // resizing RESETS canvas state…
  applyCtxStyle();             // …so re-apply font/colour/align/alpha
  const heightPt = canvasH / DPR; // actual drawn height (cap-safe)
  // Vertical-centre the block within the canvas (matches editor items-center)
  const firstLineCY = canvasH / 2 - totalH / 2 + lineHeight / 2;
  // No inset: the editor's text box has no padding (rasterWrapWidth, 10-06).
  const xAnchor = ctx.textAlign === 'center' ? canvasW / 2
    : ctx.textAlign === 'right' ? canvasW : 0;

  // An OUTLINE in the opposite colour keeps a caption readable on a busy photo
  // without tinting the photo (captionLayout.ts). Stroked first, filled over:
  // the editor's `-webkit-text-stroke` + `paint-order: stroke fill`.
  const fm = ctx.measureText('Hg');
  const baselineShift = Number.isFinite(fm.fontBoundingBoxAscent) && Number.isFinite(fm.fontBoundingBoxDescent)
    ? (fm.fontBoundingBoxAscent - fm.fontBoundingBoxDescent) / 2
    : (fm.actualBoundingBoxAscent - fm.actualBoundingBoxDescent) / 2;
  const outlinePx = el.outlineColor && (el.outlineWidth ?? 0) > 0 ? (el.outlineWidth as number) * fontCanvasPx : 0;
  return {
    canvas, ctx, applyCtxStyle, lines, fontCanvasPx, lineHeight, canvasW, canvasH, heightPt,
    firstLineCY, xAnchor, baselineShift, outlinePx, DPR, family, weight, style,
  };
}

/**
 * A text element as VECTOR OUTLINES (#1236) — same lines, same places, same
 * size as the raster (textCanvasLayout), the letters cut from the browser's own
 * font file (lib/vectorText.ts). Opaque ink only. False = not drawable as
 * vector (Arabic, a face no stylesheet links): the caller rasterises it.
 */
async function drawTextVector(
  page: PDFPage, el: PageElement, pageW: number, pageH: number, ex: number, eyTop: number, eh: number,
): Promise<boolean> {
  const text = el.text ?? '';
  if (!text.trim() || hasArabic(text) || typeof document === 'undefined') return false;
  try {
    const L = await textCanvasLayout(el, pageW, pageH);
    if (!L) return false;
    const family = primaryFontName(L.family);
    if (!family) return false;
    const weight = Number(L.weight) || 400;
    const style: 'normal' | 'italic' = /italic|oblique/i.test(String(L.style)) ? 'italic' : 'normal';
    const sizePt = L.fontCanvasPx / L.DPR;
    const built = [];
    for (const ln of L.lines) {
      const v = await vectorLine(ln.text, family, weight, style, sizePt, L.fontCanvasPx);
      if (!v) return false;
      built.push(v);
    }
    const { topPt } = textBlockBox(eyTop, eh, L.heightPt);
    const originY = pageH - topPt;
    const ink = hexToRgb(el.fontColor ?? '#1A1A1A');
    const boldW = sizePt * fakeBoldRatio(L.fontCanvasPx);
    const anchorPt = L.xAnchor / L.DPR;
    const align = el.textAlign === 'left' ? 'left' : el.textAlign === 'right' ? 'right' : 'center';
    built.forEach((v, i) => {
      const baseline = (L.firstLineCY + i * L.lineHeight + L.baselineShift) / L.DPR;
      const x0 = align === 'center' ? anchorPt - v.width / 2 : align === 'right' ? anchorPt - v.width : anchorPt;
      const { plain, bold } = v.build(x0, baseline);
      const all = plain + bold;
      if (!all) return;
      // The outline (captions on a busy photo): stroked first, filled over — the editor's paint-order.
      if (L.outlinePx > 0 && el.outlineColor) {
        page.drawSvgPath(all, { x: ex, y: originY, borderColor: hexToRgb(el.outlineColor), borderWidth: (2 * L.outlinePx) / L.DPR + (bold ? boldW : 0) });
      }
      if (plain) page.drawSvgPath(plain, { x: ex, y: originY, color: ink });
      if (bold) page.drawSvgPath(bold, { x: ex, y: originY, color: ink, borderColor: ink, borderWidth: boldW });
      if (el.textDecoration === 'underline') {
        const cy = (L.firstLineCY + i * L.lineHeight) / L.DPR;
        const h = Math.max(1 / L.DPR, (L.fontCanvasPx * 0.06) / L.DPR);
        page.drawRectangle({ x: ex + x0, y: originY - (cy + (L.fontCanvasPx * 0.42) / L.DPR) - h, width: v.width, height: h, color: ink, borderWidth: 0 });
      }
    });
    return true;
  } catch (e) {
    console.warn('[pdfgen] vector text failed — raster instead:', (e as Error)?.message);
    return false;
  }
}

/** Exported for the editor↔print parity check (textElementParity) — the PDF's own text raster. */
export async function rasterizeTextElement(
  el: PageElement,
  pageW: number,
  pageH: number,
): Promise<{ png: Uint8Array; heightPt: number } | null> {
  const L = await textCanvasLayout(el, pageW, pageH);
  if (!L) return null;
  const { canvas, ctx, lines, fontCanvasPx, lineHeight, canvasW, canvasH, heightPt, firstLineCY, xAnchor, baselineShift, outlinePx } = L;
  for (let i = 0; i < lines.length; i++) {
    const { text: line, dir } = lines[i];
    const cy = firstLineCY + i * lineHeight;
    ctx.direction = dir;
    // #1236 (2026-10-14): 'middle' put every caption 0.6-0.8 mm ABOVE where the
    // editor shows it (Dancing Script's ascent ≠ descent). The DOM centres the
    // content area (ascent + descent) in the 1.3 line box; so does this.
    const yb = cy + baselineShift;
    if (outlinePx > 0) {
      ctx.save();
      ctx.lineJoin = 'round';
      ctx.miterLimit = 2;
      ctx.lineWidth = 2 * outlinePx;
      ctx.strokeStyle = el.outlineColor as string;
      ctx.strokeText(line, xAnchor, yb);
      ctx.restore();
    }
    ctx.fillText(line, xAnchor, yb);
    if (el.textDecoration === 'underline' && line) {
      const m = ctx.measureText(line);
      const underY = cy + fontCanvasPx * 0.42;
      let underX = xAnchor;
      if (ctx.textAlign === 'center') underX = xAnchor - m.width / 2;
      else if (ctx.textAlign === 'right') underX = xAnchor - m.width;
      ctx.fillRect(underX, underY, m.width, Math.max(1, fontCanvasPx * 0.06));
    }
  }

  // PRINT-RIP MATTE (2026-07-17): read the raw pixels and encode the PNG
  // ourselves with transparent pixels forced to WHITE — canvas premultiplied
  // alpha makes this impossible via put/getImageData, so we bypass toBlob.
  // Kills the faint dark "shadow box" a bad RIP bleeds around every text.
  try {
    const rgba = ctx.getImageData(0, 0, canvasW, canvasH).data;
    const png = await encodeRgbaPngWhiteMatte(rgba, canvasW, canvasH);
    return { png, heightPt };
  } catch {
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
    if (!blob) return null;
    return { png: new Uint8Array(await blob.arrayBuffer()), heightPt };
  }
}

async function rasterizeSvgToPng(svg: string, w: number, h: number, tint?: string): Promise<Uint8Array | null> {
  if (typeof document === 'undefined') return null;
  // Inject the customer's chosen colour onto `currentColor` strokes/fills
  // so the icon picks up the same tint it had in the editor.
  const tinted = tint
    ? svg.replace(/<svg([^>]*)>/i, `<svg$1 style="color:${tint}">`)
    : svg;
  const blob = new Blob([tinted], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('svg load failed'));
      i.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.ceil(w));
    canvas.height = Math.max(1, Math.ceil(h));
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    // Same white-matte PNG encode as text — icons print without a shadow box.
    // (SVG-from-blob can taint the canvas; getImageData then throws → fall back
    // to toBlob, which is still fine for icons that don't hit a bad RIP.)
    try {
      const rgba = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      return await encodeRgbaPngWhiteMatte(rgba, canvas.width, canvas.height);
    } catch {
      const pngBlob = await new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
      if (!pngBlob) return null;
      return new Uint8Array(await pngBlob.arrayBuffer());
    }
  } catch (e) {
    console.warn('[pdfgen] svg rasterize failed:', (e as Error)?.message);
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function drawPageElements(
  doc: PDFDocument,
  page: PDFPage,
  allElements: PageElement[],
  pageW: number,
  pageH: number,
  // fontCache + fallbackFont are unused now that text rasterises via
  // canvas, but the signature is preserved so the call site doesn't
  // change. They'll come back when we add vector text support for
  // accessibility / selectable text.
  _fontCache: Map<string, PDFFont>,
  _fallbackFont: PDFFont,
  /** ISSUE 8 (2026-07-05) — resolves a photo ELEMENT's photoId to a signed
   *  URL (print original, with display fallback). Absent → photo elements
   *  are skipped (intro/outro call sites have no album photos). */
  resolvePhotoUrl?: (photoId: string) => string | null,
): Promise<Set<string>> {
  // STEP 3 — an empty text holder, and a label whose holder is empty, never
  // print (lib/decor/holders.ts; the editor and the preview read the same rule).
  const elements = printable(allElements);
  // Every photo element actually drawn — a themed (blank) page checks its
  // photos against this, so a photo layer can never vanish in silence.
  const drawn = new Set<string>();
  if (!elements.length) return drawn;

  // STEP 2 (printer upgrade) — where each soft shadow goes in the stack (own,
  // or the group's: decorStyle.shadowPlan, the editor uses the same function).
  const shadows = shadowPlan(elements);
  for (let ei = 0; ei < elements.length; ei++) {
    const el = elements[ei];
    const ex = (el.x / 100) * pageW;
    const eyTop = (el.y / 100) * pageH;
    const ew = (el.width / 100) * pageW;
    const eh = (el.height / 100) * pageH;
    // pdf-lib draws bottom-up. Element's bottom edge in pdf coords:
    const eyBottom = pageH - eyTop - eh;
    const opacity = el.opacity ?? 1;

    for (const s of shadows.get(ei) ?? []) {
      const sh = (s.height / 100) * pageH;
      const sb = { x: (s.x / 100) * pageW, y: pageH - (s.y / 100) * pageH - sh, w: (s.width / 100) * pageW, h: sh };
      try { await drawElementShadow(doc, page, s, sb, fetchAsBlob); }
      catch (e) { console.warn('[pdfgen] shadow failed:', (e as Error)?.message); }
    }

    // STEP 2 — EVERY type turns and mirrors like the editor's
    // `rotate() scaleX() scaleY()` about the box centre. Before, only photo
    // elements turned here, so a rotated sticker printed straight. Text never
    // mirrors (the editor refuses to, D78).
    const box = { x: ex, y: eyBottom, w: ew, h: eh };
    const xf = el.type === 'text' ? { rotation: el.rotation } : el;

    try {
      await withElementTransform(page, box, xf, async () => {
      if (el.type === 'text') {
        // Rasterise via canvas so the customer's exact font / weight /
        // italic / alignment / line breaks / colour render identically to
        // the editor preview. Avoids font-substitution drift entirely.
        // The book-colour band behind a caption (lib/textBand.ts): on the
        // STORED box, under the words — the editor paints the same box.
        const band = textBand(el);
        if (band) {
          // A cover text's band may stand off the box (bandPad, 2026-10-30).
          const br = bandRect(el, { x: ex, y: eyBottom, w: ew, h: eh }, pageW);
          page.drawRectangle({
            x: br.x,
            y: br.y,
            width: br.w,
            height: br.h,
            color: rgb(band.r, band.g, band.b),
            opacity: band.alpha,
            borderWidth: 0,
          });
        }
        // ⚠️ VECTOR FIRST (#1236, 2026-10-17): the letters as filled outlines —
        // no picture, no transparency for the printer to flatten into a box.
        // The raster stays only for what a linked face cannot draw (Arabic, a
        // system font), element by element.
        if (await drawTextVector(page, el, pageW, pageH, ex, eyTop, eh)) return;
        const raster = await rasterizeTextElement(el, pageW, pageH);
        if (raster) {
          const img = await doc.embedPng(raster.png);
          // The raster may be TALLER than the stored box (text overflow —
          // the editor renders it centred on the box, overflow visible).
          // Centre the effective block on the stored box so PDF == editor.
          const { topPt, heightPt } = textBlockBox(eyTop, eh, raster.heightPt);
          page.drawImage(img, {
            x: ex,
            y: pageH - topPt - heightPt,
            width: ew,
            height: heightPt,
          });
        }
      } else if (el.type === 'rect') {
        // The line (a theme's line frame, 1-04) is a PHYSICAL size stored in
        // points, centred on the box edge — the editor and the preview draw the
        // same line from lib/decor/shapePaint.ts (strokeLayerCss).
        const fillHex = el.fill ?? RECT_FILL_DEFAULT;
        const fillColor = hexToRgb(fillHex);
        const fillAlpha = parseAlphaFromHex(fillHex) ?? opacity;
        const borderColor = el.stroke ? hexToRgb(el.stroke) : undefined;
        page.drawRectangle({
          x: ex,
          y: eyBottom,
          width: ew,
          height: eh,
          color: fillColor,
          opacity: fillAlpha,
          borderColor,
          borderWidth: strokeWidthPt(el),
        });
      } else if (el.type === 'ellipse') {
        const fillHex = el.fill ?? ELLIPSE_FILL_DEFAULT;
        const fillColor = hexToRgb(fillHex);
        const fillAlpha = parseAlphaFromHex(fillHex) ?? opacity;
        page.drawEllipse({
          x: ex + ew / 2,
          y: eyBottom + eh / 2,
          xScale: ew / 2,
          yScale: eh / 2,
          color: fillColor,
          opacity: fillAlpha,
          borderColor: el.stroke ? hexToRgb(el.stroke) : undefined,
          borderWidth: strokeWidthPt(el),
        });
      } else if (el.type === 'photo' && el.photoId && resolvePhotoUrl) {
        // ISSUE 8 — photo ELEMENT (the "Photo" tool). Mirrors the editor's
        // object-fit: cover: the source is CROPPED to the element's aspect,
        // sized for 300 dpi at the element's printed size (elementPhotoPixels,
        // the slot rule — never enlarged), then embedded to fill the box.
        const url = resolvePhotoUrl(el.photoId);
        if (url) {
          try {
            const blob = await fetchAsBlob(url);
            const bmp = await createImageBitmap(blob);
            const boxAR = ew / eh;
            // CROP (2026-07-23) — when the customer saved a crop window, the
            // source is first restricted to that window (normalized 0..1 of
            // the full bitmap), and the cover-fit below operates on the window
            // instead of the whole photo. Mirrors the editor's CSS crop math
            // exactly (parity contract). No crop → identical to before.
            // The maths lives in lib/decor/cropMath.ts (cropSource) — the
            // preview's cropCss is the same window (panoramaParity.test.ts).
            const { sx, sy, sw, sh } = cropSource(bmp.width, bmp.height, el.crop, boxAR);
            // STEP 2 — 300 dpi for the element's printed size, like a slot.
            const px = elementPhotoPixels(sw, sh, ew, eh);
            const canvas = document.createElement('canvas');
            canvas.width = px.w; canvas.height = px.h;
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(bmp, sx, sy, sw, sh, 0, 0, px.w, px.h);
              const jpegBlob = await new Promise<Blob | null>((resolve) =>
                canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92));
              if (jpegBlob) {
                const jpgBytes = new Uint8Array(await jpegBlob.arrayBuffer());
                // STEP 2 — torn edge: the JPEG keeps its bytes and gets the
                // mask's alpha beside it as /SMask (≤ 1200 px wide: an edge,
                // not a photo). Rotation + flip come from the element wrapper.
                let img;
                if (el.maskUrl) {
                  const maskUrl = el.maskUrl;
                  const mw = Math.min(px.w, 1200);
                  const mh = Math.max(1, Math.round(px.h * (mw / px.w)));
                  const maskRef = await cachedImage(doc, `smask|${maskUrl}|${mw}x${mh}`,
                    async () => embedGrayMask(doc, await alphaPlane(maskUrl, mw, mh, fetchAsBlob), mw, mh));
                  img = await embedJpgWithSoftMask(doc, jpgBytes, maskRef);
                } else {
                  img = await doc.embedJpg(jpgBytes);
                }
                page.drawImage(img, { x: ex, y: eyBottom, width: ew, height: eh, opacity });
                drawn.add(el.photoId);
              }
            }
            bmp.close();
          } catch (e) {
            console.warn('[pdfgen] photo element embed failed:', (e as Error)?.message);
            // ⚠️ A placed photo element is a photo the editor shows (#1215).
            throw unprintablePhoto({ albumPage: 0, photoId: el.photoId ?? null, reason: `page element: ${(e as Error)?.message ?? 'draw failed'}` });
          }
        }
      } else if (el.type === 'icon' && el.imageUrl) {
        // Catalogue-décor icon — embed the full-res transparent PNG (public
        // covers bucket) contain-fit in the element box, exactly like the
        // editor's object-contain <img>. Print keeps the full-res source.
        // STEP 2 — embedded ONCE per PDF, reused by every page that shows it.
        const iconUrl = el.imageUrl;
        // The same key as the page background (pdfDecor.pieceKey): a paper drawn both ways is one embed.
        // A TINTED piece (lib/decor/tint.ts): recoloured on a canvas — one embed per file and tint.
        const tint = isTint(el.tint) ? el.tint : null;
        const img = await cachedImage(doc, tint ? pieceKey(tintKey(iconUrl, tint)) : pieceKey(iconUrl), async () => {
          if (tint) return doc.embedPng(await tintedPiecePng(await fetchAsBlob(iconUrl), tint));
          const { bytes, mime } = await normaliseCached(iconUrl);
          return embedAuto(doc, bytes, mime);
        });
        // STEP 3 — `fit: 'fill'` stretches the piece to its box (torn scraps).
        const fit = Math.min(ew / img.width, eh / img.height);
        const dw = el.fit === 'fill' ? ew : img.width * fit, dh = el.fit === 'fill' ? eh : img.height * fit;
        page.drawImage(img, {
          x: ex + (ew - dw) / 2,
          y: eyBottom + (eh - dh) / 2,
          width: dw,
          height: dh,
          opacity,
        });
      } else if (el.type === 'icon' && el.svg) {
        // Rasterise the SVG to a 2× density PNG so the icon stays crisp
        // when the printer scales the page. 2× is the sweet spot —
        // higher gets memory-hungry without visible quality gain at
        // print scale.
        const renderW = Math.max(64, ew * 2);
        const renderH = Math.max(64, eh * 2);
        const png = await rasterizeSvgToPng(el.svg, renderW, renderH, el.iconColor ?? el.fontColor);
        if (png) {
          const img = await doc.embedPng(png);
          page.drawImage(img, {
            x: ex,
            y: eyBottom,
            width: ew,
            height: eh,
            opacity,
          });
        }
      }
      // `photo` elements (extra free-floating photos) — placeholder in the
      // editor only, not yet renderable here without the signed_url. Skip
      // silently so the PDF doesn't blank the rest of the page.
      });
    } catch (e) {
      console.warn('[pdfgen] page element failed:', (e as Error)?.message, el);
    }
  }
  return drawn;
}

/** STEP 2 (printer upgrade) — the page's paper (`LayoutPage.background`):
 *  colour and/or image cover-fitted across the full bleed, clipped to it, the
 *  image embedded once per PDF. The editor draws the same box
 *  (decorStyle.backgroundBoxPct). Returns false when the page has no paper. */
async function drawLayoutBackground(
  doc: PDFDocument, page: PDFPage, lp: LayoutPage,
  media: { x: number; y: number; w: number; h: number },
): Promise<boolean> {
  const bg = lp.background;
  if (!bg || (!bg.imageUrl && !bg.color)) return false;
  clipRect(page, media.x, media.y, media.w, media.h);
  try {
    // A TINTED paper (4-10's speckled paper): recoloured on a canvas, as a JPEG (a paper has no alpha).
    const tint = isTint(bg.tint) ? bg.tint : null;
    await drawPageBackground(doc, page, tint && bg.imageUrl ? { ...bg, imageUrl: tintKey(bg.imageUrl, tint) } : bg, media, {
      hexToRgb,
      loadImage: async (key) => {
        if (tint && bg.imageUrl) return doc.embedJpg(await tintedPieceJpg(await fetchAsBlob(bg.imageUrl), tint));
        const { bytes, mime } = await normaliseCached(key);
        return embedAuto(doc, bytes, mime);
      },
    });
  } finally {
    popClip(page);
  }
  return true;
}

// ─── Public entry point ──────────────────────────────────────────────────────
export interface AlbumPdfBlobs {
  cover: Blob;
  interior: Blob;
}

export async function generateAlbumPdfs(
  input: AlbumPdfInput,
  onProgress?: (p: PdfGenProgress) => void,
  /**
   * Which halves to actually build.
   *
   * ⚠️ THE INTERIOR IS THE EXPENSIVE ONE — every photo re-embedded at print
   * resolution, sometimes four times over as the size ladder retries. Reprinting
   * one word on the cover used to pay that whole cost. `partsToRender` in
   * albumSourceHash.ts decides; a skipped half comes back as an empty Blob and
   * the caller must not upload it (see renderAndUploadAlbumPdfs).
   */
  parts: { cover: boolean; interior: boolean } = { cover: true, interior: true },
): Promise<AlbumPdfBlobs> {
  const EMPTY = new Blob([], { type: 'application/pdf' });

  let coverBytes: Uint8Array | null = null;
  if (parts.cover) {
    onProgress?.({ stage: 'cover', current: 0, total: 1, message: 'Génération de la couverture...' });
    coverBytes = await generateCoverPdf(input);
    onProgress?.({ stage: 'cover', current: 1, total: 1, message: 'Couverture prête' });
  }

  if (!parts.interior) {
    if (coverBytes) await assertPagesRender(coverBytes, 'couverture');
    onProgress?.({ stage: 'done', current: 1, total: 1, message: 'Couverture seule — intérieur inchangé' });
    return {
      cover: coverBytes ? new Blob([coverBytes as BlobPart], { type: 'application/pdf' }) : EMPTY,
      interior: EMPTY,
    };
  }

  // ── Interior with an adaptive size guard ──────────────────────────────────
  // HISTORY: the interior used to hit a 50 MB Supabase upload ceiling. That is
  // gone — the `pdfs` bucket allows 1 GB and interiors up to 193 MB are stored
  // (checked 2026-10-13). Nothing outside needs 50 MB: the printer downloads by
  // link, the 3D preview loads 1400 px page JPEGs, never the PDF. So generate at the best quality first and, ONLY
  // if the result is over the safe ceiling, re-encode the photos at
  // progressively lower JPEG quality until it fits. Most albums pass on the
  // first tier; the retries fire only for the heaviest ones.
  // 220 MB: the pdfs bucket allows 1 GB, so the budget is no longer the old
  // 50 MB storage cap — it bounds browser memory during generation and keeps
  // printer-link zips downloadable. Sized so a photo-heavy album never leaves
  // tier 0 with full-page embeds at PRINT_MAX_DIM (4800 px): dropping a tier
  // re-compresses EVERY photo in the album, so the budget must not bite on a
  // normal order. #1116 (32 pages, 52 photos) measured 66 MB at 3600 px.
  const SAFE_INTERIOR_BYTES = 220 * 1024 * 1024;
  const QUALITY_TIERS = [0.92, 0.85, 0.78, 0.70];
  let interiorBytes = await generateInteriorPdf(input, onProgress, QUALITY_TIERS[0]);
  for (let t = 1; t < QUALITY_TIERS.length && interiorBytes.length > SAFE_INTERIOR_BYTES; t++) {
    onProgress?.({
      stage: 'interior',
      current: 0,
      total: input.pages.length,
      message: `Optimisation de la taille (${Math.round(interiorBytes.length / 1048576)} Mo → cible < ${Math.round(SAFE_INTERIOR_BYTES / 1048576)} Mo)...`,
    });
    interiorBytes = await generateInteriorPdf(input, onProgress, QUALITY_TIERS[t]);
  }
  // OVERSIZE GUARD (2026-07-25, added with the 300-page ceiling). If the whole
  // quality ladder still leaves us over the cap, FAIL HERE with a readable
  // message. Previously the loop just ended and the oversized file was returned
  // as if healthy, then died at upload with an opaque HTTP 400 — after burning
  // four full render passes. At 300 pages this stops being a rare edge case, so
  // it has to be loud: the operator needs to know the album is too big to print
  // rather than watch a silent failure retry itself five times.
  if (interiorBytes.length > SAFE_INTERIOR_BYTES) {
    const mb = Math.round(interiorBytes.length / 1048576);
    throw new Error(
      `Intérieur trop lourd pour l'impression : ${mb} Mo après compression maximale ` +
      `(limite ${Math.round(SAFE_INTERIOR_BYTES / 1048576)} Mo). ` +
      `L'album fait ${input.pages.length} pages — réduisez le nombre de pages ou de photos.`,
    );
  }

  // ⚠️ EVERY PAGE MUST RENDER before the files leave (2026-10-07): the
  // structural print gate ran at save; this draws each page. A failure stops
  // the upload / download here, with the page numbers (pdfRenderCheck.ts).
  onProgress?.({ stage: 'interior', current: input.pages.length, total: input.pages.length, message: 'Vérification de chaque page pour l’impression…' });
  if (coverBytes) await assertPagesRender(coverBytes, 'couverture');
  await assertPagesRender(interiorBytes, 'intérieur');

  onProgress?.({ stage: 'done', current: input.pages.length, total: input.pages.length, message: 'Terminé' });

  // pdf-lib's save() returns Uint8Array<ArrayBufferLike>; cast through BlobPart
  // to satisfy strict TS in newer @types/node + lib.dom.
  return {
    cover:    coverBytes ? new Blob([coverBytes as BlobPart], { type: 'application/pdf' }) : EMPTY,
    interior: new Blob([interiorBytes as BlobPart], { type: 'application/pdf' }),
  };
}

/** The same input with no coupon leaf (previews, redos). Same object when it
 *  had none, so nothing else about the call changes. */
function withoutCoupons(input: AlbumPdfInput): AlbumPdfInput {
  if (!input.coupons && !input.onCouponPlan) return input;
  const { coupons: _c, onCouponPlan: _o, ...rest } = input;
  void _c; void _o;
  return rest;
}

// ─── Quick 3D-preview PDFs (owner, 2026-10-18 — « show the new version instantly ») ──
//
// The SAME `generateCoverPdf` / `generateInteriorPdf` the print uses — no second
// drawing path (render-parity rule). The caller feeds preview-size photos
// (lib/quickPreview.ts), so only sharpness differs from print. What is left out
// is print-only: the size ladder (a preview never nears the size cap) and the
// per-page « does every page render » gate (`assertPagesRender`) — the quick
// pass rasterizes each page it needs right after, and a page that will not
// draw fails that pass, which then simply leaves the preview to the print
// rebuild. These files are NEVER uploaded as PDFs.
export async function generatePreviewPdfs(
  input: AlbumPdfInput,
  parts: { cover: boolean; interior: boolean },
): Promise<{ cover: Uint8Array | null; interior: Uint8Array | null }> {
  const cover = parts.cover ? await generateCoverPdf(input) : null;
  // ⚠️ A PREVIEW NEVER SHOWS COUPONS (plan 2026-10-28): their codes are for the
  // printed book only.
  const interior = parts.interior ? await generateInteriorPdf(withoutCoupons(input), undefined, 0.92) : null;
  return { cover, interior };
}

// ─── Redo (réimpression) PDF generation ──────────────────────────────────────
export type RedoScope = 'full' | 'cover-only' | 'interior-only' | 'custom-pages';

export interface RedoPdfBlobs {
  cover?: Blob;
  interior?: Blob;
}

/**
 * Render ONLY the PDFs a redo needs — reusing the exact same generateCoverPdf /
 * generateInteriorPdf paths the full album uses (no duplicated layout logic):
 *
 *   - `cover-only`     → cover only
 *   - `interior-only`  → full interior (all content pages)
 *   - `full`           → cover + full interior
 *   - `custom-pages`   → interior restricted to `pageIndices` (0-based into
 *                        input.pages), bookends + ÷4 padding preserved
 *
 * The interior reuses the adaptive size-guard ladder from generateAlbumPdfs so
 * a photo-dense subset still fits the 50 MB Storage cap.
 */
export async function generateRedoPdfs(
  input: AlbumPdfInput,
  scope: RedoScope,
  pageIndices?: number[],
  onProgress?: (p: PdfGenProgress) => void,
): Promise<RedoPdfBlobs> {
  const out: RedoPdfBlobs = {};

  const wantCover = scope === 'full' || scope === 'cover-only';
  const wantInterior = scope === 'full' || scope === 'interior-only' || scope === 'custom-pages';

  if (wantCover) {
    onProgress?.({ stage: 'cover', current: 0, total: 1, message: 'Génération de la couverture (redo)…' });
    const coverBytes = await generateCoverPdf(input);
    await assertPagesRender(coverBytes, 'couverture (redo)');
    out.cover = new Blob([coverBytes as BlobPart], { type: 'application/pdf' });
    onProgress?.({ stage: 'cover', current: 1, total: 1, message: 'Couverture prête' });
  }

  if (wantInterior) {
    // `custom-pages` passes the subset through AlbumPdfInput.pageIndices; the
    // other interior scopes render the whole content set (pageIndices omitted).
    // A redo reprints album pages; coupons are never part of one.
    const interiorInput: AlbumPdfInput =
      scope === 'custom-pages' ? { ...withoutCoupons(input), pageIndices: pageIndices ?? [] } : withoutCoupons(input);

    const SAFE_INTERIOR_BYTES = 220 * 1024 * 1024; // keep in sync with the main ladder above
    const QUALITY_TIERS = [0.92, 0.85, 0.78, 0.70];
    let interiorBytes = await generateInteriorPdf(interiorInput, onProgress, QUALITY_TIERS[0]);
    for (let t = 1; t < QUALITY_TIERS.length && interiorBytes.length > SAFE_INTERIOR_BYTES; t++) {
      interiorBytes = await generateInteriorPdf(interiorInput, onProgress, QUALITY_TIERS[t]);
    }
    await assertPagesRender(interiorBytes, 'intérieur (redo)');
    out.interior = new Blob([interiorBytes as BlobPart], { type: 'application/pdf' });
  }

  onProgress?.({ stage: 'done', current: 1, total: 1, message: 'Redo prêt' });
  return out;
}

// ─── SVG-href-swap helpers ───────────────────────────────────────────────────
// Used by the per-page SVG-render branch in generateInteriorPdf. Mirrors the
// rasterizeSvgToPng helper in SlotReviewPane.tsx — duplicated rather than
// shared because the two files have different lifecycles and the function is
// pure / tiny.

async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(typeof r.result === 'string' ? r.result : '');
    r.onerror = () => reject(r.error ?? new Error('blobToDataUrl: read failed'));
    r.readAsDataURL(blob);
  });
}

async function rasterizeSvgStringToJpeg(svgString: string, targetWidth: number, quality: number): Promise<Blob> {
  return rasterizeSvgStringToBlob(svgString, targetWidth, 'image/jpeg', quality);
}

/**
 * Per-page byte budget for the templated-PDF rasterizer. Sized so a
 * theoretical 50-page album with every page at the budget still lands
 * comfortably under the 1 GB pdfs-bucket cap (8 MB × 50 = 400 MB, plus
 * pdf-lib overhead).
 *
 * Reality: ~95 % of real templated pages render at ~1-2 MB at q92, so the
 * budget is almost never hit. It exists as a safety net for pathological
 * Canva templates that pack many high-detail decorations into one page.
 */
const PER_PAGE_BUDGET_BYTES = 8 * 1024 * 1024;

/**
 * JPEG quality ladder. Start at the visually-lossless tier, step down
 * only if the prior tier blew the per-page budget. Stopping at q70
 * because below that color banding starts to show in printed photos.
 */
const ADAPTIVE_QUALITIES = [0.92, 0.85, 0.78, 0.70] as const;

async function rasterizeWithBudget(
  svgString: string,
  targetWidth: number,
  budgetBytes: number,
  qualities: readonly number[],
): Promise<Uint8Array> {
  let lastBytes: Uint8Array | null = null;
  for (const q of qualities) {
    const blob = await rasterizeSvgStringToJpeg(svgString, targetWidth, q);
    const bytes = new Uint8Array(await blob.arrayBuffer());
    if (bytes.byteLength <= budgetBytes) return bytes;
    lastBytes = bytes;
  }
  // Even the lowest-quality tier exceeded budget — we'd rather ship a
  // slightly-too-big page than fail PDF generation. The pdfs bucket has
  // a 1 GB cap; one over-budget page rarely tips that.
  return lastBytes!;
}

async function rasterizeSvgStringToBlob(
  svgString: string,
  targetWidth: number,
  mime: 'image/png' | 'image/jpeg',
  quality?: number,
): Promise<Blob> {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, 'image/svg+xml');
  const root = doc.documentElement as unknown as SVGSVGElement;
  const vbW = root.viewBox.baseVal.width || root.width.baseVal.value;
  const vbH = root.viewBox.baseVal.height || root.height.baseVal.value;
  const scale = targetWidth / vbW;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(vbW * scale);
  canvas.height = Math.round(vbH * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d ctx');
  // JPEG has no alpha; if the SVG has transparency anywhere it'd render as
  // black on JPEG. Pre-fill the canvas with white so any transparent
  // regions in the templated SVG (slots without photos, holes punched in
  // the overlay) end up as white-background instead of black.
  if (mime === 'image/jpeg') {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  const blob = new Blob([svgString], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.src = url;
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('svg→img load failed'));
    });
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  } finally {
    URL.revokeObjectURL(url);
  }
  return await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob → null'))),
      mime,
      quality,
    ),
  );
}

/** DEV HARNESS ONLY (web/dev/printer-test.tsx) — the interior renderer alone,
 *  for the printer-upgrade parity check. Not used by the app. */
export { generateInteriorPdf as generateInteriorPdfForDevTest };
